import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar as CalendarIcon, User, FileText, FileDown, MessageSquarePlus, ChevronDown, ChevronUp, Loader2, Activity, ClipboardList, MapPin, Shield, History, Video, Users, Edit3, X, Save, CalendarPlus, Search, UserPlus, UploadCloud, CheckSquare, Square, Trash2, Plus, Phone, Mail, StickyNote, Clock, CheckCircle2, AlertCircle, AlertTriangle, Wallet, CheckCircle, GraduationCap, Package, Paperclip, Mic, Copy } from 'lucide-react';
import { api, ApiError } from '../api';
import KalenderAnsicht from '../components/KalenderAnsicht';
import KundenKarte from '../components/KundenKarte';
import TerminPipelinePunkte from '../components/TerminPipelinePunkte';
import TerminPipelineTabelle from '../components/TerminPipelineTabelle';
import PflegegradPruefer from '../components/PflegegradPruefer';
import { formatTelHref, formatMailHref, istPlatzhalterEmail } from '../utils/contact';
import { fuelleTemplate, istKind, STUFEN_PATIENT, STUFEN_PATIENT_BEOBACHTET, STUFEN_PATIENT_KIND, STUFEN_PFLEGEPERSON } from '../utils/personalisierung';
import { VERWANDTSCHAFT_GRUPPEN } from '../utils/verwandtschaft';
import { platzhalterErsetzen, baueVorlagenKontext, baueEinzelfallVideoText, EINZELFALL_VIDEO_THEMEN, baueNoShowText } from '../utils/mailvorlagen';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0', blue: '#2DBAE8', green: '#8CC53F', red: '#ED2979' };

// Ein Video-Link gilt nur dann als echt, wenn er eine http(s)-Adresse ist und
// nicht einer der alten Platzhalter-/Fehler-Links (…/fehler, …/demo), die
// frueher bei einem fehlgeschlagenen Raum gespeichert wurden. So erkennt das
// Cockpit einen kaputten Videoraum und bietet "erneut anlegen" an.
function videoLinkGueltig(link) {
  const l = (link || '').trim();
  if (!/^https?:\/\//i.test(l)) return false;
  if (/\/(fehler|demo)\b/i.test(l)) return false;
  return true;
}

// Haengt einen eindeutigen Zeitstempel an eine Download-URL, damit der Browser
// nach dem Neu-Erzeugen eines Protokoll-PDFs nicht die alte, zwischen-
// gespeicherte Fassung zeigt (Cache-Buster; ergaenzt das no-store des Servers).
const mitCacheBuster = (url) => {
  if (!url) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
};

// Maßnahmen-Optionen der GKV-37.3-Vorlage. Keys müssen zum PDF_MAPPING in
// backend/services/pdf_generator.py passen — dort werden die Kreuze gesetzt.
const MASSNAHMEN_OPTIONEN = [
  { key: 'm_pflegekurs',   label: 'Pflegekurs für Angehörige' },
  { key: 'm_tagespflege',  label: 'Tages-/Nachtpflege' },
  { key: 'm_sachleistung', label: 'Pflegesachleistung' },
  { key: 'm_kombi',        label: 'Kombinationsleistung' },
  { key: 'm_verhinderung', label: 'Verhinderungspflege' },
  { key: 'm_entlastung',   label: 'Entlastungsleistungen' },
  { key: 'm_hilfsmittel',  label: 'Hilfsmittel' },
  { key: 'm_kurzzeit',     label: 'Kurzzeitpflege' },
  { key: 'm_wohnraum',     label: 'Wohnumfeld-Maßnahmen' },
  { key: 'm_reha',         label: 'Rehabilitation' },
  { key: 'm_begutachtung', label: 'Erneute Begutachtung' },
  { key: 'm_freistellung', label: 'Freistellung (Pflegezeit)' },
  { key: 'm_weitere',      label: 'Weitere Maßnahmen' },
];

const calculateAge = (dobString) => {
  if (!dobString) return '';
  const today = new Date();
  const birthDate = new Date(dobString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

const WOCHENTAGE_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONATE_KURZ_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

// Liefert "Heute", "Morgen", "Gestern" oder "Mo, 28. Apr" relativ zu heute.
// Eingabe: ISO-Datum 'YYYY-MM-DD'.
const formatTagRelativ = (datumISO) => {
  if (!datumISO) return '';
  const d = new Date(datumISO + 'T00:00:00');
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  const diffTage = Math.round((d - heute) / 86400000);
  if (diffTage === 0) return 'Heute';
  if (diffTage === 1) return 'Morgen';
  if (diffTage === -1) return 'Gestern';
  return `${WOCHENTAGE_KURZ[d.getDay()]}, ${d.getDate()}. ${MONATE_KURZ_DE[d.getMonth()]}`;
};

// Liefert eine Liste sinnvoller Halbjahr-Optionen fuers Protokoll-PDF.
// Zwei Quellen werden zusammengelegt, damit die Liste nie zu kurz ist:
//   * relativ zum TERMIN-Datum (-2 .. +2) — rueckwirkend auf vergangene
//     Halbjahre zielen oder vom Termin aus vorausgreifen.
//   * relativ zu HEUTE (0 .. +2) — damit das laufende und die beiden
//     naechsten Halbjahre IMMER waehlbar sind, auch wenn der Termin
//     selbst schon ein paar Monate zurueckliegt. Ohne diesen Teil fehlte
//     z.B. im Oktober 2026 das 1. Halbjahr 2027 bei aelteren Terminen.
// Ergebnis ist chronologisch sortiert und ohne Doppel-Eintraege.
const generiereHalbjahrOptionen = (datumISO) => {
  // Halbjahr um `offset` Halbjahre verschieben, mit Jahres-Uebertrag.
  const schiebe = (jahr, h, offset) => {
    let j = jahr;
    let n = h + offset;
    while (n < 1) { n += 2; j -= 1; }
    while (n > 2) { n -= 2; j += 1; }
    return { jahr: j, h: n };
  };

  const quellen = [];
  const d = datumISO ? new Date(datumISO) : null;
  if (d && !isNaN(d.getTime())) {
    quellen.push({
      jahr: d.getFullYear(),
      h: d.getMonth() < 6 ? 1 : 2,
      von: -2,
      bis: 2,
    });
  }
  const heute = new Date();
  quellen.push({
    jahr: heute.getFullYear(),
    h: heute.getMonth() < 6 ? 1 : 2,
    von: 0,
    bis: 2,
  });

  const gesehen = new Map();
  quellen.forEach(({ jahr, h, von, bis }) => {
    for (let offset = von; offset <= bis; offset++) {
      const ziel = schiebe(jahr, h, offset);
      const code = `${ziel.jahr}-H${ziel.h}`;
      if (!gesehen.has(code)) {
        gesehen.set(code, {
          code,
          label: `${ziel.h}. Halbjahr ${ziel.jahr}`,
          jahr: ziel.jahr,
          h: ziel.h,
        });
      }
    }
  });

  return [...gesehen.values()]
    .sort((a, b) => (a.jahr - b.jahr) || (a.h - b.h))
    .map(({ code, label }) => ({ code, label }));
};

// Sucht den passenden Default-Termin fuer ein neues Protokoll:
//   1. Wenn der Berater den Kunden ueber einen Sidebar-Termin geoeffnet hat,
//      nimm exakt diesen Termin (selectedClient.id ist dann eine Termin-ID).
//   2. Sonst: juengster Termin OHNE bestehendes Protokoll.
//   3. Sonst (alle Termine sind schon protokolliert): juengster Termin ueberhaupt.
//   4. Sonst (kein Termin im System): null — UI zeigt nur Ad-hoc-Option.
function waehleStandardProtokollTermin(selectedClient, termine, protokolle) {
  if (!termine || termine.length === 0) return null;
  if (selectedClient && selectedClient.status !== 'kein_termin' && selectedClient.id
      && termine.some(t => t.id === selectedClient.id)) {
    return selectedClient.id;
  }
  const protoTerminIds = new Set((protokolle || []).map(p => p.termin_id).filter(Boolean));
  const ohneProtokoll = termine.find(t => !protoTerminIds.has(t.id));
  if (ohneProtokoll) return ohneProtokoll.id;
  return termine[0].id;
}

export default function BeraterDashboard({ pendingKundeId, pendingTab, onPendingKundeConsumed, onKundeChange } = {}) {
  // Sidebar zeigt zwei separate Listen: die naechsten 8 zukuenftigen Termine
  // und die letzten 8 vergangenen Termine. Eine "alle-Termine-zugleich"-Liste
  // gibt es nicht mehr — Suchanfragen laufen ueber 'alleKunden'.
  const [terminListeZukunft, setTerminListeZukunft] = useState([]);
  const [terminListeVergangen, setTerminListeVergangen] = useState([]);
  const [schulungsAnfragen, setSchulungsAnfragen] = useState([]);
  const [schulungsAnfragenZeigeAlle, setSchulungsAnfragenZeigeAlle] = useState(false);
  const [neueBuchungen, setNeueBuchungen] = useState([]);
  const [neueBuchungenZeigeAlle, setNeueBuchungenZeigeAlle] = useState(false);
  const [neueBuchungenSort, setNeueBuchungenSort] = useState('eingang'); // 'eingang' | 'termin'
  const [selectedClient, setSelectedClient] = useState(null);
  // Pflegecockpit-Aggregat fuer den ausgewaehlten Kunden — wird beim
  // Wechsel von selectedClient nachgeladen. null = noch nicht geladen.
  const [pflegecockpitInfo, setPflegecockpitInfo] = useState(null);
  const [lianeBoxInfo, setLianeBoxInfo] = useState(null);
  const [textSnippets, setTextSnippets] = useState([]);
  const [protocolText, setProtocolText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('termine');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [modalTab, setModalTab] = useState('patient'); 

  // Manuelle Termin-Anlage (Paket A)
  const [alleKunden, setAlleKunden] = useState([]);
  const [isTerminModalOpen, setIsTerminModalOpen] = useState(false);
  const [isSavingTermin, setIsSavingTermin] = useState(false);
  // Wenn der Anlage-Modal aus einem Kunden-Kontext kommt (z.B. Folge-Termin
  // via Kalender), sperren wir die Kundenauswahl — der Kunde steht fest und
  // eine Suchliste ueber tausende Eintraege waere Verschwendung. Der Modus
  // wird pro Anlage-Oeffnung gesetzt, Default ist "nicht gesperrt".
  const [terminModalKundeGesperrt, setTerminModalKundeGesperrt] = useState(false);
  const [kundeSearch, setKundeSearch] = useState('');
  const heuteISO = new Date().toISOString().slice(0, 10);
  const [terminForm, setTerminForm] = useState({
    kunde_id: '', datum: heuteISO, uhrzeit: '10:00',
    termin_art: 'video', dauer_minuten: 30, notiz: '',
    mit_google_kalender: true,
    mit_info_mail: true,
    mail_an_kunde: true,
    mail_an_angehoeriger: true,
    erinnerung_24h_aktiv: true,
    erinnerung_morgen_aktiv: true,
    gueltigkeit_periode: '',  // "" = Auto (aus Datum), sonst "2026-H1" etc.
    ausserhalb_standard: false,  // Override-Schalter, siehe Banner im Modal
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Protokoll-Detailfelder (Tab 2) — werden mit dem PDF-Erstellen mitgeschickt.
  const [sichtBeteiligte, setSichtBeteiligte] = useState("");
  const [sichergestellt, setSichergestellt] = useState(true);
  const [begruendungNichtSichergestellt, setBegruendungNichtSichergestellt] = useState("");
  const [massnahmen, setMassnahmen] = useState([]);
  const [massnahmenBemerkung, setMassnahmenBemerkung] = useState("");
  const [beratung7aAngezeigt, setBeratung7aAngezeigt] = useState(false);
  const [beratung7aGewuenscht, setBeratung7aGewuenscht] = useState(false);
  const [einwilligungDatenschutz, setEinwilligungDatenschutz] = useState(true);
  const [einwilligungDatenuebermittlung, setEinwilligungDatenuebermittlung] = useState(true);
  const [einwilligung7a, setEinwilligung7a] = useState(false);
  const [isCreatingPdf, setIsCreatingPdf] = useState(false);
  const [pflegekassen, setPflegekassen] = useState([]);
  // Getrennte Vorlagen-Menüs — eins pro Textarea, damit die richtige Kategorie erscheint.
  const [showSnippetMenuFor, setShowSnippetMenuFor] = useState(null);  // 'beteiligte' | 'berater' | null
  // Terminart pro Protokoll. Vorbelegt mit dem Termin-Typ des ausgewählten
  // Eintrags (falls vorhanden), sonst Hausbesuch. Steuert im Backend das
  // Video-Kreuz im PDF und die Unterschrift.
  const [terminArt, setTerminArt] = useState('hausbesuch');

  // Protokoll-Historie des aktuellen Kunden + Edit-Modus + Collapse.
  const [protokollHistorie, setProtokollHistorie] = useState([]);
  const [isSavingProtokoll, setIsSavingProtokoll] = useState(false);
  const [editingProtokollId, setEditingProtokollId] = useState(null);  // null = Neu anlegen
  const [historieExpanded, setHistorieExpanded] = useState(false);

  // Umkreis-Karte (Modal): zeigt den aktuellen Kunden im Zentrum + alle
  // anderen Kunden im 50-km-Radius. Erlaubt Tour-Zuweisung direkt aus
  // dem Popup, wiederverwendet die bestehende KundenKarte-Komponente.
  const [showUmkreisKarte, setShowUmkreisKarte] = useState(false);

  // Termin-Nachbereitung (Assistent): welcher Termin wird gerade nachbereitet?
  // null = Modal zu. Wird aus der Akten-Terminliste UND aus der offenen
  // Nachbereitungs-Liste auf der Startseite gesetzt.
  const [nachbereitungTermin, setNachbereitungTermin] = useState(null);
  const [nachbereitungRefreshKey, setNachbereitungRefreshKey] = useState(0);

  // Termin-Auswahl fuer das Protokoll. null = noch keiner gewaehlt (Backend
  // legt einen Ad-hoc-Termin auf heute an). String = ID eines bestehenden
  // Termins aus kundeTermine. Ad-hoc-Formular legt vorab via POST /api/termine
  // einen neuen Termin an und setzt dann die ID hier.
  const [protokollTerminId, setProtokollTerminId] = useState(null);
  // Optionales "Erstellt am"-Datum der PDF (ISO 'YYYY-MM-DD'). Leer = das
  // Beratungsdatum des Termins wird verwendet.
  const [pdfErstelltDatum, setPdfErstelltDatum] = useState('');
  // Optionale Gueltigkeitsperiode (Halbjahr) fuers Protokoll-PDF.
  // '' = Automatisch (Termin-Einstellung bzw. aus dem Termindatum).
  const [protokollPeriode, setProtokollPeriode] = useState('');
  const [showAdHocTerminForm, setShowAdHocTerminForm] = useState(false);
  const [adHocDatum, setAdHocDatum] = useState('');
  const [adHocUhrzeit, setAdHocUhrzeit] = useState('');
  const [adHocTerminArt, setAdHocTerminArt] = useState('hausbesuch');
  const [isCreatingAdHocTermin, setIsCreatingAdHocTermin] = useState(false);

  // Versand-Dialog
  const [versandFor, setVersandFor] = useState(null);         // Protokoll-ID, Scan-Dokument-ID oder null
  const [versandTyp, setVersandTyp] = useState('protokoll');  // 'protokoll' | 'scan'
  const [scanRefreshKey, setScanRefreshKey] = useState(0);    // erhoeht sich nach Scan-Versand → ScansBlock reload
  const [versandAnPatient, setVersandAnPatient] = useState(true);
  const [versandAnAngehoeriger, setVersandAnAngehoeriger] = useState(true);
  const [versandZusatztext, setVersandZusatztext] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Termin-Historie des aktuellen Kunden + Edit-Termin-Modal (Paket: Termine
  // im Kundendetail). Liste kommt aus /api/termine?kunde_id=... — der Berater
  // sieht in der Akte alle Termine dieses Kunden, auch ausserhalb des
  // Sidebar-Wochenfensters.
  const [kundeTermine, setKundeTermine] = useState([]);

  // To-do-Liste des aktuellen Kunden + Eingabe-Felder fuer einen neuen ToDo.
  const [kundeTodos, setKundeTodos] = useState([]);
  const [neuerTodoText, setNeuerTodoText] = useState('');
  const [neuerTodoFaellig, setNeuerTodoFaellig] = useState('');
  const [neuerTodoPrio, setNeuerTodoPrio] = useState('B');
  const [isSavingTodo, setIsSavingTodo] = useState(false);
  const [zeigeErledigteTodos, setZeigeErledigteTodos] = useState(false);

  // Diagnosen des aktuellen Kunden + Katalog + Begutachtungs-Vorschau.
  // Der Katalog wird einmalig geladen (nicht pro Kundenwechsel).
  const [kundeDiagnosen, setKundeDiagnosen] = useState([]);
  const [diagnoseKatalog, setDiagnoseKatalog] = useState([]);
  const [diagnoseSuche, setDiagnoseSuche] = useState('');
  const [diagnoseSegmentFilter, setDiagnoseSegmentFilter] = useState('');  // '' = alle
  const [neueDiagnoseFreitext, setNeueDiagnoseFreitext] = useState('');
  const [isSavingDiagnose, setIsSavingDiagnose] = useState(false);
  const [begutachtungVorschau, setBegutachtungVorschau] = useState(null);
  const [editTerminId, setEditTerminId] = useState(null);
  const [editTerminForm, setEditTerminForm] = useState({
    datum: '', uhrzeit: '', dauer_minuten: 45, termin_art: 'hausbesuch',
    notiz: '', mit_info_mail: true, mail_an_kunde: true, mail_an_angehoeriger: true, mit_google_kalender: true,
    erinnerung_24h_aktiv: true, erinnerung_morgen_aktiv: true,
    gueltigkeit_periode: '',
    ausserhalb_standard: false,  // Override-Schalter beim Verschieben
  });
  const [isSavingEditTermin, setIsSavingEditTermin] = useState(false);

  // Kalender-Overlay (Fullscreen-Ansicht mit Monatsgrid + Halbjahres-Mini).
  // kalenderKunde = Kunden-Kontext (null = generelle Ansicht aus dem Settings-
  // Menue, Objekt = Folge-Termin fuer diesen Kunden).
  const [isKalenderOpen, setIsKalenderOpen] = useState(false);
  const [kalenderKunde, setKalenderKunde] = useState(null);
  const [kalenderRefreshKey, setKalenderRefreshKey] = useState(0);

  // Replace-Tracking fuer die Sicht-Bausteine: Wenn der Berater zweimal
  // hintereinander auf Buttons derselben Reihe klickt, soll der zuletzt
  // eingefuegte Block ersetzt werden statt angefuegt. Wir merken uns also
  // pro Reihe den letzten exakten Text, den wir reingesetzt haben.
  const [letzterPatientBlock, setLetzterPatientBlock] = useState('');
  const [letzterPflegepersonBlock, setLetzterPflegepersonBlock] = useState('');

  // Hilfsfunktion: Standardtext für "Sicht des Beraters" aus den Bausteinen ziehen.
  const getBeraterStandardText = (bausteine) => {
    const std = (bausteine || []).find(s => s.shortcut === 'ber_standard');
    return std ? std.text : '';
  };

  // Reset aller Protokoll-Felder auf Default (mit ber_standard als Berater-Text).
  // Sicht der Beteiligten wird mit den beiden Stufe-2-Bausteinen vorbelegt
  // (Patient-Sicht oben, Pflegeperson-Sicht unten) — der Berater muss bei
  // unauffaelligen Faellen nichts mehr klicken; bei abweichender Lage
  // ersetzt ein Klick auf einen anderen Stufen-Button den jeweiligen Block.
  const resetProtokollFelder = (bausteine) => {
    let defaultText = '';
    let defaultPat = '';
    let defaultPflege = '';
    if (selectedClient) {
      // Kind-Modus: bei pflegebeduerftiger Person unter 12 Jahren wird
      // der kindgerechte Stufe-2-Text als Default eingesetzt.
      const kindModus = istKind(selectedClient);
      const stufenPatient = kindModus ? STUFEN_PATIENT_KIND : STUFEN_PATIENT;
      defaultPat = fuelleTemplate(stufenPatient[1].text, selectedClient, kindModus ? 'kind' : 'erwachsen');
      defaultPflege = fuelleTemplate(STUFEN_PFLEGEPERSON[1].text, selectedClient);
      defaultText = defaultPat + '\n\n' + defaultPflege;
    }
    setSichtBeteiligte(defaultText);
    setLetzterPatientBlock(defaultPat);
    setLetzterPflegepersonBlock(defaultPflege);
    setProtocolText(getBeraterStandardText(bausteine));
    setSichergestellt(true);
    setBegruendungNichtSichergestellt('');
    setMassnahmen([]);
    setMassnahmenBemerkung('');
    setBeratung7aAngezeigt(false);
    setBeratung7aGewuenscht(false);
    setEinwilligungDatenschutz(true);
    setEinwilligungDatenuebermittlung(true);
    setEinwilligung7a(false);
    setPdfErstelltDatum('');
    setProtokollPeriode('');
    setEditingProtokollId(null);
  };

  // Bei Wechsel des selectedClient: Terminart + Formular resetten + Historie laden.
  useEffect(() => {
    if (selectedClient?.type === 'video' || selectedClient?.type === 'hausbesuch') {
      setTerminArt(selectedClient.type);
    } else {
      setTerminArt('hausbesuch');
    }
    resetProtokollFelder(textSnippets);
    setHistorieExpanded(false);

    const kundeId = selectedClient?.kundeId
      || (selectedClient?.id?.startsWith('kunde-') ? selectedClient.id.slice('kunde-'.length) : null);
    if (kundeId) {
      let abgebrochen = false;
      (async () => {
        try {
          const [protokolle, termine, todos, diagnosen, vorschau] = await Promise.all([
            api.get(`/api/protokolle?kunde_id=${encodeURIComponent(kundeId)}`),
            api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`),
            api.get(`/api/todos?kunde_id=${encodeURIComponent(kundeId)}`).catch(() => []),
            api.get(`/api/kunden/${encodeURIComponent(kundeId)}/diagnosen`).catch(() => []),
            api.get(`/api/kunden/${encodeURIComponent(kundeId)}/begutachtung-vorschau`).catch(() => null),
          ]);
          if (abgebrochen) return;
          setProtokollHistorie(protokolle || []);
          setKundeTermine(termine || []);
          setKundeTodos(todos || []);
          setKundeDiagnosen(diagnosen || []);
          setBegutachtungVorschau(vorschau);
          // Termin fuers Protokoll vorbelegen: bei Sidebar-Auswahl den dort
          // gewaehlten Termin, sonst den juengsten ohne Protokoll, sonst
          // einfach den juengsten — Hendrik kann jederzeit umstellen.
          setProtokollTerminId(
            waehleStandardProtokollTermin(selectedClient, termine || [], protokolle || [])
          );
          setShowAdHocTerminForm(false);
        } catch {
          if (!abgebrochen) {
            setProtokollHistorie([]);
            setKundeTermine([]);
            setKundeTodos([]);
            setKundeDiagnosen([]);
            setBegutachtungVorschau(null);
          }
        }
      })();
      return () => { abgebrochen = true; };
    } else {
      setProtokollHistorie([]);
      setKundeTermine([]);
      setKundeTodos([]);
      setKundeDiagnosen([]);
      setBegutachtungVorschau(null);
    }
  }, [selectedClient?.id]);

  // Pflegecockpit-Info pro Kunde (kleines Aggregat fuer die Akten-Header-Zeile).
  useEffect(() => {
    const kundeId = selectedClient?.kundeId
      || (selectedClient?.id?.startsWith('kunde-') ? selectedClient.id.slice('kunde-'.length) : null);
    if (!kundeId) {
      setPflegecockpitInfo(null);
      setLianeBoxInfo(null);
      return;
    }
    let abgebrochen = false;
    (async () => {
      try {
        const info = await api.get(`/api/kunden/${kundeId}/pflegecockpit-info`);
        if (!abgebrochen) setPflegecockpitInfo(info);
      } catch (_) {
        if (!abgebrochen) setPflegecockpitInfo(null);
      }
    })();
    (async () => {
      try {
        const info = await api.get(`/api/kunden/${kundeId}/liane-box-info`);
        if (!abgebrochen) setLianeBoxInfo(info);
      } catch (_) {
        if (!abgebrochen) setLianeBoxInfo(null);
      }
    })();
    return () => { abgebrochen = true; };
  }, [selectedClient?.id]);

  // Diagnose-Katalog einmalig laden (gross, aber statisch — bleibt im Memory).
  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const liste = await api.get('/api/diagnosen/katalog');
        if (!abgebrochen) setDiagnoseKatalog(liste || []);
      } catch { /* still — Diagnose-Block zeigt dann nur Freitext-Option */ }
    })();
    return () => { abgebrochen = true; };
  }, []);

  // Bei Kundenwechsel: Diagnose-Segment-Filter aus dem Geburtsdatum
  // automatisch ableiten (Kinder < 18, Erwachsene 18-59, Senioren ab 60).
  // Ueberschreibt eine vorherige manuelle Auswahl bewusst, weil sich der
  // Kontext (anderer Kunde) komplett geaendert hat.
  useEffect(() => {
    const dob = selectedClient?.dob;
    if (!dob) {
      setDiagnoseSegmentFilter('');
      return;
    }
    const alter = calculateAge(dob);
    if (typeof alter !== 'number' || isNaN(alter)) { setDiagnoseSegmentFilter(''); return; }
    if (alter < 18) setDiagnoseSegmentFilter('kinder');
    else if (alter < 60) setDiagnoseSegmentFilter('erwachsene');
    else setDiagnoseSegmentFilter('senioren');
  }, [selectedClient?.dob]);

  // Wenn die Bausteine asynchron eintrudeln und protocolText noch leer ist,
  // das Berater-Default setzen.
  useEffect(() => {
    if (!editingProtokollId && protocolText === '' && textSnippets.length > 0) {
      setProtocolText(getBeraterStandardText(textSnippets));
    }
  }, [textSnippets]);

  // Termine fuer die Sidebar laden: zwei Fenster (Zukunft + Vergangenheit)
  // mit jeweils 6-Monats-Reichweite, davon zeigen wir oben/unten je 8 Stueck.
  // 6 Monate ist ein Kompromiss: weit genug, damit auch bei wenig Aktivitaet
  // 8 Termine in jeder Richtung gefunden werden, aber so klein, dass die
  // Antwort schlank bleibt.
  const ladeSidebarTermine = async () => {
    const heute = new Date();
    const heuteISO = heute.toISOString().slice(0, 10);
    const sechsMonateZurueck = new Date(heute); sechsMonateZurueck.setMonth(heute.getMonth() - 6);
    const sechsMonateVoraus = new Date(heute);  sechsMonateVoraus.setMonth(heute.getMonth() + 6);
    const gestern = new Date(heute); gestern.setDate(heute.getDate() - 1);
    const vonVergangen = sechsMonateZurueck.toISOString().slice(0, 10);
    const bisVergangen = gestern.toISOString().slice(0, 10);
    const bisZukunft   = sechsMonateVoraus.toISOString().slice(0, 10);

    const [zukunft, vergangen] = await Promise.all([
      api.get(`/api/termine?von=${heuteISO}&bis=${bisZukunft}`),
      api.get(`/api/termine?von=${vonVergangen}&bis=${bisVergangen}`),
    ]);
    // Backend sortiert ASC. Zukunft passt so, vergangen brauchen wir DESC
    // (neueste zuerst), also umdrehen.
    const z = (zukunft || []).slice(0, 8);
    const v = [...(vergangen || [])].reverse().slice(0, 8);
    setTerminListeZukunft(z);
    setTerminListeVergangen(v);
    return { zukunft: z, vergangen: v };
  };

  // Schulungsanfragen ohne fixen Termin laden (Sidebar-Sektion oben).
  // Liefert max. 15 Eintraege; im UI zeigen wir erst 5, "Mehr"-Button macht die
  // restlichen sichtbar.
  const ladeSchulungsAnfragen = async () => {
    try {
      const arr = await api.get('/api/kunden/schulungsanfragen-offen?limit=15');
      setSchulungsAnfragen(Array.isArray(arr) ? arr : []);
    } catch {
      setSchulungsAnfragen([]);
    }
  };

  // Neue Buchungs-Widget-Anfragen (Sidebar-Sektion zwischen "Naechste Termine"
  // und "Letzte Termine"): die zuletzt 15 angelegten Termine, deren Kunde aus
  // dem Buchungs-Widget kommt. UI zeigt erst 5.
  const ladeNeueBuchungen = async () => {
    try {
      const arr = await api.get('/api/termine?quelle=booking_widget&limit=15');
      setNeueBuchungen(Array.isArray(arr) ? arr : []);
    } catch {
      setNeueBuchungen([]);
    }
  };

  // Initial-Load: Sidebar-Termine + Textbausteine + Kunden + Pflegekassen.
  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const [{ zukunft, vergangen }, bausteine, kunden, kassen] = await Promise.all([
          ladeSidebarTermine(),
          api.get('/api/textbausteine'),
          api.get('/api/kunden'),
          api.get('/api/pflegekassen'),
          ladeSchulungsAnfragen(),
          ladeNeueBuchungen(),
        ]);
        if (abgebrochen) return;

        // Initial-Auswahl: bewusst KEINE — der Hauptbereich zeigt die
        // Termin-Pipeline-Uebersicht (letzte 4 Wochen). Erst wenn der
        // Berater einen Kunden anklickt (Pipeline-Tabelle, Sidebar oder
        // Home-Sprung), wird die Akte aufgeschlagen.
        setSelectedClient(null);
        setTextSnippets(bausteine || []);
        setPflegekassen(kassen || []);
        setAlleKunden(kunden || []);
      } catch (err) {
        if (abgebrochen) return;
        const msg = err instanceof ApiError
          ? (err.status === 401 ? 'Bitte erneut anmelden.' : (err.detail || 'Fehler beim Laden.'))
          : 'Verbindung zum Server fehlgeschlagen.';
        setLoadError(msg);
      } finally {
        if (!abgebrochen) setIsLoading(false);
      }
    })();

    return () => { abgebrochen = true; };
  }, []);

  // Wenn die Home-Seite einen Kunden uebergibt (pendingKundeId), suchen
  // wir den passenden Sidebar-Eintrag oder bauen einen Pseudo-Eintrag
  // aus alleKunden zusammen. Das laeuft erst, wenn die Listen geladen sind.
  useEffect(() => {
    if (!pendingKundeId) return;
    if (!alleKunden || alleKunden.length === 0) return;
    const auchTerminTreffer =
      terminListeZukunft.find(t => t.kundeId === pendingKundeId)
      || terminListeVergangen.find(t => t.kundeId === pendingKundeId);
    if (auchTerminTreffer) {
      setSelectedClient(auchTerminTreffer);
      if (pendingTab) setActiveTab(pendingTab);
      onPendingKundeConsumed && onPendingKundeConsumed();
      return;
    }
    const k = alleKunden.find(x => x.id === pendingKundeId);
    if (k) {
      setSelectedClient({
        ...k,
        id: 'kunde-' + k.id,
        kundeId: k.id,
        time: '',
        type: 'hausbesuch',
        status: 'kein_termin',
        date: '',
      });
      if (pendingTab) setActiveTab(pendingTab);
      onPendingKundeConsumed && onPendingKundeConsumed();
    }
  }, [pendingKundeId, pendingTab, alleKunden, terminListeZukunft, terminListeVergangen]);

  // Gemeinsame Payload für Speichern / PDF-Erstellen, kommt aus den Formular-States.
  const buildProtokollBody = (kundeId, mitPdf) => ({
    kunde_id: kundeId,
    // Vom Berater im Protokoll-Tab gewaehlter Termin. Wenn null, faellt das
    // Backend auf den juengsten passenden Termin zurueck bzw. legt einen
    // Ad-hoc-Termin an — diesen Pfad nutzen wir nur, wenn der Kunde gar
    // keine Termine hat und der Berater auch keinen Ad-hoc-Termin gesetzt hat.
    termin_id: protokollTerminId,
    termin_art: terminArt,
    erzeuge_pdf: mitPdf,
    sicht_berater: protocolText || null,
    sicht_beteiligte: sichtBeteiligte || null,
    sichergestellt: sichergestellt,
    begruendung_nicht_sichergestellt: sichergestellt ? null : (begruendungNichtSichergestellt || null),
    massnahmen: massnahmen.length ? massnahmen : null,
    massnahmen_bemerkung: massnahmenBemerkung || null,
    beratung_7a_angezeigt: beratung7aAngezeigt,
    beratung_7a_gewuenscht: beratung7aGewuenscht,
    einwilligung_datenschutz: einwilligungDatenschutz,
    einwilligung_datenuebermittlung: einwilligungDatenuebermittlung,
    einwilligung_7a: einwilligung7a,
    pdf_erstellt_datum: pdfErstelltDatum || '',
    gueltigkeit_periode: protokollPeriode || '',
  });

  const getKundeIdFromSelection = () => selectedClient?.kundeId
    || (selectedClient?.id?.startsWith('kunde-')
        ? selectedClient.id.slice('kunde-'.length)
        : selectedClient?.id);

  // Offenen Kunden nach oben (App.jsx) melden, damit die URL ihn fuehrt und
  // ein Reload wieder genau diese Akte oeffnet. null = keine Akte offen
  // (Pipeline-Uebersicht).
  useEffect(() => {
    if (!onKundeChange) return;
    onKundeChange(selectedClient ? getKundeIdFromSelection() : null);
  }, [selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nach manuellem/automatischem Geo-Update: offene Akte UND die zentrale
  // Kundenliste mit den neuen Koordinaten aktualisieren, damit die
  // Umkreis-Karte sofort frisch ist (Stale-State-Falle vermeiden).
  const handleGeoUpdate = (felder) => {
    const kid = getKundeIdFromSelection();
    setSelectedClient(prev => (prev ? { ...prev, ...felder } : prev));
    setAlleKunden(prev => prev.map(k => (k.id === kid ? { ...k, ...felder } : k)));
  };

  // Browser-Tab-Titel: zeigt den gerade geoeffneten Kunden, damit Hendrik
  // unter vielen Chrome-Tabs sofort den richtigen findet. Setzt sich beim
  // Schliessen der Akte / Verlassen des Dashboards wieder zurueck.
  useEffect(() => {
    const vorname = selectedClient?.firstName || '';
    const nachname = selectedClient?.lastName || '';
    if (vorname || nachname) {
      document.title = `${nachname}${nachname && vorname ? ', ' : ''}${vorname}`.trim();
    } else {
      document.title = 'Beratungs-Cockpit';
    }
    return () => { document.title = 'Beratungs-Cockpit'; };
  }, [selectedClient?.id, selectedClient?.firstName, selectedClient?.lastName]);

  const ladeHistorie = async (kundeId) => {
    try {
      const liste = await api.get(`/api/protokolle?kunde_id=${encodeURIComponent(kundeId)}`);
      setProtokollHistorie(liste || []);
    } catch { /* still; Historie ist UI-Komfort, nicht kritisch */ }
  };

  const speichereProtokoll = async (kundeId, mitPdf) => {
    const body = buildProtokollBody(kundeId, mitPdf);
    if (editingProtokollId) {
      return await api.put(`/api/protokolle/${editingProtokollId}`, body);
    }
    return await api.post('/api/protokolle', body);
  };

  const handleCreatePdf = async () => {
    if (!selectedClient) return;
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) { alert('Kein Kunde ausgewählt.'); return; }
    setIsCreatingPdf(true);
    try {
      const resp = await speichereProtokoll(kundeId, true);
      if (resp?.download_url_intern) {
        window.open(mitCacheBuster(resp.download_url_intern), '_blank', 'noopener');
      }
      setEditingProtokollId(null);
      resetProtokollFelder(textSnippets);
      await ladeHistorie(kundeId);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim PDF-Erstellen.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsCreatingPdf(false);
    }
  };

  const handleOnlySave = async () => {
    if (!selectedClient) return;
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) { alert('Kein Kunde ausgewählt.'); return; }
    setIsSavingProtokoll(true);
    try {
      await speichereProtokoll(kundeId, false);
      setEditingProtokollId(null);
      resetProtokollFelder(textSnippets);
      await ladeHistorie(kundeId);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Speichern.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsSavingProtokoll(false);
    }
  };

  const handleEditProtokoll = async (protokollId) => {
    try {
      const p = await api.get(`/api/protokolle/${protokollId}`);
      setSichtBeteiligte(p.sicht_beteiligte || '');
      // Beim Bearbeiten eines bestehenden Protokolls wissen wir nicht, welcher
      // Block "Patient" und welcher "Pflegeperson" war — Tracking auf Null,
      // erster Klick auf einen Sicht-Button hangt also an statt zu ersetzen.
      setLetzterPatientBlock('');
      setLetzterPflegepersonBlock('');
      setProtocolText(p.sicht_berater || '');
      setSichergestellt(p.sichergestellt !== false);
      setBegruendungNichtSichergestellt(p.begruendung_nicht_sichergestellt || '');
      setMassnahmen(Array.isArray(p.massnahmen) ? p.massnahmen : []);
      setMassnahmenBemerkung(p.massnahmen_bemerkung || '');
      setBeratung7aAngezeigt(!!p.beratung_7a_angezeigt);
      setBeratung7aGewuenscht(!!p.beratung_7a_gewuenscht);
      setEinwilligungDatenschutz(p.einwilligung_datenschutz !== false);
      setEinwilligungDatenuebermittlung(p.einwilligung_datenuebermittlung !== false);
      setEinwilligung7a(!!p.einwilligung_7a);
      setPdfErstelltDatum(p.pdf_erstellt_datum || '');
      setProtokollPeriode(p.gueltigkeit_periode || '');
      if (p.termin_art === 'video' || p.termin_art === 'hausbesuch') setTerminArt(p.termin_art);
      if (p.termin_id) setProtokollTerminId(p.termin_id);
      setEditingProtokollId(protokollId);
      // Zum Formular scrollen (pragmatisch, keine refs)
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 50);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Laden.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    }
  };

  const handleCancelEdit = () => {
    setEditingProtokollId(null);
    resetProtokollFelder(textSnippets);
  };

  // Ad-hoc-Termin fuer das Protokoll anlegen: stille Backend-Anlage
  // (status='durchgefuehrt', ohne Info-Mail / Google-Kalender,
  // ausserhalb_standard=true damit Vergangenheits-/Slot-Pruefung
  // uebersprungen wird). Anschliessend wird der neue Termin als
  // protokollTerminId gesetzt und kundeTermine neu geladen.
  const handleAdHocTerminAnlegen = async () => {
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) { alert('Kein Kunde ausgewählt.'); return; }
    if (!adHocDatum) { alert('Bitte Datum auswählen.'); return; }
    setIsCreatingAdHocTermin(true);
    try {
      const resp = await api.post('/api/termine', {
        kunde_id: kundeId,
        datum: adHocDatum,
        uhrzeit: adHocUhrzeit || null,
        termin_art: adHocTerminArt,
        status: 'durchgefuehrt',
        mit_info_mail: false,
        mit_google_kalender: false,
        ausserhalb_standard: true,
        erinnerung_24h_aktiv: false,
        erinnerung_morgen_aktiv: false,
      });
      if (resp?.id) {
        const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
        setKundeTermine(liste || []);
        setProtokollTerminId(resp.id);
        setTerminArt(adHocTerminArt);
        setShowAdHocTerminForm(false);
        setAdHocDatum('');
        setAdHocUhrzeit('');
      }
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Anlegen.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsCreatingAdHocTermin(false);
    }
  };

  const handleOpenVersand = (protokollId, typ = 'protokoll') => {
    setVersandFor(protokollId);
    setVersandTyp(typ);
    // Standardmäßig beide anhaken — vorausgesetzt, es gibt jeweils eine Mail.
    setVersandAnPatient(!!selectedClient?.email);
    setVersandAnAngehoeriger(!!selectedClient?.relative?.email);
    setVersandZusatztext('');
  };

  const handleCancelVersand = () => {
    setVersandFor(null);
    setVersandZusatztext('');
  };

  const handleConfirmVersand = async () => {
    if (!versandFor) return;
    if (!versandAnPatient && !versandAnAngehoeriger) {
      alert('Bitte mindestens einen Empfänger auswählen.');
      return;
    }
    setIsSending(true);
    try {
      const endpoint = versandTyp === 'scan'
        ? `/api/dokumente/${versandFor}/versenden`
        : `/api/protokolle/${versandFor}/versenden`;
      const resp = await api.post(endpoint, {
        an_patient: versandAnPatient,
        an_angehoeriger: versandAnAngehoeriger,
        zusatztext: versandZusatztext.trim() || null,
      });
      const liste = Array.isArray(resp?.empfaenger) ? resp.empfaenger.join(', ') : (resp?.empfaenger || '');
      if (resp?.test_modus) {
        alert(`Test-Modus: Mail wurde nur im Server-Log simuliert. Empfänger: ${liste}`);
      } else {
        alert(`PDF an ${liste} gesendet.`);
      }
      if (versandTyp === 'scan') setScanRefreshKey((k) => k + 1);
      handleCancelVersand();
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Versenden.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsSending(false);
    }
  };

  const handleZurAbrechnung = async (protokollId) => {
    try {
      await api.post(`/api/protokolle/${protokollId}/zur-abrechnung`, {});
      const kundeId = getKundeIdFromSelection();
      if (kundeId) await ladeHistorie(kundeId);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Markieren.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    }
  };

  const handleZurueckAusAbrechnung = async (protokollId) => {
    if (!window.confirm('Protokoll aus der AS-Bremen-Warteschlange zurueckziehen? Es kann dann wieder bearbeitet werden.')) return;
    try {
      await api.del(`/api/protokolle/${protokollId}/zur-abrechnung`);
      const kundeId = getKundeIdFromSelection();
      if (kundeId) await ladeHistorie(kundeId);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Zurueckziehen.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    }
  };

  const handleDeleteProtokoll = async (protokollId) => {
    if (!window.confirm('Protokoll wirklich löschen? Das zugehörige PDF wird auch entfernt.')) return;
    try {
      await api.del(`/api/protokolle/${protokollId}`);
      if (editingProtokollId === protokollId) {
        setEditingProtokollId(null);
        resetProtokollFelder(textSnippets);
      }
      const kundeId = getKundeIdFromSelection();
      if (kundeId) await ladeHistorie(kundeId);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Löschen.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    }
  };

  const openEditModal = () => {
    setEditFormData({ ...selectedClient }); 
    setModalTab('patient'); 
    setIsEditModalOpen(true);
  };

  const handleKundeLoeschen = async () => {
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) return;
    const name = `${selectedClient?.lastName || ''}, ${selectedClient?.firstName || ''}`.replace(/^, |, $/g, '').trim() || 'dieser Kunde';
    if (!window.confirm(
      `Kunden löschen?\n\n${name}\n\n` +
      `Der Kunde wird in den Papierkorb verschoben und nach 30 Tagen automatisch ` +
      `endgültig gelöscht — dann sind alle personenbezogenen Daten (Termine, ` +
      `Protokolle, Mails, Aufgaben) unwiderruflich entfernt.\n\n` +
      `Bis dahin kann ein Admin den Kunden im Papierkorb wiederherstellen.\n\n` +
      `Fortfahren?`
    )) return;
    try {
      await api.del(`/api/kunden/${kundeId}`);
      setAlleKunden(prev => (prev || []).filter(k => k.id !== kundeId));
      setSelectedClient(null);
    } catch (e) {
      console.warn('Kunde loeschen fehlgeschlagen', e);
      alert(e?.detail || 'Löschen fehlgeschlagen — bitte erneut versuchen.');
    }
  };

  const handleKundeKopieren = async () => {
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) return;
    try {
      const neu = await api.post(`/api/kunden/${kundeId}/kopie`, {});
      // Neuen Kunden in die Liste aufnehmen, Akte oeffnen und direkt das
      // Bearbeiten-Fenster zeigen — der Berater aendert nur noch den Namen.
      setAlleKunden(prev => [neu, ...(prev || [])]);
      setSelectedClient(neu);
      setEditFormData({ ...neu });
      setModalTab('patient');
      setIsEditModalOpen(true);
    } catch (e) {
      console.warn('Kunde kopieren fehlgeschlagen', e);
      alert(e?.detail || 'Kopie konnte nicht angelegt werden.');
    }
  };

  // Videoraum fuer einen bestehenden Video-Termin (neu) anlegen — z.B. wenn
  // Sprechstunde.Online bei der Termin-Anlage kurz nicht erreichbar war und
  // deshalb kein Link erzeugt wurde. Aktualisiert Akte + Termin-Liste.
  const handleVideoraumNeu = async (terminId) => {
    if (!terminId) return;
    try {
      const akt = await api.post(`/api/termine/${terminId}/videoraum`, {});
      setSelectedClient(prev => (prev && prev.id === terminId ? { ...prev, ...akt } : prev));
      setKundeTermine(prev => (prev || []).map(t => (t.id === terminId ? { ...t, ...akt } : t)));
      if (akt?.videoWarnung) {
        alert('Der Videoraum konnte weiterhin nicht angelegt werden:\n\n'
          + akt.videoWarnung
          + '\n\nMeist liegt es daran, dass der Sprechstunde.Online-Zugang (SPO_API_KEY) '
          + 'auf dem Server fehlt oder abgelaufen ist. Bitte in ein paar Minuten erneut '
          + 'versuchen — klappt es dann nicht, den Zugang pruefen.');
      }
    } catch (e) {
      console.warn('Videoraum neu anlegen fehlgeschlagen', e);
      alert(e?.detail || 'Videoraum konnte nicht angelegt werden.');
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRelativeEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, relative: { ...prev.relative, [name]: value } }));
  };

  const handleAddRelative = () => {
    setEditFormData(prev => ({ ...prev, relative: { salutation: '', firstName: '', lastName: '', relationship: 'Sohn', phone: '', email: '' } }));
  };

  const handleRemoveRelative = () => {
    if(window.confirm("Möchten Sie diese Kontaktperson wirklich aus der Akte entfernen?")) {
        setEditFormData(prev => { const newData = { ...prev }; delete newData.relative; return newData; });
        setModalTab('patient');
    }
  };

  const handleNewClient = () => {
    setEditFormData({
      id: 'new-' + Date.now(), salutation: '', firstName: '', lastName: '', dob: '',
      insuranceId: '', insuranceName: '', insuranceNumber: '', insuranceType: '', careLevel: '0',
      street: '', zip: '', city: '', state: '', phone: '', email: '',
      tour: '', partnerName: '', dauerhaftVideo: false,
      diagnosen: [], kunden_notiz: '', isNew: true
    });
    setModalTab('patient');
    setIsEditModalOpen(true);
  };

  // === Manuelle Termin-Anlage ===
  const handleOpenTerminModal = () => {
    // Vorauswahl: falls aktuell ein Kunde selektiert ist, gleich uebernehmen.
    const kundeIdVoraus = getKundeIdFromSelection() || '';
    setTerminForm({
      kunde_id: kundeIdVoraus, datum: heuteISO, uhrzeit: '10:00',
      termin_art: 'video', dauer_minuten: 30, notiz: '',
      mit_google_kalender: true,
      mit_info_mail: true,
    mail_an_kunde: true,
    mail_an_angehoeriger: true,
    erinnerung_24h_aktiv: true,
    erinnerung_morgen_aktiv: true,
      gueltigkeit_periode: '',
      ausserhalb_standard: false,  // Override bei jedem Oeffnen frisch
    });
    setKundeSearch('');
    // "+ Neuer Termin" aus der Sidebar: Kundenauswahl bleibt sichtbar
    // (Kontext unbekannt).
    setTerminModalKundeGesperrt(false);
    setIsTerminModalOpen(true);
  };

  const handleTerminChange = (field, value) => {
    setTerminForm(prev => ({ ...prev, [field]: value }));
  };

  // "+ Neuer Termin" direkt aus der Akte: oeffnet das Anlage-Modal sofort
  // mit der aktuell geoeffneten Kundin/dem Kunden vorbefuellt UND gesperrt.
  // Bewusst kein Umweg ueber den Kalender — der Folge-Termin-Knopf daneben
  // macht das, mit dem bekannten Risiko, dass ein Klick auf einen schon
  // belegten Slot in den Bearbeiten-Modus eines fremden Termins fuehrt.
  // Dieser Direkt-Knopf umgeht die Falle.
  const handleNeuerTerminAusAkte = () => {
    if (!selectedClient) return;
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) return;
    setTerminForm({
      kunde_id: kundeId,
      datum: heuteISO,
      uhrzeit: '10:00',
      termin_art: 'video',
      dauer_minuten: 30,
      notiz: '',
      mit_google_kalender: true,
      mit_info_mail: true,
      mail_an_kunde: true,
      mail_an_angehoeriger: true,
      erinnerung_24h_aktiv: true,
      erinnerung_morgen_aktiv: true,
      gueltigkeit_periode: '',
      ausserhalb_standard: false,
    });
    setKundeSearch('');
    setTerminModalKundeGesperrt(true);
    setIsTerminModalOpen(true);
  };

  const handleSaveTermin = async () => {
    if (!terminForm.kunde_id) { alert('Bitte einen Kunden auswählen.'); return; }
    if (!terminForm.datum || !terminForm.uhrzeit) { alert('Datum und Uhrzeit sind Pflichtfelder.'); return; }
    setIsSavingTermin(true);
    try {
      const angelegt = await api.post('/api/termine', {
        kunde_id: terminForm.kunde_id,
        datum: terminForm.datum,
        uhrzeit: terminForm.uhrzeit,
        termin_art: terminForm.termin_art,
        dauer_minuten: terminForm.dauer_minuten || 30,
        notiz: terminForm.notiz || null,
        mit_google_kalender: terminForm.mit_google_kalender,
        mit_info_mail: terminForm.mit_info_mail,
        mail_an_kunde: terminForm.mail_an_kunde,
        mail_an_angehoeriger: terminForm.mail_an_angehoeriger,
        erinnerung_24h_aktiv: terminForm.erinnerung_24h_aktiv,
        erinnerung_morgen_aktiv: terminForm.erinnerung_morgen_aktiv,
        gueltigkeit_periode: terminForm.gueltigkeit_periode || null,
        ausserhalb_standard: terminForm.ausserhalb_standard,
      });
      // Sidebar-Termine neu laden, damit der neue Termin in der oberen oder
      // unteren Liste auftaucht. Kunden parallel mitziehen, damit die Suche
      // nach Anlage-Modal-Input frisch bleibt. Wenn der Termin fuer den
      // gerade geoeffneten Kunden ist, ziehen wir auch dessen Akten-Termin-
      // Liste frisch — sonst muesste Hendrik die Seite hart neu laden, um
      // den frisch angelegten Termin in der Akte zu sehen.
      try {
        const promises = [
          ladeSidebarTermine(),
          ladeSchulungsAnfragen(),
          ladeNeueBuchungen(),
          api.get('/api/kunden'),
        ];
        const aktuelleKundeId = getKundeIdFromSelection ? getKundeIdFromSelection() : null;
        const istFuerOffeneAkte = aktuelleKundeId && aktuelleKundeId === terminForm.kunde_id;
        if (istFuerOffeneAkte) {
          promises.push(api.get(`/api/termine?kunde_id=${encodeURIComponent(terminForm.kunde_id)}`));
        }
        const ergebnisse = await Promise.all(promises);
        const kunden = ergebnisse[3];
        setAlleKunden(Array.isArray(kunden) ? kunden : []);
        if (istFuerOffeneAkte) {
          setKundeTermine(Array.isArray(ergebnisse[4]) ? ergebnisse[4] : []);
        }
        // Wenn der Kalender offen ist und wir gerade einen Termin fuer
        // einen Kunden angelegt haben, merken wir uns diesen Kunden als
        // kalenderKunde — dann zeigt der "Zurueck"-Button im Kalender-
        // Header "Zurueck zur Akte von <Name>" und beim Schliessen
        // landen wir direkt in der frischen Akte.
        if (isKalenderOpen && terminForm.kunde_id) {
          const k = (kunden || []).find(x => x.id === terminForm.kunde_id);
          if (k) {
            setKalenderKunde({
              id: k.id,
              firstName: k.firstName || '',
              lastName: k.lastName || '',
            });
          }
        }
      } catch (e) {
        console.warn('handleSaveTermin: Reload nach Anlage fehlgeschlagen', e);
      }
      setIsTerminModalOpen(false);
      setKalenderRefreshKey(k => k + 1);
      // Video-Raum konnte nicht angelegt werden? Sofort deutlich sagen —
      // sonst faellt es erst spaeter auf, dass kein Link da ist.
      if (angelegt?.videoWarnung) {
        alert('Der Termin wurde angelegt, ABER der Videoraum-Link konnte NICHT '
          + 'erzeugt werden:\n\n' + angelegt.videoWarnung
          + '\n\nEs wurde KEINE Mail an den Kunden verschickt. Du kannst den '
          + 'Videoraum in der Akte beim Termin per "Videoraum anlegen" nachziehen '
          + 'und die Info-Mail danach senden.');
      }
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Anlegen des Termins.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsSavingTermin(false);
    }
  };

  // === Kalender-Overlay: Einstiege + Callbacks ===
  const handleFolgeTermin = () => {
    // Aus der Kunden-Akte: "Folge-Termin"-Button oeffnet den Kalender im
    // Kontext des aktuellen Kunden.
    if (!selectedClient) return;
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) return;
    setKalenderKunde({
      id: kundeId,
      firstName: selectedClient.firstName || '',
      lastName: selectedClient.lastName || '',
    });
    setIsKalenderOpen(true);
  };

  const handleKalenderSchliessen = () => {
    // Wenn der Kalender im Kunden-Kontext geoeffnet war (Folge-Termin-Flow):
    // - kundeTermine neu laden, damit der frisch gebuchte Termin in der
    //   Akten-Termin-Liste sichtbar ist
    // - Sicherstellen, dass selectedClient noch auf diesen Kunden zeigt
    //   (falls der Berater zwischendurch ueber den Kalender-Verlauf einen
    //   anderen Kunden aktiviert hat, holen wir den Ursprungskunden zurueck)
    const kundeIdNachher = kalenderKunde?.id || null;
    setIsKalenderOpen(false);
    setKalenderKunde(null);
    if (kundeIdNachher) {
      api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeIdNachher)}`)
        .then(liste => setKundeTermine(liste || []))
        .catch(() => { /* still */ });
      // Falls zwischenzeitlich ein anderer Kunde selektiert wurde,
      // springen wir zurueck zum Ursprungskunden — dafuer suchen wir
      // einen passenden Sidebar-Eintrag oder bauen einen Platzhalter.
      const aktuellId = selectedClient?.kundeId || (selectedClient?.id?.startsWith('kunde-') ? selectedClient.id.slice('kunde-'.length) : null);
      if (aktuellId !== kundeIdNachher) {
        const treffer = terminListeZukunft.find(t => t.kundeId === kundeIdNachher)
          || terminListeVergangen.find(t => t.kundeId === kundeIdNachher);
        if (treffer) {
          setSelectedClient(treffer);
        } else {
          const k = (alleKunden || []).find(x => x.id === kundeIdNachher);
          if (k) {
            setSelectedClient({
              ...k,
              id: 'kunde-' + k.id,
              kundeId: k.id,
              time: '',
              type: k.dauerhaft_video ? 'video' : 'hausbesuch',
              status: 'kein_termin',
            });
          }
        }
      }
    }
  };

  const handleKalenderAnlageOeffnen = (datumISO, uhrzeit, kundeIdVoraus) => {
    // Kalender-Zelle angeklickt -> bestehendes Termin-Anlage-Modal oeffnen,
    // mit vorausgefuellten Werten. Die bestehenden Handler uebernehmen
    // Validierung + Speichern.
    setTerminForm(prev => ({
      ...prev,
      kunde_id: kundeIdVoraus || '',
      datum: datumISO,
      uhrzeit: uhrzeit || '10:00',
      termin_art: 'video',
      dauer_minuten: 30,
      notiz: '',
      mit_google_kalender: true,
      mit_info_mail: true,
    mail_an_kunde: true,
    mail_an_angehoeriger: true,
    erinnerung_24h_aktiv: true,
    erinnerung_morgen_aktiv: true,
      gueltigkeit_periode: '',
      ausserhalb_standard: false,
    }));
    setKundeSearch('');
    // Kundenauswahl nur sperren, wenn wir aus dem "Folge-Termin"-Flow
    // kommen (Kunde-Kontext steht fest). Im Ueberblick-Modus (ohne kunde)
    // bleibt die Suchliste sichtbar.
    setTerminModalKundeGesperrt(!!kundeIdVoraus);
    setIsTerminModalOpen(true);
  };

  const handleKalenderEditOeffnen = (termin) => {
    // Kalender-Chip angeklickt -> bestehendes Edit-Termin-Modal oeffnen.
    handleOpenEditTermin(termin);
  };

  // Settings-Dropdown in App.jsx triggert 'kalender:open' per CustomEvent —
  // damit kann der Kalender auch ohne Kunden-Kontext geoeffnet werden.
  useEffect(() => {
    const handler = () => {
      setKalenderKunde(null);
      setIsKalenderOpen(true);
    };
    window.addEventListener('kalender:open', handler);
    return () => window.removeEventListener('kalender:open', handler);
  }, []);

  // Fehler aus dem Backend lesbar formatieren — auch wenn 'detail' ein Array
  // (FastAPI-Validation) oder leer ist. So sehen wir im Alert konkret, was
  // der Server moniert.
  const formatApiFehler = (err, fallbackText) => {
    if (!(err instanceof ApiError)) {
      return 'Verbindung zum Server fehlgeschlagen.';
    }
    const d = err.detail;
    if (typeof d === 'string' && d) return d;
    if (Array.isArray(d) && d.length > 0) {
      return d.map(e => e.msg || JSON.stringify(e)).join('; ');
    }
    if (d && typeof d === 'object') return JSON.stringify(d);
    return `${fallbackText} (HTTP ${err.status})`;
  };

  // === To-dos: Anlegen, Erledigt-Toggle, Loeschen ===
  const handleAddTodo = async () => {
    const text = neuerTodoText.trim();
    if (!text) return;
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) { alert('Kein Kunde ausgewählt.'); return; }
    setIsSavingTodo(true);
    try {
      const neu = await api.post('/api/todos', {
        kunde_id: kundeId,
        text,
        faellig_am: neuerTodoFaellig || null,
        prioritaet: neuerTodoPrio || 'B',
      });
      setKundeTodos(prev => [neu, ...prev]);
      setNeuerTodoText('');
      setNeuerTodoFaellig('');
      setNeuerTodoPrio('B');
    } catch (err) {
      alert(formatApiFehler(err, 'Anlegen fehlgeschlagen.'));
    } finally {
      setIsSavingTodo(false);
    }
  };

  const handleToggleTodo = async (todo) => {
    try {
      const aktualisiert = await api.put(`/api/todos/${todo.id}`, { erledigt: !todo.erledigt });
      setKundeTodos(prev => prev.map(t => t.id === todo.id ? aktualisiert : t));
    } catch (err) {
      alert(formatApiFehler(err, 'Update fehlgeschlagen.'));
    }
  };

  // Inline-Edit: Text / Faelligkeitsdatum / Prioritaet kommen einzeln oder
  // gemeinsam aus dem TodoZeile-Edit-Modus. Wir schicken nur die Felder
  // weiter, die der Berater geaendert hat.
  const handleUpdateTodo = async (todo, patch) => {
    try {
      const aktualisiert = await api.put(`/api/todos/${todo.id}`, patch);
      setKundeTodos(prev => prev.map(t => t.id === todo.id ? aktualisiert : t));
      return aktualisiert;
    } catch (err) {
      alert(formatApiFehler(err, 'Update fehlgeschlagen.'));
      throw err;
    }
  };

  const handleDeleteTodo = async (todo) => {
    if (!confirm(`To-do wirklich löschen?\n\n„${todo.text}"`)) return;
    try {
      await api.del(`/api/todos/${todo.id}`);
      setKundeTodos(prev => prev.filter(t => t.id !== todo.id));
    } catch (err) {
      alert(formatApiFehler(err, 'Löschen fehlgeschlagen.'));
    }
  };

  // === Diagnosen: Katalog-Eintrag oder Freitext zum Kunden hinzufuegen ===
  const reloadVorschau = async (kundeId) => {
    try {
      const v = await api.get(`/api/kunden/${encodeURIComponent(kundeId)}/begutachtung-vorschau`);
      setBegutachtungVorschau(v);
    } catch { /* still */ }
  };

  const handleAddDiagnose = async ({ diagnoseId = null, freitext = null }) => {
    const kundeId = getKundeIdFromSelection();
    if (!kundeId) { alert('Kein Kunde ausgewählt.'); return; }
    if (!diagnoseId && !(freitext && freitext.trim())) return;
    setIsSavingDiagnose(true);
    try {
      const neu = await api.post(`/api/kunden/${encodeURIComponent(kundeId)}/diagnosen`, {
        diagnose_id: diagnoseId,
        freitext: freitext ? freitext.trim() : null,
      });
      setKundeDiagnosen(prev => [neu, ...prev]);
      setDiagnoseSuche('');
      setNeueDiagnoseFreitext('');
      reloadVorschau(kundeId);
    } catch (err) {
      alert(formatApiFehler(err, 'Diagnose hinzufügen fehlgeschlagen.'));
    } finally {
      setIsSavingDiagnose(false);
    }
  };

  const handleDeleteDiagnose = async (kd) => {
    if (!confirm(`Diagnose entfernen?\n\n„${kd.name}"`)) return;
    const kundeId = getKundeIdFromSelection();
    try {
      await api.del(`/api/kunden-diagnosen/${kd.id}`);
      setKundeDiagnosen(prev => prev.filter(x => x.id !== kd.id));
      if (kundeId) reloadVorschau(kundeId);
    } catch (err) {
      alert(formatApiFehler(err, 'Löschen fehlgeschlagen.'));
    }
  };

  // === Termin in der Kundenakte bearbeiten / verschieben ===
  const handleOpenEditTermin = (termin) => {
    setEditTerminId(termin.id);
    setEditTerminForm({
      datum: termin.date || '',
      uhrzeit: termin.time || '',
      dauer_minuten: termin.dauer_minuten || 45,
      termin_art: termin.type || 'hausbesuch',
      notiz: '',
      mit_info_mail: true,
    mail_an_kunde: true,
    mail_an_angehoeriger: true,
    erinnerung_24h_aktiv: true,
    erinnerung_morgen_aktiv: true,
      mit_google_kalender: true,
      gueltigkeit_periode: termin.gueltigkeit_periode || '',
      ausserhalb_standard: false,  // Bei jedem Oeffnen frisch — Override wird gezielt aktiviert
    });
  };

  const handleCancelEditTermin = () => {
    setEditTerminId(null);
  };

  const handleEditTerminChange = (field, value) => {
    setEditTerminForm(prev => ({ ...prev, [field]: value }));
  };

  const reloadAppointmentsSidebar = async () => {
    try {
      const [, , , kunden] = await Promise.all([
        ladeSidebarTermine(),
        ladeSchulungsAnfragen(),
        ladeNeueBuchungen(),
        api.get('/api/kunden'),
      ]);
      setAlleKunden(kunden || []);
    } catch { /* Reload ist UI-Komfort, Fehler nicht eskalieren */ }
  };

  const handleSaveEditTermin = async () => {
    if (!editTerminId) return;
    if (!editTerminForm.datum || !editTerminForm.uhrzeit) {
      alert('Datum und Uhrzeit sind Pflichtfelder.');
      return;
    }
    setIsSavingEditTermin(true);
    try {
      await api.put(`/api/termine/${editTerminId}`, {
        datum: editTerminForm.datum,
        uhrzeit: editTerminForm.uhrzeit,
        dauer_minuten: editTerminForm.dauer_minuten || 45,
        termin_art: editTerminForm.termin_art,
        notiz: editTerminForm.notiz || null,
        mit_info_mail: editTerminForm.mit_info_mail,
        mail_an_kunde: editTerminForm.mail_an_kunde,
        mail_an_angehoeriger: editTerminForm.mail_an_angehoeriger,
        erinnerung_24h_aktiv: editTerminForm.erinnerung_24h_aktiv,
        erinnerung_morgen_aktiv: editTerminForm.erinnerung_morgen_aktiv,
        mit_google_kalender: editTerminForm.mit_google_kalender,
        // Leerer String wird im Backend als NULL (Auto) gespeichert.
        gueltigkeit_periode: editTerminForm.gueltigkeit_periode || '',
        ausserhalb_standard: editTerminForm.ausserhalb_standard,
      });
      // Termin-Historie dieses Kunden neu laden
      const kundeId = getKundeIdFromSelection();
      if (kundeId) {
        try {
          const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
          setKundeTermine(liste || []);
        } catch { /* still */ }
      }
      // Sidebar (Wochenfenster) aktualisieren — der Termin kann reingerutscht
      // oder rausgefallen sein.
      await reloadAppointmentsSidebar();
      setEditTerminId(null);
      setKalenderRefreshKey(k => k + 1);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Speichern.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsSavingEditTermin(false);
    }
  };

  // Termin endgueltig loeschen (kein Mail-Versand). GCal-Eintrag wird im
  // Backend automatisch mit-geloescht. Bei 409 (Termin hat ein Protokoll)
  // klare Meldung an den User.
  const handleDeleteEditTermin = async () => {
    if (!editTerminId) return;
    const bestaetigt = window.confirm(
      'Termin wirklich endgueltig loeschen?\n\n'
      + '• Es wird KEINE Mail an den Kunden geschickt\n'
      + '• Der Eintrag im Google-Kalender wird ebenfalls geloescht\n'
      + '• Diese Aktion kann nicht rueckgaengig gemacht werden'
    );
    if (!bestaetigt) return;
    setIsSavingEditTermin(true);
    try {
      await api.del(`/api/termine/${editTerminId}`);
      // Akten-Termine neu laden
      const kundeId = getKundeIdFromSelection();
      if (kundeId) {
        try {
          const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
          setKundeTermine(liste || []);
        } catch { /* still */ }
      }
      await reloadAppointmentsSidebar();
      setEditTerminId(null);
      setKalenderRefreshKey(k => k + 1);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.detail || `Fehler ${err.status} beim Loeschen.`)
        : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsSavingEditTermin(false);
    }
  };

  // Wandelt das UI-FormData (camelCase) in das Backend-Schema (snake_case).
  const buildKundeBody = (fd) => ({
    vorname: fd.firstName || "",
    nachname: fd.lastName || "",
    anrede: fd.salutation || null,
    geburtsdatum: fd.dob || null,
    telefon: fd.phone || null,
    email: fd.email || null,
    strasse: fd.street || null,
    plz: fd.zip || null,
    ort: fd.city || null,
    bundesland: fd.state || null,
    pflegekasse_id: fd.insuranceId || null,
    versichertennummer: fd.insuranceNumber || null,
    pflegegrad: fd.careLevel !== undefined && fd.careLevel !== "" ? parseInt(fd.careLevel, 10) : null,
    notizen: fd.kunden_notiz || null,
    versicherungsart: fd.insuranceType || null,
    tour: fd.tour ?? null,
    partner_name: fd.partnerName ?? null,
    dauerhaft_video: typeof fd.dauerhaftVideo === 'boolean' ? fd.dauerhaftVideo : null,
    angehoeriger: fd.relative ? {
      vorname: fd.relative.firstName || "",
      nachname: fd.relative.lastName || "",
      anrede: fd.relative.salutation || null,
      verwandtschaft: fd.relative.relationship || null,
      telefon: fd.relative.phone || null,
      email: fd.relative.email || null,
    } : null,
  });

  const saveClientData = async () => {
    setIsSaving(true);
    try {
      if (editFormData.isNew) {
        const kunde = await api.post('/api/kunden', buildKundeBody(editFormData));
        // Neuer Kunde hat noch keinen Termin und taucht damit weder in der
        // Zukunfts- noch in der Vergangenheits-Liste auf. Wir setzen ihn als
        // ausgewaehlten Client, damit der Berater direkt seine Akte sieht und
        // einen Termin anlegen kann. In 'alleKunden' merken wir ihn fuer die
        // Suche vor.
        const platzhalter = {
          ...kunde,
          id: 'kunde-' + kunde.id,
          kundeId: kunde.id,
          time: '',
          type: editFormData.type || 'hausbesuch',
          status: 'kein_termin',
        };
        setSelectedClient(platzhalter);
        setAlleKunden(prev => [kunde, ...(prev || []).filter(k => k.id !== kunde.id)]);
      } else {
        // Bestehenden Kunden aktualisieren. Kunden-ID aus selectedClient
        // (kundeId fuer Termin-Eintraege, 'kunde-PREFIX' fuer Platzhalter-IDs).
        const kundeId = selectedClient?.kundeId
          || (selectedClient?.id?.startsWith('kunde-')
                ? selectedClient.id.slice('kunde-'.length)
                : editFormData.id);
        const aktualisierter = await api.put(`/api/kunden/${kundeId}`, buildKundeBody(editFormData));
        // Beide Sidebar-Listen aktualisieren: alle Termine mit gleicher
        // Kunden-ID bekommen die neuen Stammdaten (Name etc. sind dort drin).
        const refreshList = (liste) => liste.map(apt => {
          if (apt.kundeId !== kundeId) return apt;
          return { ...apt, ...aktualisierter, id: apt.id, kundeId, time: apt.time, type: apt.type, status: apt.status, date: apt.date };
        });
        setTerminListeZukunft(prev => refreshList(prev));
        setTerminListeVergangen(prev => refreshList(prev));
        setSelectedClient(prev => prev ? { ...prev, ...aktualisierter, id: prev.id, kundeId, time: prev.time, type: prev.type, status: prev.status } : prev);
        // Zentrale Kundenliste mitziehen, damit Sidebar-Suche und alle anderen
        // Stellen sofort den neuen Stand sehen (statt erst nach App-Neustart).
        setAlleKunden(prev => Array.isArray(prev)
          ? prev.map(k => k.id === kundeId ? { ...k, ...aktualisierter } : k)
          : prev);
      }
      setIsEditModalOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || 'Speichern fehlgeschlagen.') : 'Verbindung zum Server fehlgeschlagen.';
      alert(msg);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: CI.brombeer }} />
          <p className="font-bold">Lade sichere Daten aus der Cloud...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-xl shadow border border-red-100 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <X className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Daten konnten nicht geladen werden</h2>
          <p className="text-sm text-gray-600 mb-4">{loadError}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg font-bold text-white" style={{ backgroundColor: CI.brombeer }}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  // Top-Navigation (Logo + Tabs + User-Pille) liegt jetzt global in App.jsx,
  // deshalb keinen eigenen Header mehr — sonst doppelt.
  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col relative">

      <div className="flex flex-1 overflow-hidden mt-2">
        {/* Seitenleiste. Auf dem Telefon nimmt sie die volle Breite, solange
            keine Akte offen ist — und verschwindet, sobald eine offen ist.
            Sonst blieben bei 390 Pixeln keine 70 fuer die Akte uebrig, und
            ein Sprung aus der Aufgaben-App (?kunde=...) waere auf dem
            iPhone wertlos. Ab mittlerer Breite ist alles wie bisher. */}
        <div className={`${selectedClient ? 'hidden md:flex' : 'flex w-full'} md:w-80 bg-white border-r border-gray-200 flex-col shadow-sm`}>
          <div className="p-4 border-b border-gray-100" style={{ backgroundColor: CI.brombeer }}>
            <h2 className="text-white font-bold flex items-center gap-2 mb-3"><Users className="w-4 h-4" /> Beratungskunden</h2>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" placeholder="Suchen (Name, PLZ...)" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full py-2 pl-9 pr-3 rounded-md text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-white/50" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleNewClient} className="flex-1 flex items-center justify-center gap-1 bg-white/20 hover:bg-white/30 text-white text-xs font-bold py-2 rounded transition-colors"><UserPlus className="w-4 h-4" /> Neuer Kunde</button>
              <button onClick={handleOpenTerminModal} className="flex-1 flex items-center justify-center gap-1 bg-white/20 hover:bg-white/30 text-white text-xs font-bold py-2 rounded transition-colors"><CalendarPlus className="w-4 h-4" /> Neuer Termin</button>
            </div>
          </div>
          
          <div className="overflow-y-auto flex-1">
            {searchQuery.trim() ? (
              <SidebarSucheTreffer
                query={searchQuery.trim()}
                alleKunden={alleKunden}
                terminListeZukunft={terminListeZukunft}
                terminListeVergangen={terminListeVergangen}
                selectedClient={selectedClient}
                setSelectedClient={setSelectedClient}
              />
            ) : (
              <>
                <SidebarSchulungsAnfragen
                  anfragen={schulungsAnfragen}
                  zeigeAlle={schulungsAnfragenZeigeAlle}
                  setZeigeAlle={setSchulungsAnfragenZeigeAlle}
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                />
                <SidebarSektion
                  titel="Nächste Termine"
                  termine={terminListeZukunft}
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                  emptyText="Keine zukünftigen Termine in den nächsten 6 Monaten."
                  istZukunft
                />
                <SidebarNeueBuchungen
                  termine={neueBuchungen}
                  zeigeAlle={neueBuchungenZeigeAlle}
                  setZeigeAlle={setNeueBuchungenZeigeAlle}
                  sortModus={neueBuchungenSort}
                  setSortModus={setNeueBuchungenSort}
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                />
                <SidebarSektion
                  titel="Letzte Termine"
                  termine={terminListeVergangen}
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                  emptyText="Noch keine vergangenen Termine."
                  istZukunft={false}
                />
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 relative">
          {!selectedClient && (
            <div className="max-w-6xl mx-auto">
              <OffeneNachbereitung
                refreshKey={nachbereitungRefreshKey}
                onNachbereiten={(t) => setNachbereitungTermin(t)}
              />
              <TerminPipelineTabelle
                onKundeWaehlen={(t) => setSelectedClient(t)}
                refreshKey={kalenderRefreshKey}
              />
            </div>
          )}
          {selectedClient && (
            <div className="max-w-4xl mx-auto space-y-6">
              <button
                type="button"
                onClick={() => setSelectedClient(null)}
                className="text-sm text-brombeer hover:underline flex items-center gap-1"
                style={{ color: CI.brombeer }}
              >
                ← Zurück zur Termin-Pipeline
              </button>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex justify-between items-start relative overflow-hidden">
                <div className="space-y-3 relative z-10">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 group">
                    <User className="w-5 h-5" style={{ color: CI.brombeer }} />
                    {selectedClient.lastName}, {selectedClient.firstName}
                    <span className="text-sm font-normal text-gray-500">({calculateAge(selectedClient.dob)} Jahre)</span>
                    <button onClick={openEditModal} className="ml-2 p-1.5 text-gray-400 hover:text-brombeer hover:bg-fuchsia-50 rounded-md transition-all opacity-0 group-hover:opacity-100 focus:opacity-100" title="Stammdaten bearbeiten"><Edit3 className="w-5 h-5" /></button>
                    <button onClick={handleKundeKopieren} className="p-1.5 text-gray-300 hover:text-brombeer hover:bg-fuchsia-50 rounded-md transition-all" title="Kopie anlegen — neue Person im selben Haushalt (Adresse, Versicherung, Angehörige werden übernommen; danach nur den Namen ändern)"><Copy className="w-5 h-5" /></button>
                    <button onClick={handleKundeLoeschen} className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-md transition-all" title="Kunde löschen (in den Papierkorb, nach 30 Tagen endgültig)"><Trash2 className="w-5 h-5" /></button>
                  </h3>
                  {selectedClient.doiStatus === 'pending' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-50 border border-red-300 text-sm text-red-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 animate-pulse" />
                      <span>
                        <strong>⚠ DSGVO: Double-Opt-In noch nicht bestätigt.</strong>
                        {selectedClient.doiFrist && (
                          <> Frist bis <strong>{new Date(selectedClient.doiFrist).toLocaleDateString('de-DE')}</strong> — danach Auto-Löschung, falls keine Bestätigung kommt.</>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2 flex-wrap">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{selectedClient.street}, {selectedClient.zip} {selectedClient.city}</span>
                      <GeoKoordinatenControl
                        kundeId={getKundeIdFromSelection()}
                        lat={selectedClient.lat}
                        lng={selectedClient.lng}
                        geoQuelle={selectedClient.geoQuelle}
                        onKarte={() => setShowUmkreisKarte(true)}
                        onUpdate={handleGeoUpdate}
                      />
                    </div>
                    <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" />{selectedClient.insuranceName} (PG {selectedClient.careLevel})</div>
                    <div className="flex items-center gap-2"><History className="w-4 h-4 text-gray-400" />Letzter Termin: {selectedClient.lastAppointment ? `${new Date(selectedClient.lastAppointment).toLocaleDateString('de-DE')} (${selectedClient.lastAppointmentType === 'video' ? 'per Video' : 'vor Ort'})` : 'Erster Termin'}</div>
                    {selectedClient.relative && (
                      <div className="flex items-center gap-2 font-medium text-gray-700"><Users className="w-4 h-4 text-gray-400" />Kontakt: {selectedClient.relative.firstName} {selectedClient.relative.lastName} ({selectedClient.relative.relationship})</div>
                    )}
                  </div>
                  {pflegecockpitInfo?.is_pflegecockpit_user && (
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <Activity className="w-4 h-4" style={{ color: CI.brombeer }} />
                      <span className="text-sm text-gray-700">
                        Auch <strong>Pflegecockpit-Nutzer</strong>
                        {pflegecockpitInfo.earliest_registered_at && (
                          <> seit {new Date(pflegecockpitInfo.earliest_registered_at).toLocaleDateString('de-DE')}</>
                        )}
                        {pflegecockpitInfo.account_count > 1 && (
                          <span className="text-xs text-gray-400 ml-1">({pflegecockpitInfo.account_count} Accounts)</span>
                        )}
                      </span>
                    </div>
                  )}
                  {lianeBoxInfo?.is_liane_box_kunde && (
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <Package className="w-4 h-4" style={{ color: CI.green }} />
                      <span className="text-sm text-gray-700">
                        Auch <strong>Liane-Box-Kunde</strong>
                        {lianeBoxInfo.aufnahme_am && (
                          <> seit {new Date(lianeBoxInfo.aufnahme_am).toLocaleDateString('de-DE')}</>
                        )}
                      </span>
                      {lianeBoxInfo.status && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          lianeBoxInfo.status === 'aktiv' ? 'bg-green-100 text-green-800'
                          : lianeBoxInfo.status === 'pause' ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-200 text-gray-600'
                        }`}>
                          {lianeBoxInfo.status === 'aktiv' ? 'Box aktiv'
                            : lianeBoxInfo.status === 'pause' ? 'Box pausiert'
                            : 'Box inaktiv'}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="text-right flex flex-col items-end gap-3 relative z-10">
                  <div>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Gültigkeitsperiode</span>
                    <p className="font-bold text-gray-700">{selectedClient?.gueltigkeit_periode_anzeige || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <button onClick={handleNeuerTerminAusAkte} className="flex items-center gap-2 px-3 py-2 font-bold rounded-lg hover:-translate-y-0.5 transition-all border shadow-sm text-sm text-white" style={{ backgroundColor: CI.brombeer, borderColor: CI.brombeer }}><CalendarPlus className="w-4 h-4" /> Neuer Termin</button>
                    <button onClick={handleFolgeTermin} title="Termin im Kalender platzieren — nuetzlich, wenn du einen freien Slot zwischen anderen Terminen suchen willst." className="flex items-center gap-2 px-3 py-2 bg-white text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm text-sm"><CalendarIcon className="w-4 h-4" /> Im Kalender</button>
                    {selectedClient.type === 'video' && videoLinkGueltig(selectedClient.videoLink) && (
                      <a href={selectedClient.videoLink} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 font-bold rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 shadow-sm text-sm"><Video className="w-4 h-4" /> Videoraum</a>
                    )}
                    {selectedClient.type === 'video' && !videoLinkGueltig(selectedClient.videoLink) && (
                      <button onClick={() => handleVideoraumNeu(selectedClient.id)} title="Es wurde noch kein gueltiger Videoraum-Link erzeugt. Klick legt einen an." className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 font-bold rounded-lg hover:bg-red-100 transition-colors border border-red-200 shadow-sm text-sm"><Video className="w-4 h-4" /> Videoraum anlegen</button>
                    )}
                  </div>
                </div>
              </div>

              {/* --- Stammdaten-Aufklapper — alle Kontakt- und Personendaten
                   auf einen Blick, ohne ins Bearbeiten-Modal wechseln zu muessen. --- */}
              <StammdatenBlock
                kunde={selectedClient}
                onEdit={openEditModal}
                pflegekassen={pflegekassen}
                kundeId={getKundeIdFromSelection()}
                onSaved={(neu) => {
                  setSelectedClient(prev => ({ ...prev, ...neu, id: prev?.id, kundeId: prev?.kundeId || neu.id }));
                  setAlleKunden(prev => (prev || []).map(k => (k.id === neu.id ? { ...k, ...neu } : k)));
                }}
              />

              {/* --- Notizen-Aufklapper — termin-unabhaengige Notizen zum Kunden,
                   direkt editierbar. --- */}
              {(() => {
                const echteKundeId = getKundeIdFromSelection();
                return (
                  <NotizenBlock
                    key={echteKundeId || selectedClient.id}
                    kundeId={echteKundeId}
                    initialNotizen={selectedClient.notizen || ''}
                    onSaved={(neu) => {
                      setSelectedClient(prev => prev ? { ...prev, notizen: neu } : prev);
                      setAlleKunden(prev => (prev || []).map(k => k.id === echteKundeId ? { ...k, notizen: neu } : k));
                    }}
                  />
                );
              })()}

              {/* --- Stammdatenhistorie — nachvollziehbares Aenderungsprotokoll,
                   lazy beim Aufklappen geladen. --- */}
              {(() => {
                const echteKundeId = getKundeIdFromSelection();
                return <HistorieBlock key={`hist-${echteKundeId || selectedClient.id}`} kundeId={echteKundeId} />;
              })()}

              {/* --- Gespraechshistorie (Vermerke aus der Termin-Nachbereitung) --- */}
              {(() => {
                const echteKundeId = getKundeIdFromSelection();
                return <GespraechsHistorieBlock key={`verm-${echteKundeId || selectedClient.id}-${nachbereitungRefreshKey}`} kundeId={echteKundeId} />;
              })()}

              {/* --- To-dos zu diesem Kunden — direkt unter der Uebersicht,
                   tab-unabhaengig sichtbar. --- */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <KundeTodosBlock
                  todos={kundeTodos}
                  neuerText={neuerTodoText}
                  setNeuerText={setNeuerTodoText}
                  neuerFaellig={neuerTodoFaellig}
                  setNeuerFaellig={setNeuerTodoFaellig}
                  neuerPrio={neuerTodoPrio}
                  setNeuerPrio={setNeuerTodoPrio}
                  isSaving={isSavingTodo}
                  zeigeErledigte={zeigeErledigteTodos}
                  setZeigeErledigte={setZeigeErledigteTodos}
                  onAdd={handleAddTodo}
                  onToggle={handleToggleTodo}
                  onUpdate={handleUpdateTodo}
                  onDelete={handleDeleteTodo}
                />
              </div>

              <div className="flex border-b border-gray-200 mb-4 px-2">
                <button onClick={() => setActiveTab('termine')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'termine' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'termine' ? CI.brombeer : 'transparent' }}><CalendarIcon className="w-4 h-4" /> 1. Termine</button>
                <button onClick={() => setActiveTab('tour')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'tour' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'tour' ? CI.brombeer : 'transparent' }}><MapPin className="w-4 h-4" /> 2. Tour</button>
                <button onClick={() => setActiveTab('akte')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'akte' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'akte' ? CI.brombeer : 'transparent' }}><Activity className="w-4 h-4" /> 3. Vorbereitung</button>
                <button onClick={() => setActiveTab('protokoll')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'protokoll' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'protokoll' ? CI.brombeer : 'transparent' }}><ClipboardList className="w-4 h-4" /> 4. Beratungsprotokoll</button>
                <button onClick={() => setActiveTab('dokumente')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'dokumente' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'dokumente' ? CI.brombeer : 'transparent' }}><FileText className="w-4 h-4" /> 5. Dokumente</button>
                <button onClick={() => setActiveTab('mail')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'mail' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'mail' ? CI.brombeer : 'transparent' }}><Mail className="w-4 h-4" /> 6. Mail-Verlauf</button>
                <button onClick={() => setActiveTab('pflegegrad')} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'pflegegrad' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: activeTab === 'pflegegrad' ? CI.brombeer : 'transparent' }}><Wallet className="w-4 h-4" /> 7. Pflegegrad-Prüfer</button>
              </div>

              {activeTab === 'termine' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-fade-in">
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: CI.brombeer }}>
                    <CalendarIcon className="w-4 h-4" /> Termine dieses Kunden
                    {kundeTermine.length > 0 && <span className="text-xs font-normal text-gray-500">({kundeTermine.length})</span>}
                  </h3>
                  {kundeTermine.length === 0 ? (
                    <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                      Noch keine Termine für diesen Kunden hinterlegt. Über den Button „Neuer Termin" oben rechts können Sie einen anlegen — alternativ „Im Kalender", wenn Sie einen freien Slot zwischen anderen Terminen suchen wollen.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                      {kundeTermine.map(termin => {
                        const datumAnzeige = termin.date ? new Date(termin.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' }) : '—';
                        const uhrAnzeige = termin.time && termin.time !== 'Offen' ? termin.time : '—';
                        const istVergangen = termin.date && new Date(termin.date) < new Date(new Date().setHours(0,0,0,0));
                        return (
                          <li key={termin.id} className="flex flex-col gap-2 px-4 py-3 text-sm bg-white hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <span className={`font-bold ${istVergangen ? 'text-gray-500' : 'text-gray-800'}`}>{datumAnzeige}</span>
                                <span className="text-gray-600">{uhrAnzeige}</span>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded text-white" style={{ backgroundColor: termin.type === 'video' ? CI.blue : CI.green }}>
                                  {termin.type === 'video' ? 'Video' : 'Hausbesuch'}
                                </span>
                                {termin.status && termin.status !== 'geplant' && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">{termin.status}</span>
                                )}
                                {termin.type === 'video' && videoLinkGueltig(termin.videoLink) && (
                                  <a
                                    href={termin.videoLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded border border-blue-200"
                                    title="Videoraum in Sprechstunde.Online oeffnen"
                                  >
                                    <Video className="w-3.5 h-3.5" /> Videoraum
                                  </a>
                                )}
                                {termin.type === 'video' && !videoLinkGueltig(termin.videoLink) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleVideoraumNeu(termin.id); }}
                                    className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 bg-red-50 text-red-700 hover:bg-red-100 rounded border border-red-200"
                                    title="Kein gueltiger Videoraum — jetzt anlegen"
                                  >
                                    <Video className="w-3.5 h-3.5" /> Videoraum anlegen
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => setNachbereitungTermin({ id: termin.id, name: `${selectedClient.lastName || ''}, ${selectedClient.firstName || ''}`.replace(/^, |, $/g, ''), date: termin.date, time: termin.time, type: termin.type })} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-fuchsia-50 hover:bg-fuchsia-100 rounded-md border border-fuchsia-200" style={{ color: CI.brombeer }} title="Termin nachbereiten (Status, Gesprächsvermerk, Aufgaben)">
                                  <ClipboardList className="w-3.5 h-3.5" /> Nachbereiten
                                </button>
                                <button onClick={() => handleOpenEditTermin(termin)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-md border border-amber-200" title="Termin verschieben / bearbeiten">
                                  <Edit3 className="w-3.5 h-3.5" /> Verschieben
                                </button>
                              </div>
                            </div>
                            <div className="pl-1">
                              <TerminPipelinePunkte
                                pipeline={termin.pipeline}
                                onToggleStattgefunden={async () => {
                                  try {
                                    await api.post(`/api/termine/${termin.id}/stattgefunden`, {});
                                    const kundeId = getKundeIdFromSelection();
                                    if (kundeId) {
                                      const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
                                      setKundeTermine(Array.isArray(liste) ? liste : []);
                                    }
                                  } catch (e) {
                                    console.warn('Pipeline: stattgefunden-Toggle fehlgeschlagen', e);
                                    alert('Konnte nicht abhaken — bitte spaeter erneut probieren.');
                                  }
                                }}
                                onToggleBegleitMail={async () => {
                                  try {
                                    await api.post(`/api/termine/${termin.id}/begleit-mail-status`, {});
                                    const kundeId = getKundeIdFromSelection();
                                    if (kundeId) {
                                      const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
                                      setKundeTermine(Array.isArray(liste) ? liste : []);
                                    }
                                  } catch (e) {
                                    console.warn('Pipeline: begleit-mail-Toggle fehlgeschlagen', e);
                                    alert('Konnte nicht umschalten — bitte spaeter erneut probieren.');
                                  }
                                }}
                                onToggleAbrechnungErledigt={async () => {
                                  try {
                                    await api.post(`/api/termine/${termin.id}/abrechnung-erledigt`, {});
                                    const kundeId = getKundeIdFromSelection();
                                    if (kundeId) {
                                      const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
                                      setKundeTermine(Array.isArray(liste) ? liste : []);
                                    }
                                  } catch (e) {
                                    console.warn('Pipeline: abrechnung-Toggle fehlgeschlagen', e);
                                    alert('Konnte nicht umschalten — bitte spaeter erneut probieren.');
                                  }
                                }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {activeTab === 'tour' && (
                <div className="animate-fade-in">
                  {(() => {
                    const echteKundeId = getKundeIdFromSelection();
                    return (
                      <TourZuweisungBlock
                        key={`tour-${echteKundeId || selectedClient.id}`}
                        kundeId={echteKundeId}
                        clickupTourText={selectedClient.tour || ''}
                        street={selectedClient.street}
                        zip={selectedClient.zip}
                        city={selectedClient.city}
                        lat={selectedClient.lat}
                        lng={selectedClient.lng}
                      />
                    );
                  })()}
                </div>
              )}

              {activeTab === 'akte' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-fade-in">
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-6 flex items-center gap-2" style={{ color: CI.brombeer }}><Activity className="w-4 h-4" /> Vorbereitung</h3>

                  {/* --- Diagnosen-Block: Katalog-Suche + Freitext + Liste --- */}
                  <KundeDiagnosenBlock
                    diagnosen={kundeDiagnosen}
                    katalog={diagnoseKatalog}
                    suche={diagnoseSuche}
                    setSuche={setDiagnoseSuche}
                    segmentFilter={diagnoseSegmentFilter}
                    setSegmentFilter={setDiagnoseSegmentFilter}
                    freitext={neueDiagnoseFreitext}
                    setFreitext={setNeueDiagnoseFreitext}
                    isSaving={isSavingDiagnose}
                    onAdd={handleAddDiagnose}
                    onDelete={handleDeleteDiagnose}
                  />

                  {/* --- Begutachtungs-Vorschau (heuristische Pflegegrad-Hinweise) --- */}
                  {begutachtungVorschau && begutachtungVorschau.module && begutachtungVorschau.module.length > 0 && (
                    <BegutachtungVorschauBlock vorschau={begutachtungVorschau} />
                  )}

                  {/* --- Persoenliche Notizen aus Vorbereitung (falls vorhanden) --- */}
                  {selectedClient.kunden_notiz && (
                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Persönliche Notizen des Kunden</label>
                      <div className="mt-3 p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-sm text-gray-800 italic shadow-inner">"{selectedClient.kunden_notiz}"</div>
                    </div>
                  )}

                  <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end">
                    <button onClick={() => setActiveTab('protokoll')} className="px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all" style={{ backgroundColor: CI.blue }}>Weiter zur Protokoll-Erstellung →</button>
                  </div>
                </div>
              )}

              {activeTab === 'protokoll' && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-fade-in">
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-6 flex items-center gap-2" style={{ color: CI.brombeer }}><FileText className="w-4 h-4" /> 37.3 Protokoll-Erstellung</h3>

                  {/* --- Eingescannte / hochgeladene Protokolle (vor-Ort-Unterschriften, historische Faelle) --- */}
                  <ProtokollScansBlock
                    kundeId={getKundeIdFromSelection()}
                    onVersenden={(scanId) => handleOpenVersand(scanId, 'scan')}
                    refreshKey={scanRefreshKey}
                  />

                  <div className="space-y-4">
                    {/* --- Historie bestehender Protokolle --- */}
                    {(() => {
                      const anzahl = protokollHistorie.length;
                      const darfKollabieren = anzahl > 2;
                      const zeigeListe = !darfKollabieren || historieExpanded;
                      return (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-bold text-gray-700">Bisherige Protokolle für diesen Kunden{anzahl > 0 && <span className="ml-2 text-xs font-normal text-gray-500">({anzahl})</span>}</label>
                            {darfKollabieren && (
                              <button onClick={() => setHistorieExpanded(!historieExpanded)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-md border border-gray-200">
                                {zeigeListe ? 'Einklappen' : 'Aufklappen'} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${zeigeListe ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                          </div>
                          {anzahl === 0 ? (
                            <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">Noch keine Protokolle für diesen Kunden.</div>
                          ) : zeigeListe ? (
                            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                              {protokollHistorie.map(p => (
                                <li key={p.id} className={`flex items-center justify-between px-4 py-3 text-sm ${editingProtokollId === p.id ? 'bg-fuchsia-50' : 'bg-white hover:bg-gray-50'}`}>
                                  <div className="flex items-center gap-4">
                                    <span className="font-bold text-gray-800">{p.created_at ? new Date(p.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded text-white" style={{ backgroundColor: p.termin_art === 'video' ? CI.blue : CI.green }}>{p.termin_art === 'video' ? 'Video' : 'Hausbesuch'}</span>
                                    {p.termin_datum && <span className="text-gray-500 text-xs">Termin: {new Date(p.termin_datum).toLocaleDateString('de-DE')}</span>}
                                    {editingProtokollId === p.id && <span className="text-xs font-bold text-fuchsia-700">• wird bearbeitet</span>}
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {p.hat_pdf && p.download_url_intern && (
                                      <button onClick={() => window.open(mitCacheBuster(p.download_url_intern), '_blank', 'noopener')} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md border border-blue-200"><FileDown className="w-3.5 h-3.5" /> Öffnen</button>
                                    )}
                                    {p.hat_pdf && p.abrechnung_status !== 'versendet' && (
                                      <button onClick={() => handleOpenVersand(p.id)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100 rounded-md border border-fuchsia-200" title="Protokoll an Patient / Angehörigen senden"><MessageSquarePlus className="w-3.5 h-3.5" /> Versenden</button>
                                    )}
                                    {!p.hat_pdf && <span className="text-xs italic text-gray-400">nur gespeichert</span>}
                                    {/* Bearbeiten: gesperrt, sobald Protokoll fuer Abrechnung markiert oder versendet ist */}
                                    {p.abrechnung_status === 'offen' ? (
                                      <button onClick={() => handleEditProtokoll(p.id)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-md border border-amber-200" title="Protokoll bearbeiten"><Edit3 className="w-3.5 h-3.5" /> Bearbeiten</button>
                                    ) : (
                                      <span className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-gray-100 text-gray-400 rounded-md border border-gray-200 cursor-not-allowed" title="Bearbeiten erst moeglich, wenn das Protokoll aus der Abrechnung zurueckgezogen wurde."><Edit3 className="w-3.5 h-3.5" /> Bearbeiten</span>
                                    )}
                                    {/* Abrechnen-Button (nur wenn PDF da + Status offen) */}
                                    {p.hat_pdf && p.abrechnung_status === 'offen' && (
                                      <button onClick={() => handleZurAbrechnung(p.id)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded-md border border-emerald-200" title="Protokoll zur AS-Bremen-Sammelmail markieren">
                                        <Wallet className="w-3.5 h-3.5" /> Abrechnen
                                      </button>
                                    )}
                                    {p.abrechnung_status === 'wartend' && (
                                      <span className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-md border border-emerald-300" title={`Markiert am ${p.zur_abrechnung_markiert_am ? new Date(p.zur_abrechnung_markiert_am).toLocaleString('de-DE') : ''}`}>
                                        <Wallet className="w-3.5 h-3.5" /> in AS-Bremen-Warteschlange
                                      </span>
                                    )}
                                    {p.abrechnung_status === 'versendet' && (
                                      <span className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md border border-slate-300" title={`Versendet am ${p.abrechnung_versendet_am ? new Date(p.abrechnung_versendet_am).toLocaleString('de-DE') : ''}`}>
                                        <CheckCircle className="w-3.5 h-3.5" /> an AS Bremen versendet
                                      </span>
                                    )}
                                    {/* Loeschen: nur solange noch nicht versendet (zur Abrechnung markiert -> Hendrik kann zuerst zurueckziehen, dann loeschen). */}
                                    {p.abrechnung_status !== 'versendet' && (
                                      <button onClick={() => handleDeleteProtokoll(p.id)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded-md border border-red-200" title="Protokoll löschen"><X className="w-3.5 h-3.5" /></button>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">Historie eingeklappt — {anzahl} Protokolle vorhanden. Klick auf „Aufklappen", um sie zu sehen.</div>
                          )}
                        </div>
                      );
                    })()}

                    <div className="border-t border-gray-200 pt-4">
                      {editingProtokollId ? (
                        <div className="flex items-center justify-between px-4 py-2 bg-fuchsia-50 border border-fuchsia-200 rounded-md">
                          <div className="text-sm font-bold text-fuchsia-800">Bearbeitungs-Modus — Änderungen werden am ausgewählten Protokoll gespeichert.</div>
                          <button onClick={handleCancelEdit} className="text-xs font-bold px-3 py-1.5 bg-white text-gray-700 hover:bg-gray-50 rounded-md border border-gray-300">Abbrechen</button>
                        </div>
                      ) : (
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Neues Protokoll</div>
                      )}
                    </div>

                    {/* --- Termin-Auswahl fuer dieses Protokoll --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">Termin für dieses Protokoll</label>
                      {kundeTermine.length === 0 && !showAdHocTerminForm ? (
                        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                          Für diesen Kunden ist noch kein Termin hinterlegt. Bitte unten ein Datum für das Protokoll auswählen.
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <select
                            value={protokollTerminId || ''}
                            onChange={(e) => setProtokollTerminId(e.target.value || null)}
                            className="flex-1 p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                            style={{ '--tw-ring-color': CI.brombeer }}
                          >
                            {kundeTermine.length === 0 && <option value="">— kein Termin —</option>}
                            {(() => {
                              const protoTerminIds = new Set(protokollHistorie.map(p => p.termin_id).filter(Boolean));
                              return kundeTermine.map(t => {
                                const datumAnz = t.date ? new Date(t.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' }) : '—';
                                const uhrAnz = t.time && t.time !== 'Offen' ? ` ${t.time}` : '';
                                const artAnz = t.type === 'video' ? 'Video' : 'Hausbesuch';
                                const hatProto = protoTerminIds.has(t.id);
                                return (
                                  <option key={t.id} value={t.id}>
                                    {datumAnz}{uhrAnz} — {artAnz}{hatProto ? ' (bereits protokolliert)' : ''}
                                  </option>
                                );
                              });
                            })()}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowAdHocTerminForm(s => !s)}
                            className="text-xs font-bold px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg border border-blue-200 whitespace-nowrap"
                          >
                            {showAdHocTerminForm ? 'Eigenes Datum schließen' : 'Eigenes Datum wählen'}
                          </button>
                        </div>
                      )}
                      {showAdHocTerminForm && (
                        <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                          <div className="text-xs text-blue-900">
                            Legt einen rein protokollarischen Termin an — ohne E-Mail an den Kunden, ohne Google-Kalender-Eintrag, ohne Erinnerungs-Mails. Sinnvoll, wenn das Beratungsgespräch außerplanmäßig stattgefunden hat.
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs font-bold text-gray-700 block mb-1">Datum</label>
                              <input type="date" value={adHocDatum} onChange={(e) => setAdHocDatum(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-gray-700 block mb-1">Uhrzeit (optional)</label>
                              <input type="time" value={adHocUhrzeit} onChange={(e) => setAdHocUhrzeit(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-gray-700 block mb-1">Art</label>
                              <select value={adHocTerminArt} onChange={(e) => setAdHocTerminArt(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm">
                                <option value="hausbesuch">Hausbesuch</option>
                                <option value="video">Video</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={handleAdHocTerminAnlegen}
                              disabled={isCreatingAdHocTermin || !adHocDatum}
                              className="text-xs font-bold px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg disabled:opacity-50"
                            >
                              {isCreatingAdHocTermin ? 'Lege an…' : 'Termin anlegen und auswählen'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* --- Optionales "Erstellt am"-Datum der PDF-Datei --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">
                        „Erstellt am"-Datum der PDF-Datei <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <input
                          type="date"
                          value={pdfErstelltDatum}
                          onChange={(e) => setPdfErstelltDatum(e.target.value)}
                          className="p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': CI.brombeer }}
                        />
                        {pdfErstelltDatum && (
                          <button
                            type="button"
                            onClick={() => setPdfErstelltDatum('')}
                            className="text-xs font-bold px-3 py-2 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200 whitespace-nowrap"
                          >
                            Feld leeren
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Leer lassen = das Beratungsdatum des Termins wird als Erstell-Datum der Datei gesetzt. Nur ausfüllen, wenn die Datei ein abweichendes Datum tragen soll (z.B. ein paar Tage nach dem Besuch). Das ändert das in den Datei-Eigenschaften hinterlegte „Erstellt am" — die Rechner-Uhr muss dafür nicht mehr verstellt werden.
                      </p>
                    </div>

                    {/* --- Gültigkeitsperiode (Halbjahr) fürs PDF --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">
                        Gültigkeitsperiode (Halbjahr) im PDF
                      </label>
                      {(() => {
                        const gewaehlterTermin = kundeTermine.find(t => String(t.id) === String(protokollTerminId));
                        const datumBasis = gewaehlterTermin?.date || null;
                        const autoAnzeige = gewaehlterTermin?.gueltigkeit_periode_anzeige || '';
                        return (
                          <>
                            <select
                              value={protokollPeriode}
                              onChange={(e) => setProtokollPeriode(e.target.value)}
                              className="w-full sm:w-auto p-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                              style={{ '--tw-ring-color': CI.brombeer }}
                            >
                              <option value="">
                                Automatisch{autoAnzeige ? ` — ${autoAnzeige}` : ' — aus dem Termin-Datum'}
                              </option>
                              {generiereHalbjahrOptionen(datumBasis).map(opt => (
                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Steht im PDF oben rechts neben dem Pflegegrad. „Automatisch"{autoAnzeige ? ` ergibt aktuell „${autoAnzeige}"` : ''} — hier kannst du das Halbjahr fürs Protokoll noch ändern, auch nachträglich, bevor du das PDF erzeugst.
                            </p>
                          </>
                        );
                      })()}
                    </div>

                    {/* --- Terminart für dieses Protokoll --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">Beratung fand statt als</label>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="terminArt" value="hausbesuch" checked={terminArt === 'hausbesuch'} onChange={() => setTerminArt('hausbesuch')} />Hausbesuch (Kunde unterschreibt auf dem Ausdruck)</label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" name="terminArt" value="video" checked={terminArt === 'video'} onChange={() => setTerminArt('video')} />Videotermin (Beraterunterschrift wird eingefügt)</label>
                      </div>
                    </div>

                    {/* --- Sicht der Beteiligten (Kunde + Angehörige) — mit den 12 pp*-Vorlagen --- */}
                    <div className="relative">
                      <div className="flex justify-between items-end mb-2">
                        <label className="text-sm font-bold text-gray-700">Sicht der Beteiligten (Kunde / Angehörige)</label>
                        <div className="relative">
                            <button onClick={() => setShowSnippetMenuFor(showSnippetMenuFor === 'beteiligte' ? null : 'beteiligte')} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md transition-colors border border-blue-200"><MessageSquarePlus className="w-3.5 h-3.5" /> Vorlage einfügen <ChevronDown className="w-3 h-3" /></button>
                            {showSnippetMenuFor === 'beteiligte' && (
                              <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                                <div className="bg-gray-50 px-3 py-2 border-b text-xs font-bold text-gray-500">Bausteine: Kunde / Angehörige</div>
                                <div className="max-h-64 overflow-y-auto">
                                  {textSnippets.filter(s => s.category === 'sicht_beteiligte').map(snippet => (
                                    <button key={snippet.id} onClick={() => { setSichtBeteiligte(prev => prev + (prev.trim() === "" ? "" : "\n\n") + snippet.text); setShowSnippetMenuFor(null); }} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors group"><div className="font-bold text-sm text-gray-800 group-hover:text-blue-600 transition-colors">{snippet.title}</div><div className="text-xs text-gray-500 line-clamp-2 mt-1">{snippet.text}</div></button>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                      <SichtBausteinReihen
                        kunde={selectedClient}
                        currentText={sichtBeteiligte}
                        letzterPatientBlock={letzterPatientBlock}
                        letzterPflegepersonBlock={letzterPflegepersonBlock}
                        onSetzeText={(text, neuerPatBlock, neuerPflegeBlock) => {
                          setSichtBeteiligte(text);
                          if (neuerPatBlock !== undefined) setLetzterPatientBlock(neuerPatBlock);
                          if (neuerPflegeBlock !== undefined) setLetzterPflegepersonBlock(neuerPflegeBlock);
                        }}
                      />
                      <textarea value={sichtBeteiligte} onChange={(e) => setSichtBeteiligte(e.target.value)} placeholder="Wie beschreiben Kunde und Angehörige die Pflegesituation?" className="w-full h-40 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y" style={{ '--tw-ring-color': CI.brombeer }} />
                    </div>

                    {/* --- Sicht des Beraters (Gesprächsinhalte) — mit Berater-Standard-Vorlage --- */}
                    <div className="relative">
                      <div className="flex justify-between items-end mb-2">
                        <label className="text-sm font-bold text-gray-700">Sicht des Beraters (Gesprächsinhalte)</label>
                        <div className="relative">
                            <button onClick={() => setShowSnippetMenuFor(showSnippetMenuFor === 'berater' ? null : 'berater')} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md transition-colors border border-blue-200"><MessageSquarePlus className="w-3.5 h-3.5" /> Vorlage einfügen <ChevronDown className="w-3 h-3" /></button>
                            {showSnippetMenuFor === 'berater' && (
                              <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                                <div className="bg-gray-50 px-3 py-2 border-b text-xs font-bold text-gray-500">Bausteine: Berater-Sicht</div>
                                <div className="max-h-64 overflow-y-auto">
                                  {textSnippets.filter(s => s.category === 'sicht_berater' || s.category === 'sonstiges').map(snippet => (
                                    <button key={snippet.id} onClick={() => { setProtocolText(snippet.text); setShowSnippetMenuFor(null); }} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors group"><div className="font-bold text-sm text-gray-800 group-hover:text-blue-600 transition-colors">{snippet.title}</div><div className="text-xs text-gray-500 line-clamp-2 mt-1">{snippet.text}</div></button>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                      <textarea value={protocolText} onChange={(e) => setProtocolText(e.target.value)} placeholder="Ihre Einschätzung als Berater zur Pflegesituation..." className="w-full h-40 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y" style={{ '--tw-ring-color': CI.brombeer }} />
                    </div>

                    {/* --- Pflege sichergestellt --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">Pflegesituation</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-sm"><input type="radio" name="sichergestellt" checked={sichergestellt === true} onChange={() => setSichergestellt(true)} />Sichergestellt</label>
                        <label className="flex items-center gap-2 text-sm"><input type="radio" name="sichergestellt" checked={sichergestellt === false} onChange={() => setSichergestellt(false)} />Nicht sichergestellt</label>
                      </div>
                      {!sichergestellt && (
                        <textarea value={begruendungNichtSichergestellt} onChange={(e) => setBegruendungNichtSichergestellt(e.target.value)} placeholder="Begründung, warum die Pflege nicht sichergestellt ist..." className="mt-3 w-full h-24 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y" style={{ '--tw-ring-color': CI.brombeer }} />
                      )}
                    </div>

                    {/* --- Empfohlene Maßnahmen --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">Empfohlene Maßnahmen</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {MASSNAHMEN_OPTIONEN.map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-2 text-sm px-3 py-2 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={massnahmen.includes(key)}
                              onChange={(e) => {
                                if (e.target.checked) setMassnahmen(prev => [...prev, key]);
                                else setMassnahmen(prev => prev.filter(k => k !== key));
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                      <textarea value={massnahmenBemerkung} onChange={(e) => setMassnahmenBemerkung(e.target.value)} placeholder="Bemerkungen zu den Maßnahmen (optional)..." className="mt-3 w-full h-20 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:border-transparent resize-y" style={{ '--tw-ring-color': CI.brombeer }} />
                    </div>

                    {/* --- § 7a Pflegeberatung --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">§ 7a Pflegeberatung</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={beratung7aAngezeigt} onChange={(e) => setBeratung7aAngezeigt(e.target.checked)} />Beratung nach § 7a wurde angezeigt</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={beratung7aGewuenscht} onChange={(e) => setBeratung7aGewuenscht(e.target.checked)} />Beratung nach § 7a wird gewünscht</label>
                      </div>
                    </div>

                    {/* --- Einwilligungen --- */}
                    <div>
                      <label className="text-sm font-bold text-gray-700 block mb-2">Einwilligungen des Kunden</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={einwilligungDatenschutz} onChange={(e) => setEinwilligungDatenschutz(e.target.checked)} />Datenschutz-Einwilligung</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={einwilligungDatenuebermittlung} onChange={(e) => setEinwilligungDatenuebermittlung(e.target.checked)} />Einwilligung zur Datenübermittlung an die Pflegekasse</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={einwilligung7a} onChange={(e) => setEinwilligung7a(e.target.checked)} />Einwilligung zur § 7a-Datenübermittlung</label>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end gap-3">
                    <button onClick={handleOnlySave} disabled={isSavingProtokoll || !selectedClient} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">{isSavingProtokoll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{isSavingProtokoll ? 'Speichert…' : (editingProtokollId ? 'Änderungen speichern' : 'Nur speichern')}</button>
                    <button onClick={handleCreatePdf} disabled={isCreatingPdf || !selectedClient} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0" style={{ backgroundColor: CI.brombeer }}>{isCreatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}{isCreatingPdf ? 'Erzeuge PDF…' : (editingProtokollId ? 'PDF neu erstellen' : 'PDF Erstellen')}</button>
                  </div>
                </div>
              )}

              {activeTab === 'dokumente' && selectedClient && (
                <DokumenteTab kundeId={selectedClient.kundeId || selectedClient.id?.replace(/^kunde-/, '')} />
              )}

              {activeTab === 'mail' && selectedClient && (
                <KundeMailTab
                  kundeId={selectedClient.kundeId || selectedClient.id?.replace(/^kunde-/, '')}
                  kunde={selectedClient}
                />
              )}

              {activeTab === 'pflegegrad' && selectedClient && (
                <PflegegradPruefer
                  kundeId={selectedClient.kundeId || selectedClient.id?.replace(/^kunde-/, '')}
                  kunde={selectedClient}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {isEditModalOpen && editFormData && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 pb-0 border-b border-gray-100 bg-slate-50 flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">{editFormData.isNew ? <UserPlus className="w-5 h-5 text-brombeer" style={{ color: CI.brombeer }} /> : <Edit3 className="w-5 h-5 text-brombeer" style={{ color: CI.brombeer }} />} {editFormData.isNew ? 'Neuen Patienten anlegen' : 'Stammdaten bearbeiten'}</h2>
                <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg transition-colors"><X className="w-5 h-5"/></button>
              </div>
              <div className="flex gap-6">
                <button onClick={() => setModalTab('patient')} className={`pb-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${modalTab === 'patient' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: modalTab === 'patient' ? CI.brombeer : 'transparent' }}><User className="w-4 h-4" /> Pflegebedürftiger</button>
                <button onClick={() => setModalTab('relative')} className={`pb-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${modalTab === 'relative' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: modalTab === 'relative' ? CI.brombeer : 'transparent' }}><Users className="w-4 h-4" /> Kontaktperson / Angehöriger</button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-white">
              {modalTab === 'patient' && (
                <div className="space-y-6 animate-fade-in">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Persönliche Daten</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Anrede (für die Mail-Begrüßung)</label>
                        <select name="salutation" value={editFormData.salutation || ''} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                          <option value="">— bitte wählen —</option>
                          <option value="Frau">Frau</option>
                          <option value="Herr">Herr</option>
                          <option value="Divers">Divers / keine Angabe</option>
                        </select>
                      </div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Vorname</label><input type="text" name="firstName" value={editFormData.firstName} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label><input type="text" name="lastName" value={editFormData.lastName} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-2 sm:col-span-1"><label className="block text-sm font-medium text-gray-700 mb-1">Geburtsdatum</label><input type="date" name="dob" value={editFormData.dob} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Kontaktdaten</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Telefon / Mobil</label><input type="text" name="phone" value={editFormData.phone || ''} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">E-Mail Adresse</label><input type="email" name="email" value={editFormData.email || ''} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Adresse & Erreichbarkeit</h4>
                    <div className="grid grid-cols-6 gap-4">
                      <div className="col-span-6"><label className="block text-sm font-medium text-gray-700 mb-1">Straße & Hausnummer</label><input type="text" name="street" value={editFormData.street} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label><input type="text" name="zip" value={editFormData.zip} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-4"><label className="block text-sm font-medium text-gray-700 mb-1">Ort</label><input type="text" name="city" value={editFormData.city} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Versicherung</h4>
                    <div className="grid grid-cols-6 gap-4">
                      <div className="col-span-6 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Versicherungsart</label>
                        <select name="insuranceType" value={editFormData.insuranceType || ''} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                          <option value="">— bitte wählen —</option>
                          <option value="GKV">Gesetzlich (GKV)</option>
                          <option value="PKV">Privat (PKV)</option>
                        </select>
                      </div>
                      <div className="col-span-6 md:col-span-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pflegekasse</label>
                        <select name="insuranceId" value={editFormData.insuranceId || ''} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                          <option value="">— bitte auswählen —</option>
                          {pflegekassen.map(pk => (
                            <option key={pk.id} value={pk.id}>{pk.name}{pk.plz ? ` (${pk.plz} ${pk.ort || ''})` : ''}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Aktueller Pflegegrad</label>
                        <select name="careLevel" value={editFormData.careLevel} onChange={handleEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                          <option value="0">Kein Pflegegrad</option><option value="1">Pflegegrad 1</option><option value="2">Pflegegrad 2</option><option value="3">Pflegegrad 3</option><option value="4">Pflegegrad 4</option><option value="5">Pflegegrad 5</option>
                        </select>
                      </div>
                      <div className="col-span-6 md:col-span-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Versichertennummer</label>
                        <input type="text" name="insuranceNumber" value={editFormData.insuranceNumber || ''} onChange={handleEditChange} placeholder="z.B. A123456789" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Organisation</h4>
                    <div className="grid grid-cols-6 gap-4">
                      <div className="col-span-6 md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tour / Route</label>
                        <input type="text" name="tour" value={editFormData.tour || ''} onChange={handleEditChange} placeholder="wird ueber Tourplan zugewiesen" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-gray-50" disabled />
                        <p className="mt-1 text-xs text-gray-500">Diese Zuweisung erfolgt spaeter ueber die Tourenplanung (Einstellungen &rarr; Tourplan).</p>
                      </div>
                      <div className="col-span-6 md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Partner / Berater-Zuordnung</label>
                        <input type="text" name="partnerName" value={editFormData.partnerName || ''} onChange={handleEditChange} placeholder="z.B. SPA Pflegeberatung" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                        <p className="mt-1 text-xs text-gray-500">Wird spaeter ueber die Partner-Liste im Super-Admin als Dropdown gepflegt.</p>
                      </div>
                      <div className="col-span-6">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={!!editFormData.dauerhaftVideo} onChange={(e) => setEditFormData(prev => ({ ...prev, dauerhaftVideo: e.target.checked }))} />
                          <span className="font-medium text-gray-700">Dauerhaft per Video beraten</span>
                          <span className="text-xs text-gray-500">— Folge-Termine starten dann standardmaessig als Video.</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {modalTab === 'relative' && (
                <div className="animate-fade-in h-full">
                  {!editFormData.relative ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4"><Users className="w-8 h-8 text-gray-400" /></div>
                      <h3 className="text-lg font-bold text-gray-800 mb-2">Kein Angehöriger hinterlegt</h3>
                      <p className="text-sm text-gray-500 max-w-md mb-6">Für diesen Patienten ist aktuell keine externe Kontaktperson gespeichert. Möchten Sie einen Angehörigen anlegen?</p>
                      <button onClick={handleAddRelative} className="px-5 py-2.5 bg-blue-50 text-blue-700 font-bold rounded-lg hover:bg-blue-100 transition-colors">+ Angehörigen hinzufügen</button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Persönliche Daten</h4>
                          <button onClick={handleRemoveRelative} className="text-xs text-red-500 hover:text-red-700 font-bold transition-colors">Entfernen</button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Anrede (für die Mail-Begrüßung)</label>
                            <select name="salutation" value={editFormData.relative.salutation || ''} onChange={handleRelativeEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                              <option value="">— bitte wählen —</option>
                              <option value="Frau">Frau</option>
                              <option value="Herr">Herr</option>
                              <option value="Divers">Divers / keine Angabe</option>
                            </select>
                          </div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">Vorname</label><input type="text" name="firstName" value={editFormData.relative.firstName} onChange={handleRelativeEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label><input type="text" name="lastName" value={editFormData.relative.lastName} onChange={handleRelativeEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Verwandtschaftsverhältnis / Beziehung</label>
                            <select name="relationship" value={editFormData.relative.relationship} onChange={handleRelativeEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                              <option value="">Bitte wählen…</option>
                              {VERWANDTSCHAFT_GRUPPEN.map(g => (
                                <optgroup key={g.label} label={g.label}>
                                  {g.optionen.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Kontaktdaten</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">Telefon / Mobil</label><input type="text" name="phone" value={editFormData.relative.phone || ''} onChange={handleRelativeEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">E-Mail Adresse</label><input type="email" name="email" value={editFormData.relative.email || ''} onChange={handleRelativeEditChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-100 bg-slate-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setIsEditModalOpen(false)} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm">Abbrechen</button>
              <button onClick={saveClientData} disabled={isSaving} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0" style={{ backgroundColor: CI.blue }}>{isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{isSaving ? 'Speichert…' : (editFormData.isNew ? 'Kunde speichern' : 'Änderungen speichern')}</button>
            </div>
          </div>
        </div>
      )}

      {versandFor && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><MessageSquarePlus className="w-5 h-5" style={{ color: CI.brombeer }} />Protokoll an Patient / Angehörigen senden</h2>
              <button onClick={handleCancelVersand} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                Zur Info-Zustellung an den <b>Patienten</b> und/oder einen <b>Angehörigen</b>.
                Die <b>verschlüsselte PDF-Version</b> wird als Anhang verschickt —
                das Passwort-Schema (Geburtsdatum mit Punkten, z.B. 15.06.1945) steht im Mail-Text.
                Der Versand an die Pflegekasse erfolgt später über einen separaten Weg.
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Empfänger</label>
                <div className="space-y-2">
                  {selectedClient?.email ? (
                    <label className="flex items-center gap-3 px-3 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={versandAnPatient} onChange={(e) => setVersandAnPatient(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-800">Patient: {selectedClient.firstName} {selectedClient.lastName}</div>
                        <div className="text-xs text-gray-500">{selectedClient.email}</div>
                      </div>
                    </label>
                  ) : (
                    <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-400 italic">Patient hat keine E-Mail-Adresse hinterlegt.</div>
                  )}
                  {selectedClient?.relative?.email ? (
                    <label className="flex items-center gap-3 px-3 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={versandAnAngehoeriger} onChange={(e) => setVersandAnAngehoeriger(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-800">Angehöriger: {selectedClient.relative.firstName} {selectedClient.relative.lastName}{selectedClient.relative.relationship ? ` (${selectedClient.relative.relationship})` : ''}</div>
                        <div className="text-xs text-gray-500">{selectedClient.relative.email}</div>
                      </div>
                    </label>
                  ) : (
                    <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-400 italic">Kein Angehöriger mit E-Mail-Adresse vorhanden.</div>
                  )}
                </div>
                {!selectedClient?.email && !selectedClient?.relative?.email && (
                  <div className="mt-2 text-xs text-red-600">Weder Patient noch Angehöriger haben eine E-Mail-Adresse. Bitte zuerst Stammdaten ergänzen.</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zusatztext (optional)</label>
                <textarea value={versandZusatztext} onChange={(e) => setVersandZusatztext(e.target.value)} placeholder="Ergänzende Nachricht an den Empfänger..." className="w-full h-24 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-y" />
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 bg-slate-50 flex justify-end gap-3">
              <button onClick={handleCancelVersand} disabled={isSending} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60">Abbrechen</button>
              <button onClick={handleConfirmVersand} disabled={isSending || (!versandAnPatient && !versandAnAngehoeriger)} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0" style={{ backgroundColor: CI.brombeer }}>{isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4" />}{isSending ? 'Sendet…' : 'Jetzt senden'}</button>
            </div>
          </div>
        </div>
      )}

      {isTerminModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <CalendarPlus className="w-5 h-5" style={{ color: CI.brombeer }} /> Neuen Termin anlegen
              </h2>
              <button onClick={() => setIsTerminModalOpen(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg"><X className="w-5 h-5"/></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {/* Schritt 1: Kunde auswaehlen — kompakt, wenn aus Folge-Termin
                  (Kunde steht fest); ansonsten die komplette Suchliste. */}
              {terminModalKundeGesperrt ? (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">1. Kunde</label>
                  <div className="flex items-center justify-between px-4 py-3 bg-fuchsia-50 border border-fuchsia-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" style={{ color: CI.brombeer }} />
                      <span className="text-sm font-bold text-gray-800">
                        {(() => {
                          const k = alleKunden.find(x => x.id === terminForm.kunde_id);
                          return k ? `${k.lastName || '—'}, ${k.firstName || '—'}` : '—';
                        })()}
                      </span>
                    </div>
                    <button
                      onClick={() => setTerminModalKundeGesperrt(false)}
                      className="text-xs font-bold text-fuchsia-800 hover:text-fuchsia-900 underline"
                    >
                      anderen Kunden wählen
                    </button>
                  </div>
                </div>
              ) : (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">1. Kunde <span className="text-red-500">*</span></label>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Nach Name suchen..."
                    value={kundeSearch}
                    onChange={(e) => setKundeSearch(e.target.value)}
                    className="w-full py-2 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {alleKunden
                    .filter(k => {
                      const q = kundeSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (k.firstName || '').toLowerCase().includes(q) ||
                        (k.lastName || '').toLowerCase().includes(q)
                      );
                    })
                    .slice(0, 50)
                    .map(k => (
                      <button
                        key={k.id}
                        onClick={() => handleTerminChange('kunde_id', k.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${terminForm.kunde_id === k.id ? 'bg-fuchsia-50 font-bold' : 'hover:bg-gray-50'}`}
                      >
                        {k.lastName || '—'}, {k.firstName || '—'}
                        <span className="text-xs text-gray-500 ml-2">{k.dob || ''}</span>
                      </button>
                    ))}
                  {alleKunden.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-400 italic text-center">
                      Noch keine Kunden vorhanden. Erst über „+ Kunde" anlegen.
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Schritt 2: Termin-Eckdaten */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">2. Termin-Details</label>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">Datum & Uhrzeit <span className="text-red-500">*</span></label>
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={terminForm.ausserhalb_standard}
                        onChange={(e) => handleTerminChange('ausserhalb_standard', e.target.checked)}
                      />
                      <span>Außerhalb meiner Standard-Verfügbarkeit planen</span>
                    </label>
                  </div>
                  {terminForm.ausserhalb_standard && (
                    <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                      <strong>Override aktiv:</strong> Wochenplan und Abwesenheiten werden ignoriert. Du kannst jeden Tag und jede Uhrzeit zwischen 07:00 und 21:00 wählen.
                    </div>
                  )}
                  <VerfuegbarkeitsPicker
                    datum={terminForm.datum}
                    uhrzeit={terminForm.uhrzeit}
                    onChange={(d, u) => setTerminForm(prev => ({ ...prev, datum: d, uhrzeit: u }))}
                    ueberregelung={terminForm.ausserhalb_standard}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Dauer (Minuten)</label>
                    <input type="number" min="15" step="15" value={terminForm.dauer_minuten} onChange={(e) => handleTerminChange('dauer_minuten', parseInt(e.target.value, 10) || 30)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Termin-Art</label>
                    <div className="flex gap-4 pt-2">
                      <label className="flex items-center gap-1 text-sm cursor-pointer"><input type="radio" name="terminArt" checked={terminForm.termin_art === 'hausbesuch'} onChange={() => handleTerminChange('termin_art', 'hausbesuch')} /> Vor Ort</label>
                      <label className="flex items-center gap-1 text-sm cursor-pointer"><input type="radio" name="terminArt" checked={terminForm.termin_art === 'video'} onChange={() => handleTerminChange('termin_art', 'video')} /> Video</label>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Gültigkeitsperiode (fürs Protokoll)</label>
                    <select value={terminForm.gueltigkeit_periode} onChange={(e) => handleTerminChange('gueltigkeit_periode', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                      <option value="">Automatisch — aus dem Termin-Datum</option>
                      {generiereHalbjahrOptionen(terminForm.datum).map(opt => (
                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Nur ändern, wenn der Termin rückwirkend für ein zurückliegendes Halbjahr gewertet werden soll.</p>
                  </div>
                </div>
              </div>

              {/* Schritt 3: Optionen */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">3. Notiz (optional)</label>
                <textarea
                  value={terminForm.notiz}
                  onChange={(e) => handleTerminChange('notiz', e.target.value)}
                  placeholder="Freier Text für den Kalendereintrag..."
                  className="w-full h-20 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-y"
                />
                <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={terminForm.mit_google_kalender}
                    onChange={(e) => handleTerminChange('mit_google_kalender', e.target.checked)}
                    className="w-4 h-4"
                  />
                  Termin auch im Google-Kalender eintragen
                </label>
                {(() => {
                  const k = alleKunden.find(x => x.id === terminForm.kunde_id);
                  const kundeMail = k?.email || '';
                  const angMail = k?.relative?.email || '';
                  const istVideo = terminForm.termin_art === 'video';
                  const masterDisabled = !kundeMail && !angMail;
                  return (
                    <div className="mt-3 space-y-2">
                      <label className={`flex items-center gap-2 text-sm ${masterDisabled ? 'opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={terminForm.mit_info_mail && !masterDisabled}
                          disabled={masterDisabled}
                          onChange={(e) => handleTerminChange('mit_info_mail', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span>
                          Per E-Mail informieren
                          {istVideo && !masterDisabled && <span className="text-xs text-gray-500 ml-1">(Mail enthält den Sprechstunde.Online-Link)</span>}
                          {masterDisabled && <span className="text-xs text-gray-500 ml-1">(weder Kunde noch Angehöriger hat eine E-Mail-Adresse)</span>}
                        </span>
                      </label>
                      {terminForm.mit_info_mail && !masterDisabled && (
                        <div className="ml-6 space-y-1.5 pl-3 border-l-2 border-fuchsia-100">
                          <label className={`flex items-center gap-2 text-sm ${!kundeMail ? 'opacity-50' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={terminForm.mail_an_kunde && !!kundeMail}
                              disabled={!kundeMail}
                              onChange={(e) => handleTerminChange('mail_an_kunde', e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span>
                              An Patient/Patientin
                              {kundeMail
                                ? <span className="text-xs text-gray-500 ml-1">→ {kundeMail}</span>
                                : <span className="text-xs text-gray-500 ml-1">(keine E-Mail-Adresse)</span>}
                            </span>
                          </label>
                          <label className={`flex items-center gap-2 text-sm ${!angMail ? 'opacity-50' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={terminForm.mail_an_angehoeriger && !!angMail}
                              disabled={!angMail}
                              onChange={(e) => handleTerminChange('mail_an_angehoeriger', e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span>
                              An Angehörige/n
                              {angMail
                                ? <span className="text-xs text-gray-500 ml-1">→ {angMail}</span>
                                : <span className="text-xs text-gray-500 ml-1">(keine E-Mail-Adresse hinterlegt)</span>}
                            </span>
                          </label>
                        </div>
                      )}

                      {/* Erinnerungs-Schalter (unabhaengig vom Bestaetigungs-Mail-Block) */}
                      <div className="pt-2 mt-2 border-t border-gray-100">
                        <div className="text-xs font-semibold text-gray-600 mb-1.5">Automatische Erinnerungen</div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={terminForm.erinnerung_24h_aktiv}
                            onChange={(e) => handleTerminChange('erinnerung_24h_aktiv', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span>24 Stunden vor dem Termin</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={terminForm.erinnerung_morgen_aktiv}
                            onChange={(e) => handleTerminChange('erinnerung_morgen_aktiv', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span>Am Termin-Tag um 07:00 Uhr</span>
                        </label>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 bg-slate-50 flex justify-end gap-3">
              <button onClick={() => setIsTerminModalOpen(false)} disabled={isSavingTermin} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60">Abbrechen</button>
              <button onClick={handleSaveTermin} disabled={isSavingTermin || !terminForm.kunde_id} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0" style={{ backgroundColor: CI.brombeer }}>
                {isSavingTermin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSavingTermin ? 'Speichert…' : 'Termin anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editTerminId && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Edit3 className="w-5 h-5" style={{ color: CI.brombeer }} /> Termin verschieben / bearbeiten
              </h2>
              <button onClick={handleCancelEditTermin} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg"><X className="w-5 h-5"/></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                Kunde: <b>{selectedClient?.lastName}, {selectedClient?.firstName}</b>
                <div className="text-xs text-gray-500 mt-1">
                  Änderungen an Datum oder Uhrzeit lösen — je nach Häkchen unten — eine Verschiebungs-Mail an den Kunden und ein Update im Google-Kalender aus.
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Termin-Details</label>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">Datum & Uhrzeit <span className="text-red-500">*</span></label>
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editTerminForm.ausserhalb_standard}
                        onChange={(e) => handleEditTerminChange('ausserhalb_standard', e.target.checked)}
                      />
                      <span>Außerhalb meiner Standard-Verfügbarkeit planen</span>
                    </label>
                  </div>
                  {editTerminForm.ausserhalb_standard && (
                    <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                      <strong>Override aktiv:</strong> Wochenplan und Abwesenheiten werden ignoriert. Du kannst jeden Tag und jede Uhrzeit zwischen 07:00 und 21:00 wählen.
                    </div>
                  )}
                  <VerfuegbarkeitsPicker
                    datum={editTerminForm.datum}
                    uhrzeit={editTerminForm.uhrzeit}
                    onChange={(d, u) => setEditTerminForm(prev => ({ ...prev, datum: d, uhrzeit: u }))}
                    exkludiereTerminId={editTerminId}
                    ueberregelung={editTerminForm.ausserhalb_standard}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Dauer (Minuten)</label>
                    <input type="number" min="15" step="15" value={editTerminForm.dauer_minuten} onChange={(e) => handleEditTerminChange('dauer_minuten', parseInt(e.target.value, 10) || 45)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Termin-Art</label>
                    <div className="flex gap-4 pt-2">
                      <label className="flex items-center gap-1 text-sm cursor-pointer"><input type="radio" name="editTerminArt" checked={editTerminForm.termin_art === 'hausbesuch'} onChange={() => handleEditTerminChange('termin_art', 'hausbesuch')} /> Vor Ort</label>
                      <label className="flex items-center gap-1 text-sm cursor-pointer"><input type="radio" name="editTerminArt" checked={editTerminForm.termin_art === 'video'} onChange={() => handleEditTerminChange('termin_art', 'video')} /> Video</label>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Gültigkeitsperiode (fürs Protokoll)</label>
                    <select value={editTerminForm.gueltigkeit_periode} onChange={(e) => handleEditTerminChange('gueltigkeit_periode', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white">
                      <option value="">Automatisch — aus dem Termin-Datum</option>
                      {generiereHalbjahrOptionen(editTerminForm.datum).map(opt => (
                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Nur setzen, wenn der Termin rückwirkend für ein zurückliegendes Halbjahr gewertet werden soll.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Notiz für Mail / Kalender (optional)</label>
                <textarea
                  value={editTerminForm.notiz}
                  onChange={(e) => handleEditTerminChange('notiz', e.target.value)}
                  placeholder="z.B. 'Termin wurde auf Wunsch des Kunden verschoben' — erscheint in der Verschiebungs-Mail und im Kalendereintrag."
                  className="w-full h-20 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-y"
                />
                <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editTerminForm.mit_google_kalender}
                    onChange={(e) => handleEditTerminChange('mit_google_kalender', e.target.checked)}
                    className="w-4 h-4"
                  />
                  Google-Kalender-Eintrag aktualisieren
                </label>
                {(() => {
                  const kundeMail = selectedClient?.email || '';
                  const angMail = selectedClient?.relative?.email || '';
                  const istVideo = editTerminForm.termin_art === 'video';
                  const masterDisabled = !kundeMail && !angMail;
                  return (
                    <div className="mt-3 space-y-2">
                      <label className={`flex items-center gap-2 text-sm ${masterDisabled ? 'opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={editTerminForm.mit_info_mail && !masterDisabled}
                          disabled={masterDisabled}
                          onChange={(e) => handleEditTerminChange('mit_info_mail', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span>
                          Per E-Mail über den neuen Termin informieren
                          {istVideo && !masterDisabled && <span className="text-xs text-gray-500 ml-1">(Mail enthält den Sprechstunde.Online-Link)</span>}
                          {masterDisabled && <span className="text-xs text-gray-500 ml-1">(weder Kunde noch Angehöriger hat eine E-Mail-Adresse)</span>}
                        </span>
                      </label>
                      {editTerminForm.mit_info_mail && !masterDisabled && (
                        <div className="ml-6 space-y-1.5 pl-3 border-l-2 border-fuchsia-100">
                          <label className={`flex items-center gap-2 text-sm ${!kundeMail ? 'opacity-50' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={editTerminForm.mail_an_kunde && !!kundeMail}
                              disabled={!kundeMail}
                              onChange={(e) => handleEditTerminChange('mail_an_kunde', e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span>
                              An Patient/Patientin
                              {kundeMail
                                ? <span className="text-xs text-gray-500 ml-1">→ {kundeMail}</span>
                                : <span className="text-xs text-gray-500 ml-1">(keine E-Mail-Adresse)</span>}
                            </span>
                          </label>
                          <label className={`flex items-center gap-2 text-sm ${!angMail ? 'opacity-50' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={editTerminForm.mail_an_angehoeriger && !!angMail}
                              disabled={!angMail}
                              onChange={(e) => handleEditTerminChange('mail_an_angehoeriger', e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span>
                              An Angehörige/n
                              {angMail
                                ? <span className="text-xs text-gray-500 ml-1">→ {angMail}</span>
                                : <span className="text-xs text-gray-500 ml-1">(keine E-Mail-Adresse hinterlegt)</span>}
                            </span>
                          </label>
                        </div>
                      )}

                      {/* Erinnerungs-Schalter im Edit-Modal */}
                      <div className="pt-2 mt-2 border-t border-gray-100">
                        <div className="text-xs font-semibold text-gray-600 mb-1.5">Automatische Erinnerungen</div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editTerminForm.erinnerung_24h_aktiv}
                            onChange={(e) => handleEditTerminChange('erinnerung_24h_aktiv', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span>24 Stunden vor dem Termin</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editTerminForm.erinnerung_morgen_aktiv}
                            onChange={(e) => handleEditTerminChange('erinnerung_morgen_aktiv', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span>Am Termin-Tag um 07:00 Uhr</span>
                        </label>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 bg-slate-50 flex justify-between items-center gap-3">
              <button
                onClick={handleDeleteEditTermin}
                disabled={isSavingEditTermin}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-700 bg-white border border-red-200 hover:bg-red-50 disabled:opacity-50"
                title="Termin loeschen (ohne Mail an den Kunden, GCal-Eintrag wird mit-geloescht)"
              >
                <Trash2 className="w-3.5 h-3.5" /> Termin loeschen
              </button>
              <div className="flex gap-3">
                <button onClick={handleCancelEditTermin} disabled={isSavingEditTermin} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60">Abbrechen</button>
                <button onClick={handleSaveEditTermin} disabled={isSavingEditTermin} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0" style={{ backgroundColor: CI.brombeer }}>
                  {isSavingEditTermin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isSavingEditTermin ? 'Speichert…' : 'Änderungen speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kalender-Fullscreen-Overlay — liegt ueber dem Dashboard, modale
          Termin-Dialoge darueber (z-50), die aus dem Kalender getriggert
          werden, ueberlagern beides sauber. */}
      <KalenderAnsicht
        isOffen={isKalenderOpen}
        kunde={kalenderKunde}
        onClose={handleKalenderSchliessen}
        onAnlageOeffnen={handleKalenderAnlageOeffnen}
        onEditOeffnen={handleKalenderEditOeffnen}
        refreshKey={kalenderRefreshKey}
      />

      {/* Umkreis-Karten-Modal — Klick auf "Karte & Umkreis" in der Akte */}
      {showUmkreisKarte && selectedClient && (
        <UmkreisKartenModal
          zielKunde={selectedClient}
          alleKunden={alleKunden}
          onClose={() => setShowUmkreisKarte(false)}
        />
      )}

      {nachbereitungTermin && (
        <NachbereitungModal
          termin={nachbereitungTermin}
          onClose={() => setNachbereitungTermin(null)}
          onSaved={async () => {
            setNachbereitungTermin(null);
            setNachbereitungRefreshKey(k => k + 1);
            // Wenn eine Akte offen ist, deren Terminliste frisch laden.
            const kundeId = getKundeIdFromSelection();
            if (kundeId) {
              try {
                const liste = await api.get(`/api/termine?kunde_id=${encodeURIComponent(kundeId)}`);
                setKundeTermine(Array.isArray(liste) ? liste : []);
              } catch (e) { console.warn('Terminliste neu laden fehlgeschlagen', e); }
            }
          }}
        />
      )}
    </div>
  );
}

// ==========================================================================
// Sidebar-Komponenten
// ==========================================================================

// Eine Zeile in der Sidebar: Name + relatives Datum/Uhrzeit + Pflegegrad-Mini.
// Ist anklickbar und visuell hervorgehoben, wenn ausgewaehlt.
// ==========================================================================
// Diagnosen-Block in der Kundenakte (Tab 1)
// ==========================================================================
function KundeDiagnosenBlock({
  diagnosen, katalog, suche, setSuche, segmentFilter, setSegmentFilter,
  freitext, setFreitext, isSaving, onAdd, onDelete,
}) {
  const SEGMENTE = [
    { key: '', label: 'Alle' },
    { key: 'kinder', label: 'Kinder/Jugendliche' },
    { key: 'erwachsene', label: 'Erwachsene' },
    { key: 'senioren', label: 'Senioren ab 60' },
  ];

  // Bereits zugeordnete diagnose_ids — die filtern wir aus den Vorschlaegen.
  const bereitsErfasst = new Set(diagnosen.filter(d => d.diagnose_id).map(d => d.diagnose_id));

  // Vorschlagsliste: Suchtreffer im Katalog (Name + Oberkategorie),
  // optional Segment-Filter. 50 reicht: Senioren haben ca. 29 Eintraege,
  // Erwachsene ca. 41 — beides passt in die scrollbare Box drunter.
  const q = suche.trim().toLowerCase();
  const vorschlaege = (katalog || [])
    .filter(d => d.aktiv !== false)
    .filter(d => !bereitsErfasst.has(d.id))
    .filter(d => !segmentFilter || d.segment === segmentFilter)
    .filter(d => !q
      || d.name.toLowerCase().includes(q)
      || (d.oberkategorie || '').toLowerCase().includes(q))
    .slice(0, 50);

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: CI.brombeer }}>
          <Activity className="w-4 h-4" /> Diagnosen
          {diagnosen.length > 0 && <span className="text-xs font-normal text-gray-500">({diagnosen.length})</span>}
        </h4>
      </div>

      {/* Bereits erfasste Diagnosen */}
      {diagnosen.length === 0 ? (
        <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-4">
          Noch keine Diagnosen erfasst. Wähle unten aus dem Katalog oder gib eine sonstige Diagnose als Freitext an.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mb-4">
          {diagnosen.map(kd => (
            <span
              key={kd.id}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border ${kd.diagnose_id
                ? 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200'
                : 'bg-amber-50 text-amber-800 border-amber-200'}`}
              title={kd.oberkategorie ? `${kd.oberkategorie}${kd.segment ? ' · ' + kd.segment : ''}` : 'Freitext-Diagnose'}
            >
              {kd.name}
              <button
                onClick={() => onDelete(kd)}
                className="ml-1 text-current opacity-50 hover:opacity-100"
                title="Diagnose entfernen"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Suche im Katalog */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="flex flex-col md:flex-row gap-2 mb-2">
          <input
            type="text"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={'Diagnose suchen (z.B. Demenz, Diabetes, COPD)…'}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
            disabled={isSaving}
          />
          <select
            value={segmentFilter}
            onChange={(e) => setSegmentFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
            disabled={isSaving}
          >
            {SEGMENTE.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {/* Vorschlagsliste — immer sichtbar, damit der Berater sofort sieht,
            was es im aktiven Filter zur Auswahl gibt. Empty-States
            unterscheiden, ob der Katalog leer ist oder nur die Filterung. */}
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {(katalog || []).length === 0 ? (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              Diagnose-Katalog ist leer. Auf dem Server bitte einmalig
              <code className="mx-1 px-1 bg-red-100 rounded">python seed_diagnosen.py</code>
              im Backend-Verzeichnis ausführen.
            </div>
          ) : vorschlaege.length === 0 ? (
            <div className="text-xs text-gray-500 italic px-2 py-3 text-center">
              Keine Treffer für diese Filterung. Andere Altersgruppe wählen, Suche anpassen — oder unten eine Freitext-Diagnose angeben.
            </div>
          ) : vorschlaege.map(d => (
            <button
              key={d.id}
              onClick={() => onAdd({ diagnoseId: d.id })}
              disabled={isSaving}
              className="w-full text-left px-3 py-2 bg-white border border-gray-200 rounded hover:bg-fuchsia-50 hover:border-fuchsia-300 transition-colors disabled:opacity-50"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-gray-800">{d.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-500">{d.segment}</span>
              </div>
              <div className="text-xs text-gray-500">{d.oberkategorie}</div>
            </button>
          ))}
        </div>

        {/* Freitext-Diagnose */}
        <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2">
          <input
            type="text"
            value={freitext}
            onChange={(e) => setFreitext(e.target.value)}
            placeholder="Sonstige Diagnose (Freitext)…"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
            disabled={isSaving}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && freitext.trim()) {
                e.preventDefault();
                onAdd({ freitext });
              }
            }}
          />
          <button
            onClick={() => onAdd({ freitext })}
            disabled={isSaving || !freitext.trim()}
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
            style={{ backgroundColor: CI.brombeer }}
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Freitext
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Begutachtungs-Vorschau: heuristische Liste der MDK-Kriterien, die aus
// den erfassten Diagnosen wahrscheinlich relevant werden. Pro Modul
// gruppiert + nach Trefferzahl sortiert.
// ==========================================================================
function BegutachtungVorschauBlock({ vorschau }) {
  const MODUL_LABELS = {
    M1: 'Mobilität',
    M2: 'Kognition / Kommunikation',
    M3: 'Verhalten / psych. Problemlagen',
    M4: 'Selbstversorgung',
    M5: 'Krankheits- / Therapieanforderungen',
    M6: 'Alltagsleben / soziale Kontakte',
  };

  return (
    <div className="mt-8 pt-6 border-t border-gray-100">
      <h4 className="text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: CI.brombeer }}>
        <ClipboardList className="w-4 h-4" /> Begutachtungs-Vorschau
        <span className="text-xs font-normal text-gray-500">aus {vorschau.anzahl_diagnosen} Diagnose{vorschau.anzahl_diagnosen === 1 ? '' : 'n'}</span>
      </h4>
      <p className="text-xs text-gray-500 italic mb-4">
        Heuristische Vorauswahl der MDK-Kriterien, die aus den erfassten Diagnosen typischerweise besonders relevant werden. Keine MDK-Begutachtung — nur Hinweis fürs Beratungsgespräch.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vorschau.module.map(m => (
          <div key={m.modul} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
              {m.modul} — {MODUL_LABELS[m.modul] || ''}
              <span className="ml-2 text-gray-400 normal-case font-normal">({m.kriterien.length})</span>
            </div>
            <ul className="space-y-1">
              {m.kriterien.map(k => (
                <li key={k.nummer} className="flex items-start gap-2 text-xs">
                  <span className="font-mono text-gray-400 w-12 flex-shrink-0">{k.nummer}</span>
                  <span className="flex-1 text-gray-700">{k.beschreibung}</span>
                  {k.anzahl_treffer > 1 && (
                    <span
                      className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                      style={{ backgroundColor: CI.brombeer }}
                      title={`Erwähnt von: ${k.diagnosen.join(', ')}`}
                    >
                      ×{k.anzahl_treffer}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==========================================================================
// To-do-Block in der Kundenakte (Tab 1)
// ==========================================================================
// Prioritaets-Badge fuer ToDos. A = rot/dringend, B = grau/neutral,
// C = blass/nachrangig. Beim Klick (sofern onClick gesetzt) wird durch
// A -> B -> C -> A rotiert, was den Inline-Edit ohne extra Modal erlaubt.
const PRIO_STYLE = {
  A: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5', label: 'A' },
  B: { bg: '#E5E7EB', text: '#374151', border: '#D1D5DB', label: 'B' },
  C: { bg: '#F3F4F6', text: '#9CA3AF', border: '#E5E7EB', label: 'C' },
};
const PRIO_NEXT = { A: 'B', B: 'C', C: 'A' };

function PrioBadge({ prio = 'B', onClick, title }) {
  const code = (prio || 'B').toUpperCase();
  const s = PRIO_STYLE[code] || PRIO_STYLE.B;
  const klickbar = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={klickbar ? (e) => { e.stopPropagation(); onClick(PRIO_NEXT[code] || 'B'); } : undefined}
      disabled={!klickbar}
      title={title || (klickbar ? `Priorität ${code} — Klick: nächste Stufe` : `Priorität ${code}`)}
      className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold border transition-transform hover:scale-110 disabled:cursor-default"
      style={{ backgroundColor: s.bg, color: s.text, borderColor: s.border }}
    >
      {s.label}
    </button>
  );
}

function KundeTodosBlock({
  todos, neuerText, setNeuerText, neuerFaellig, setNeuerFaellig,
  neuerPrio, setNeuerPrio,
  isSaving, zeigeErledigte, setZeigeErledigte,
  onAdd, onToggle, onUpdate, onDelete,
}) {
  const heuteISO = new Date().toISOString().slice(0, 10);

  // Gruppieren: ueberfaellig / heute / spaeter (mit Datum) / ohne Datum / erledigt.
  const gruppen = {
    ueberfaellig: [],
    heute: [],
    spaeter: [],
    ohneDatum: [],
    erledigt: [],
  };
  for (const t of todos) {
    if (t.erledigt) { gruppen.erledigt.push(t); continue; }
    if (!t.faellig_am) { gruppen.ohneDatum.push(t); continue; }
    if (t.faellig_am < heuteISO) gruppen.ueberfaellig.push(t);
    else if (t.faellig_am === heuteISO) gruppen.heute.push(t);
    else gruppen.spaeter.push(t);
  }

  // Submit per Enter im Textfeld (ohne Shift) — schnelles Tippen.
  const onTextKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <h4 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: CI.brombeer }}>
        <CheckSquare className="w-4 h-4" /> To-dos für diesen Kunden
        {todos.filter(t => !t.erledigt).length > 0 && (
          <span className="text-xs font-normal text-gray-500">({todos.filter(t => !t.erledigt).length} offen)</span>
        )}
      </h4>

      {/* Eingabe-Zeile */}
      <div className="flex gap-2 mb-4 items-center">
        <input
          type="text"
          value={neuerText}
          onChange={(e) => setNeuerText(e.target.value)}
          onKeyDown={onTextKeyDown}
          placeholder="Neue Aufgabe (Enter zum Hinzufügen)…"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          disabled={isSaving}
        />
        <select
          value={neuerPrio || 'B'}
          onChange={(e) => setNeuerPrio(e.target.value)}
          title="Priorität: A = hoch, B = Standard, C = niedrig"
          className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          disabled={isSaving}
        >
          <option value="A">A — hoch</option>
          <option value="B">B — mittel</option>
          <option value="C">C — niedrig</option>
        </select>
        <input
          type="date"
          value={neuerFaellig}
          onChange={(e) => setNeuerFaellig(e.target.value)}
          title="Optionales Fälligkeitsdatum (leer lassen = ohne Datum)"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          disabled={isSaving}
        />
        <button
          onClick={onAdd}
          disabled={isSaving || !neuerText.trim()}
          className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
          style={{ backgroundColor: CI.brombeer }}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Hinzufügen
        </button>
      </div>

      {todos.length === 0 && (
        <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          Noch keine To-dos. Tipp: Notiere hier z.B. „Im Mai mit Bremen-Nord-Tour verbinden" oder „Pflegegrad-Antrag-Status nachfragen".
        </div>
      )}

      {/* Gruppen rendern */}
      <TodoGruppe titel="Überfällig" termine={gruppen.ueberfaellig} farbeText="text-red-700" onToggle={onToggle} onUpdate={onUpdate} onDelete={onDelete} zeigeDatum />
      <TodoGruppe titel="Heute fällig" termine={gruppen.heute} farbeText="text-amber-700" onToggle={onToggle} onUpdate={onUpdate} onDelete={onDelete} zeigeDatum />
      <TodoGruppe titel="Geplant" termine={gruppen.spaeter} farbeText="text-gray-700" onToggle={onToggle} onUpdate={onUpdate} onDelete={onDelete} zeigeDatum />
      <TodoGruppe titel="Ohne Datum" termine={gruppen.ohneDatum} farbeText="text-gray-600" onToggle={onToggle} onUpdate={onUpdate} onDelete={onDelete} zeigeDatum={false} />

      {/* Erledigte: einklappbar */}
      {gruppen.erledigt.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setZeigeErledigte(v => !v)}
            className="text-xs font-bold text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            {zeigeErledigte ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Erledigt ({gruppen.erledigt.length})
          </button>
          {zeigeErledigte && (
            <div className="mt-2 space-y-1">
              {gruppen.erledigt.map(t => (
                <TodoZeile key={t.id} todo={t} onToggle={onToggle} onUpdate={onUpdate} onDelete={onDelete} zeigeDatum erledigt />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TodoGruppe({ titel, termine, farbeText, onToggle, onUpdate, onDelete, zeigeDatum }) {
  if (termine.length === 0) return null;
  return (
    <div className="mb-3">
      <div className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${farbeText}`}>{titel} ({termine.length})</div>
      <div className="space-y-1">
        {termine.map(t => (
          <TodoZeile key={t.id} todo={t} onToggle={onToggle} onUpdate={onUpdate} onDelete={onDelete} zeigeDatum={zeigeDatum} />
        ))}
      </div>
    </div>
  );
}

function TodoZeile({ todo, onToggle, onUpdate, onDelete, zeigeDatum, erledigt }) {
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(todo.text || '');
  const [editFaellig, setEditFaellig] = useState(todo.faellig_am || '');
  const [editPrio, setEditPrio] = useState((todo.prioritaet || 'B').toUpperCase());
  const [isSavingZeile, setIsSavingZeile] = useState(false);

  const datumLabel = todo.faellig_am
    ? new Date(todo.faellig_am + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' })
    : '';

  const startEdit = () => {
    setEditText(todo.text || '');
    setEditFaellig(todo.faellig_am || '');
    setEditPrio((todo.prioritaet || 'B').toUpperCase());
    setEditMode(true);
  };

  const speichern = async () => {
    const text = (editText || '').trim();
    if (!text) { alert('Text darf nicht leer sein.'); return; }
    setIsSavingZeile(true);
    try {
      await onUpdate(todo, {
        text,
        faellig_am: editFaellig || null,
        prioritaet: editPrio,
      });
      setEditMode(false);
    } catch { /* Fehleranzeige uebernimmt onUpdate */ }
    finally { setIsSavingZeile(false); }
  };

  if (editMode) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 rounded border bg-fuchsia-50 border-fuchsia-200">
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); speichern(); } if (e.key === 'Escape') setEditMode(false); }}
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          autoFocus
        />
        <select
          value={editPrio}
          onChange={(e) => setEditPrio(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
          title="Priorität"
        >
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
        <input
          type="date"
          value={editFaellig}
          onChange={(e) => setEditFaellig(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded text-sm"
        />
        <button
          onClick={speichern}
          disabled={isSavingZeile}
          className="text-xs font-bold px-3 py-1 rounded text-white bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50"
        >
          {isSavingZeile ? '…' : 'Speichern'}
        </button>
        <button
          onClick={() => setEditMode(false)}
          className="text-xs font-bold px-3 py-1 rounded text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
        >
          Abbrechen
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded border ${erledigt ? 'bg-gray-50 border-gray-100 text-gray-400' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
      <button
        onClick={() => onToggle(todo)}
        className={`flex-shrink-0 ${erledigt ? 'text-gray-400' : 'text-gray-600 hover:text-fuchsia-700'}`}
        title={erledigt ? 'Als offen markieren' : 'Als erledigt markieren'}
      >
        {todo.erledigt ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
      </button>
      <PrioBadge
        prio={todo.prioritaet}
        onClick={erledigt ? undefined : (next) => onUpdate(todo, { prioritaet: next })}
      />
      <div
        className={`flex-1 text-sm cursor-pointer ${erledigt ? 'line-through' : ''}`}
        onClick={erledigt ? undefined : startEdit}
        title={erledigt ? '' : 'Klick zum Bearbeiten'}
      >
        {todo.text}
      </div>
      {zeigeDatum && datumLabel && (
        <div className={`text-xs whitespace-nowrap ${erledigt ? 'text-gray-400' : 'text-gray-500'}`}>{datumLabel}</div>
      )}
      {!erledigt && (
        <button
          onClick={startEdit}
          className="flex-shrink-0 text-gray-300 hover:text-fuchsia-600 transition-colors"
          title="Bearbeiten"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={() => onDelete(todo)}
        className="flex-shrink-0 text-gray-300 hover:text-red-600 transition-colors"
        title="Löschen"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function SidebarTerminZeile({ termin, isSelected, onClick, istZukunft }) {
  const tagLabel = formatTagRelativ(termin.date);
  const istVideo = termin.type === 'video';
  const istDoiPending = termin.doiStatus === 'pending';
  return (
    <div
      onClick={onClick}
      className={`p-3 rounded-lg cursor-pointer border-l-4 transition-colors ${isSelected ? 'bg-fuchsia-50 shadow-sm' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
      style={{ borderColor: isSelected ? CI.brombeer : undefined }}
    >
      <div className="flex justify-between items-start gap-2">
        <span className={`font-bold text-sm leading-tight flex items-center gap-1.5 ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
          {istDoiPending && (
            <span
              className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0"
              title="DSGVO-Bestätigung steht noch aus (Double-Opt-In)"
            />
          )}
          {termin.lastName}, {termin.firstName}
        </span>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white whitespace-nowrap"
          style={{ backgroundColor: istVideo ? CI.blue : CI.green }}
          title={istVideo ? 'Videotermin' : 'Hausbesuch'}
        >
          {istVideo ? 'Video' : 'vor Ort'}
        </span>
      </div>
      <div className="flex items-center justify-between mt-1 text-xs">
        <span className={`${istZukunft ? 'text-gray-700 font-semibold' : 'text-gray-500'}`}>
          {tagLabel}{termin.time ? ` · ${termin.time}` : ''}
        </span>
        <span className="text-gray-400">PG {termin.careLevel || '–'}</span>
      </div>
    </div>
  );
}

// ==========================================================================
// DokumenteTab — manueller Datei-Upload pro Kunde, in zwei Sub-Sektionen.
//   - Zweitgutachten (Pflegekasse-Bescheide, MDK-Reports)
//   - Sonstige Dokumente (alles uebrige)
// Dateien <= 20 MB. Erlaubte Typen: PDF, JPG, PNG, HEIC, DOCX.
// ==========================================================================
function DokumenteTab({ kundeId }) {
  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState(null);

  const laden = async () => {
    if (!kundeId) return;
    setLoading(true); setFehler(null);
    try {
      const liste = await api.get(`/api/kunden/${encodeURIComponent(kundeId)}/dokumente`);
      setDokumente(liste || []);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { laden(); }, [kundeId]);

  const zweitgutachten = dokumente.filter(d => d.kategorie === 'zweitgutachten');
  const sonstige      = dokumente.filter(d => d.kategorie === 'sonstige');

  return (
    <div className="p-4 space-y-6">
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: CI.brombeer }}>
          <FileText className="w-4 h-4" /> Zweitgutachten
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Pflegekasse-Bescheide, MDK-Reports, Gutachten von externen Stellen.
        </p>
        <DokumenteUploadFeld
          kundeId={kundeId}
          kategorie="zweitgutachten"
          onUploaded={laden}
        />
        <DokumenteListe
          dokumente={zweitgutachten}
          loading={loading}
          onLoeschen={async (id) => {
            if (!confirm('Dieses Dokument wirklich loeschen? Datei wird vom Server entfernt.')) return;
            try {
              await api.del(`/api/dokumente/${id}`);
              await laden();
            } catch (err) {
              alert('Loeschen fehlgeschlagen: ' + (err?.detail || err?.message || 'unbekannt'));
            }
          }}
        />
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: CI.brombeer }}>
          <FileText className="w-4 h-4" /> Sonstige Dokumente
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Alles uebrige — Briefe, Fotos, Vollmachten, Notizen mit Anhang.
        </p>
        <DokumenteUploadFeld
          kundeId={kundeId}
          kategorie="sonstige"
          onUploaded={laden}
        />
        <DokumenteListe
          dokumente={sonstige}
          loading={loading}
          onLoeschen={async (id) => {
            if (!confirm('Dieses Dokument wirklich loeschen? Datei wird vom Server entfernt.')) return;
            try {
              await api.del(`/api/dokumente/${id}`);
              await laden();
            } catch (err) {
              alert('Loeschen fehlgeschlagen: ' + (err?.detail || err?.message || 'unbekannt'));
            }
          }}
        />
      </div>

      {fehler && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {fehler}
        </div>
      )}
    </div>
  );
}

function DokumenteUploadFeld({ kundeId, kategorie, onUploaded }) {
  const [hochladen, setHochladen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const upload = async (datei) => {
    if (!datei) return;
    const erlaubteTypen = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!erlaubteTypen.includes((datei.type || '').toLowerCase())) {
      alert(`Dateityp nicht erlaubt: ${datei.type || 'unbekannt'}.\nErlaubt: PDF, JPG, PNG, HEIC, DOCX.`);
      return;
    }
    if (datei.size > 20 * 1024 * 1024) {
      alert(`Datei zu gross (${(datei.size / 1024 / 1024).toFixed(1)} MB). Maximal 20 MB.`);
      return;
    }
    setHochladen(true);
    const fd = new FormData();
    fd.append('kategorie', kategorie);
    fd.append('beschreibung', '');
    fd.append('datei', datei);
    try {
      const res = await fetch(`/api/kunden/${encodeURIComponent(kundeId)}/dokumente`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      onUploaded && onUploaded();
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err.message || 'unbekannt'));
    } finally {
      setHochladen(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const datei = e.dataTransfer.files?.[0];
        if (datei) upload(datei);
      }}
      className={`border-2 border-dashed rounded-lg p-4 mb-3 text-center transition-colors cursor-pointer ${
        dragOver ? 'border-fuchsia-400 bg-fuchsia-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
      }`}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,application/pdf,image/jpeg,image/png,image/heic,image/heif,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
      />
      {hochladen ? (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade hoch…
        </div>
      ) : (
        <div className="text-sm text-gray-500">
          <UploadCloud className="w-6 h-6 mx-auto mb-1 text-gray-400" />
          Datei hierher ziehen oder <span className="font-bold text-fuchsia-700">klicken zum Auswählen</span>
          <div className="text-xs text-gray-400 mt-1">
            PDF, JPG, PNG, HEIC, DOCX — max. 20 MB
          </div>
        </div>
      )}
    </div>
  );
}

function DokumenteListe({ dokumente, loading, onLoeschen }) {
  if (loading && (!dokumente || dokumente.length === 0)) {
    return <div className="text-xs text-gray-400 italic px-2 py-3"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> lade…</div>;
  }
  if (!dokumente || dokumente.length === 0) {
    return <div className="text-xs text-gray-400 italic px-2 py-3">Noch keine Dokumente in dieser Kategorie.</div>;
  }
  return (
    <ul className="space-y-1.5">
      {dokumente.map(d => (
        <li key={d.id} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <a
            href={`/api/dokumente/${d.id}/download`}
            target="_blank"
            rel="noreferrer"
            className="flex-1 text-sm font-medium text-gray-800 hover:text-fuchsia-700 truncate"
            title={d.dateiname_anzeige}
          >
            {d.dateiname_anzeige}
          </a>
          <span className="text-[10px] text-gray-400 flex-shrink-0">
            {d.groesse_bytes != null ? `${(d.groesse_bytes / 1024).toFixed(0)} KB` : ''}
          </span>
          <span className="text-[10px] text-gray-400 flex-shrink-0">
            {d.created_at ? new Date(d.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}
          </span>
          <button
            onClick={() => onLoeschen(d.id)}
            className="text-gray-400 hover:text-red-600 p-1 flex-shrink-0"
            title="Dokument loeschen"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Sidebar-Sektion fuer offene Schulungsanfragen (Kunden mit
 * quelle='schulung_anfrage' + DOI bestaetigt + kein zukuenftiger Termin).
 * Zeigt initial 5, "Mehr"-Button blendet die naechsten 10 ein.
 */
function SidebarSchulungsAnfragen({ anfragen, zeigeAlle, setZeigeAlle,
                                    selectedClient, setSelectedClient }) {
  const sichtbar = zeigeAlle ? anfragen : anfragen.slice(0, 5);
  const mehrCount = Math.max(0, anfragen.length - 5);

  return (
    <div className="border-b border-gray-100">
      <div className="px-4 py-2 bg-fuchsia-50 border-b border-fuchsia-100 text-xs font-bold text-fuchsia-700 uppercase tracking-wider flex items-center gap-2">
        <GraduationCap className="w-3.5 h-3.5" />
        Schulungs-Anfragen
        <span className="ml-auto font-normal text-fuchsia-500 normal-case tracking-normal">
          {anfragen.length || ''}
        </span>
      </div>
      <div className="p-3 space-y-2">
        {anfragen.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-3 px-2">
            Keine offenen Schulungsanfragen.
          </div>
        )}
        {sichtbar.map(k => {
          const platzhalter = {
            id: 'kunde-' + k.id,
            kundeId: k.id,
            firstName: k.firstName,
            lastName: k.lastName,
            zip: k.zip,
            city: k.city,
            phone: k.phone,
            email: k.email,
            time: '',
            type: 'schulung',
            status: 'kein_termin',
            date: '',
          };
          const isSelected = selectedClient?.id === platzhalter.id ||
                             selectedClient?.kundeId === k.id;
          return (
            <button key={k.id}
              onClick={() => setSelectedClient(platzhalter)}
              className={`w-full text-left p-2 rounded-lg border transition-colors ${
                isSelected
                  ? 'bg-fuchsia-50 border-fuchsia-300'
                  : 'bg-white border-gray-200 hover:bg-fuchsia-50/40 hover:border-fuchsia-200'
              }`}
            >
              <div className="text-sm font-medium text-gray-800 truncate">
                {k.firstName} {k.lastName}
              </div>
              <div className="text-[11px] text-gray-500 truncate">
                {k.zip} {k.city}
              </div>
            </button>
          );
        })}
        {!zeigeAlle && mehrCount > 0 && (
          <button
            onClick={() => setZeigeAlle(true)}
            className="w-full text-center text-xs font-medium text-fuchsia-600 hover:text-fuchsia-700 py-2 hover:bg-fuchsia-50 rounded-lg"
          >
            +{mehrCount} weitere anzeigen
          </button>
        )}
        {zeigeAlle && anfragen.length > 5 && (
          <button
            onClick={() => setZeigeAlle(false)}
            className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700 py-2 hover:bg-gray-50 rounded-lg"
          >
            ↑ einklappen
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Sidebar-Sektion fuer neue Buchungs-Widget-Anfragen.
 *
 * Sortierung umschaltbar:
 *   - 'eingang' (default): chronologisch nach Anlage-Zeitpunkt (Neueste oben).
 *     Backend liefert das schon so; clientseitig nichts zu tun.
 *   - 'termin': nach geplantem Beratungstermin (frueheste oben). Bei
 *     Hausbesuchen, wo das Termin-Datum noch nicht fix ist, faellt das
 *     ans Ende.
 *
 * Anzeige pro Eintrag:
 *   - PRIMAER  : Eingangs-Datum + Uhrzeit (immer, sortierschluessel im
 *                Default-Modus)
 *   - SEKUNDAER: Termin-Slot bei Video ("📹 Termin: Mo, 25.05. 14:00")
 *                bzw. Hinweis "🏠 Hausbesuch — Termin wird vereinbart"
 *
 * Limit 5, "mehr anzeigen" geht auf 15.
 */
function SidebarNeueBuchungen({ termine, zeigeAlle, setZeigeAlle,
                                  sortModus, setSortModus,
                                  selectedClient, setSelectedClient }) {
  const formatDatum = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };
  const formatUhrzeit = (uhr) => (uhr || '').slice(0, 5);  // 'HH:MM:SS' -> 'HH:MM'
  const formatEingang = (isoTs) => {
    if (!isoTs) return '';
    const d = new Date(isoTs);
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })
      + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Backend liefert immer nach 'eingang' (created_at DESC) sortiert.
  // Wenn Hendrik auf 'termin' umschaltet, sortieren wir clientseitig:
  // frueheste Termine zuerst, Termine ohne Datum (Hausbesuche) ans Ende.
  const sortiert = sortModus === 'termin'
    ? [...termine].sort((a, b) => {
        const da = a.date || '9999-12-31';
        const db = b.date || '9999-12-31';
        if (da !== db) return da.localeCompare(db);
        return (a.time || '').localeCompare(b.time || '');
      })
    : termine;  // backend liefert schon nach Eingang sortiert

  const sichtbar = zeigeAlle ? sortiert : sortiert.slice(0, 5);
  const mehrCount = Math.max(0, sortiert.length - 5);

  return (
    <div className="border-b border-gray-100">
      <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-2">
        <CalendarPlus className="w-3.5 h-3.5" />
        Neue Buchungs-Anfragen
        <span className="ml-auto font-normal text-emerald-500 normal-case tracking-normal">
          {termine.length || ''}
        </span>
      </div>
      {termine.length > 0 && (
        <div className="px-3 pt-2 pb-1 flex gap-1 text-[10px] uppercase tracking-wider">
          <button
            onClick={() => setSortModus('eingang')}
            className={`flex-1 py-1 rounded font-bold transition-colors ${
              sortModus === 'eingang'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Sortierung nach Eingangs-Zeitpunkt (neueste oben)"
          >
            ↓ Eingang
          </button>
          <button
            onClick={() => setSortModus('termin')}
            className={`flex-1 py-1 rounded font-bold transition-colors ${
              sortModus === 'termin'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Sortierung nach Beratungstermin (frueheste oben, Hausbesuche unten)"
          >
            ↑ Termin
          </button>
        </div>
      )}
      <div className="p-3 space-y-2">
        {termine.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-3 px-2">
            Keine neuen Buchungs-Anfragen.
          </div>
        )}
        {sichtbar.map(t => {
          const isSelected = selectedClient?.id === t.id;
          const istVideo = t.type === 'video';
          const pg = (t.careLevel && t.careLevel !== '0') ? `PG ${t.careLevel}` : '';
          return (
            <button key={t.id}
              onClick={() => setSelectedClient(t)}
              className={`w-full text-left p-2 rounded-lg border transition-colors ${
                isSelected
                  ? 'bg-emerald-50 border-emerald-300'
                  : 'bg-white border-gray-200 hover:bg-emerald-50/40 hover:border-emerald-200'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-gray-800 truncate">
                  {t.firstName} {t.lastName}
                </div>
                {pg && (
                  <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                    {pg}
                  </span>
                )}
              </div>
              {/* Zeile 1: IMMER Eingang — das ist der Sortierschluessel im Default */}
              <div className="text-[11px] text-gray-600 mt-0.5 flex items-center gap-1">
                <Clock className="w-3 h-3 text-gray-400" />
                <span>Eingang: {formatEingang(t.created_at)}</span>
              </div>
              {/* Zeile 2: Termin-Info (Video-Slot oder Hausbesuch-Hinweis) */}
              <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                {istVideo ? (
                  <>
                    <Video className="w-3 h-3 text-blue-500" />
                    <span>Termin: {formatDatum(t.date)} {formatUhrzeit(t.time)} Uhr</span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-3 h-3 text-green-600" />
                    <span>Hausbesuch — Termin wird vereinbart</span>
                  </>
                )}
              </div>
            </button>
          );
        })}
        {!zeigeAlle && mehrCount > 0 && (
          <button
            onClick={() => setZeigeAlle(true)}
            className="w-full text-center text-xs font-medium text-emerald-700 hover:text-emerald-800 py-2 hover:bg-emerald-50 rounded-lg"
          >
            +{mehrCount} weitere anzeigen
          </button>
        )}
        {zeigeAlle && termine.length > 5 && (
          <button
            onClick={() => setZeigeAlle(false)}
            className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700 py-2 hover:bg-gray-50 rounded-lg"
          >
            ↑ einklappen
          </button>
        )}
      </div>
    </div>
  );
}


function SidebarSektion({ titel, termine, selectedClient, setSelectedClient, emptyText, istZukunft }) {
  return (
    <div className="border-b border-gray-100">
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        {istZukunft ? <CalendarIcon className="w-3.5 h-3.5" /> : <History className="w-3.5 h-3.5" />}
        {titel}
        <span className="ml-auto font-normal text-gray-400 normal-case tracking-normal">{termine.length || ''}</span>
      </div>
      <div className="p-3 space-y-2">
        {termine.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-4 px-2">{emptyText}</div>
        )}
        {termine.map(t => (
          <SidebarTerminZeile
            key={t.id}
            termin={t}
            isSelected={selectedClient?.id === t.id}
            onClick={() => setSelectedClient(t)}
            istZukunft={istZukunft}
          />
        ))}
      </div>
    </div>
  );
}

// Bei aktiver Suche: alle Kunden nach Name/Ort durchsuchen, anklickbar zur
// Kunden-Akte. Wenn der Kunde aktuell einen Sidebar-Termin hat, springt der
// Klick auf diesen Termin (damit die Akte den richtigen Kontext zeigt).
function SidebarSucheTreffer({ query, alleKunden, terminListeZukunft, terminListeVergangen, selectedClient, setSelectedClient }) {
  const q = query.toLowerCase();
  // /api/kunden liefert bereits Dashboard-Schema (firstName, lastName, zip, city ...)
  // plus 'relative' (primaerer Angehoeriger). Wir suchen ueber beide Datenquellen,
  // damit z.B. "Christiane Sauret" gefunden wird, wenn das die Tochter eines
  // Kunden ist (gerade bei demenziell Erkrankten oft der Termin-Anker).
  const treffer = (alleKunden || []).map(k => {
    const haystackKunde = `${k.firstName || ''} ${k.lastName || ''} ${k.zip || ''} ${k.city || ''}`.toLowerCase();
    if (haystackKunde.includes(q)) {
      return { kunde: k, viaAngehoeriger: false };
    }
    if (k.relative) {
      const haystackAngeh = `${k.relative.firstName || ''} ${k.relative.lastName || ''}`.toLowerCase();
      if (haystackAngeh.includes(q)) {
        return { kunde: k, viaAngehoeriger: true };
      }
    }
    return null;
  }).filter(Boolean).slice(0, 30);

  // Kandidaten-Termin pro Kunde finden: zuerst naechster Zukunfts-Termin,
  // sonst letzter vergangener — fuer den Klick als Kontext-Anker.
  const findeTermin = (kundeId) => {
    return terminListeZukunft.find(t => t.kundeId === kundeId)
        || terminListeVergangen.find(t => t.kundeId === kundeId)
        || null;
  };

  if (treffer.length === 0) {
    return <div className="p-4 text-center text-sm text-gray-400">Keine Treffer für „{query}".</div>;
  }
  return (
    <div className="p-3 space-y-2">
      {treffer.map(({ kunde: k, viaAngehoeriger }) => {
        const ktermin = findeTermin(k.id);
        const platzhalter = ktermin || {
          ...k,
          id: 'kunde-' + k.id,
          kundeId: k.id,
          time: '',
          type: 'hausbesuch',
          status: 'kein_termin',
          date: '',
        };
        const isSelected = selectedClient?.id === platzhalter.id
                        || selectedClient?.kundeId === k.id;
        return (
          <div
            key={k.id}
            onClick={() => setSelectedClient(platzhalter)}
            className={`p-3 rounded-lg cursor-pointer border-l-4 transition-colors ${isSelected ? 'bg-fuchsia-50 shadow-sm' : 'bg-white hover:bg-gray-50 border-gray-200'}`}
            style={{ borderColor: isSelected ? CI.brombeer : undefined }}
          >
            <div className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
              {k.doiStatus === 'pending' && (
                <span
                  className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0"
                  title="DSGVO-Bestätigung steht noch aus (Double-Opt-In)"
                />
              )}
              {k.lastName}, {k.firstName}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {k.zip || ''} {k.city || ''}
              {viaAngehoeriger && k.relative && (
                <span className="ml-1 text-fuchsia-700 font-semibold">
                  · über Angehörigen: {k.relative.firstName} {k.relative.lastName}
                </span>
              )}
              {ktermin && (
                <span className="ml-1 text-gray-400">
                  · nächster: {formatTagRelativ(ktermin.date)}{ktermin.time ? ` ${ktermin.time}` : ''}
                </span>
              )}
              {!ktermin && <span className="ml-1 italic text-gray-400">· kein Termin</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ==========================================================================
// VerfuegbarkeitsPicker — Mini-Kalender + Slot-Auswahl + Tages-Termin-Liste
// fuer Termin-Anlage und -Verschiebung. Statt zwei abstrakter input-Felder
// zeigt es direkt was geht (Wochenplan / Abwesenheit / belegte Slots), so
// dass der Berater nicht erst probieren und Fehlermeldung lesen muss.
// ==========================================================================
const KAL_MONATE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const KAL_WOCHENTAGE = ['Mo','Di','Mi','Do','Fr','Sa','So'];

// `ueberregelung=true` (aus dem Override-Schalter im Termin-Modal) hebelt
// die Wochenplan-/Abwesenheits-Sperre aus: dann sind alle zukuenftigen
// Tage klickbar und das Slot-Raster zeigt 07:00-21:00 in 30-Minuten-
// Schritten, unabhaengig vom Standard-Plan.
function VerfuegbarkeitsPicker({ datum, uhrzeit, onChange, exkludiereTerminId, ueberregelung = false }) {
  const heuteDate = new Date();
  const initJahr = datum ? parseInt(datum.slice(0, 4), 10) : heuteDate.getFullYear();
  const initMonat = datum ? parseInt(datum.slice(5, 7), 10) - 1 : heuteDate.getMonth();
  const [calJahr, setCalJahr] = useState(initJahr);
  const [calMonat, setCalMonat] = useState(initMonat);
  const [ausgebuchtTage, setAusgebuchtTage] = useState([]);
  const [busy, setBusy] = useState([]);
  const [verfuegbar, setVerfuegbar] = useState([]);
  const [tagesTermine, setTagesTermine] = useState([]);
  const [loadingMonat, setLoadingMonat] = useState(false);
  const [loadingTag, setLoadingTag] = useState(false);

  // Monats-Daten laden
  useEffect(() => {
    setLoadingMonat(true);
    api.get(`/api/kalender/ausgebucht?jahr=${calJahr}&monat=${calMonat + 1}`)
      .then(d => setAusgebuchtTage(d.ausgebucht || []))
      .catch(() => setAusgebuchtTage([]))
      .finally(() => setLoadingMonat(false));
  }, [calJahr, calMonat]);

  // Tages-Daten laden, wenn Datum gesetzt
  useEffect(() => {
    if (!datum) { setBusy([]); setVerfuegbar([]); setTagesTermine([]); return; }
    setLoadingTag(true);
    // Im Override-Modus laesst das Backend ein durchgehendes 30-Min-Raster
    // 07:00-21:00 pruefen, damit Termine ausserhalb der Standard-Verfuegbarkeit
    // korrekt als belegt erscheinen.
    const belegtUrl = `/api/kalender/belegt?datum=${datum}` + (ueberregelung ? '&vollraster=true' : '');
    Promise.all([
      api.get(belegtUrl).catch(() => ({ busy: [], verfuegbar: [] })),
      api.get(`/api/termine?von=${datum}&bis=${datum}`).catch(() => []),
    ]).then(([slotData, termine]) => {
      setBusy(slotData.busy || []);
      setVerfuegbar(slotData.verfuegbar || []);
      // Eigenen Termin (beim Verschieben) NICHT als belegt anzeigen
      setTagesTermine((termine || []).filter(t => t.id !== exkludiereTerminId));
    }).finally(() => setLoadingTag(false));
  }, [datum, exkludiereTerminId, ueberregelung]);

  // Mini-Kalender-Zellen berechnen
  const tageImMonat = new Date(calJahr, calMonat + 1, 0).getDate();
  const erster = new Date(calJahr, calMonat, 1);
  const startOffset = (erster.getDay() + 6) % 7;  // Mo=0..So=6
  const heuteISO = heuteDate.toISOString().slice(0, 10);

  const vorMonat = () => {
    let m = calMonat - 1, j = calJahr;
    if (m < 0) { m = 11; j--; }
    setCalMonat(m); setCalJahr(j);
  };
  const nachMonat = () => {
    let m = calMonat + 1, j = calJahr;
    if (m > 11) { m = 0; j++; }
    setCalMonat(m); setCalJahr(j);
  };

  const klickTag = (tag) => {
    const d = `${calJahr}-${String(calMonat + 1).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
    if (!ueberregelung && ausgebuchtTage.includes(d)) return; // ausgebucht / Abwesenheit / kein Plan → Klick blockieren (im Override durchgereicht)
    if (!ueberregelung && d < heuteISO) return;  // Vergangenheit nur im Override-Modus erlaubt (nachtraegliche Termin-Anlage fuer Protokolle)
    onChange(d, '');                             // Datum setzen, Uhrzeit zurueck
  };

  const klickSlot = (slot) => {
    // Im Override-Modus auch GCal-belegte Slots klickbar — Hendrik blockt
    // sich Tage fuer Touren und fuellt Slots nachtraeglich. Doppel-Eintrag
    // im Google-Kalender ist gewollt.
    if (!ueberregelung && busy.includes(slot)) return;
    onChange(datum, slot);
  };

  // Map fuer "welcher Termin liegt zu welcher Uhrzeit" (fuer Hover-Info)
  const terminProUhrzeit = {};
  for (const t of tagesTermine) {
    if (t.time) terminProUhrzeit[t.time] = t;
  }

  // Im Override-Modus zeigen wir ein durchgehendes Raster 07:00-21:00 in
  // 30-Minuten-Schritten an, statt nur den Wochenplan-Slots — damit kann
  // der Berater einen Sondertermin auch frueh morgens oder spaet abends
  // ansetzen. Bereits belegte Slots bleiben weiter gesperrt.
  const overrideSlotsGenerieren = () => {
    const out = [];
    for (let h = 7; h <= 21; h++) {
      out.push(`${String(h).padStart(2, '0')}:00`);
      if (h < 21) out.push(`${String(h).padStart(2, '0')}:30`);
    }
    return out;
  };
  const slotsAnzeigen = ueberregelung ? overrideSlotsGenerieren() : verfuegbar;

  // Wochentags-Header + Tageszellen
  const zellen = [];
  for (let i = 0; i < startOffset; i++) zellen.push(null);
  for (let t = 1; t <= tageImMonat; t++) zellen.push(t);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      {/* Linke Seite: Mini-Monats-Kalender */}
      <div className="border border-gray-200 rounded-lg p-3 bg-white">
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={vorMonat} className="p-1 rounded hover:bg-gray-100">
            <ChevronDown className="w-4 h-4 rotate-90 text-gray-600" />
          </button>
          <div className="text-sm font-bold text-gray-800">{KAL_MONATE[calMonat]} {calJahr}</div>
          <button type="button" onClick={nachMonat} className="p-1 rounded hover:bg-gray-100">
            <ChevronDown className="w-4 h-4 -rotate-90 text-gray-600" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {KAL_WOCHENTAGE.map(w => (
            <div key={w} className="text-[10px] text-center text-gray-400 font-bold uppercase">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {zellen.map((tag, i) => {
            if (!tag) return <div key={i} className="aspect-square"></div>;
            const d = `${calJahr}-${String(calMonat + 1).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
            const istHeute = d === heuteISO;
            const istVergangen = d < heuteISO;
            const istAusgebucht = !ueberregelung && ausgebuchtTage.includes(d);
            const istAusgewaehlt = d === datum;
            // Im Override-Modus sind auch Vergangenheits-Tage klickbar.
            const istVergangenGesperrt = istVergangen && !ueberregelung;

            let cls = 'aspect-square text-xs rounded flex items-center justify-center transition-colors ';
            if (istAusgewaehlt) {
              cls += 'text-white font-bold cursor-pointer';
            } else if (istVergangenGesperrt) {
              cls += 'text-gray-300 cursor-not-allowed';
            } else if (istAusgebucht) {
              cls += 'bg-gray-100 text-gray-300 line-through cursor-not-allowed';
            } else if (istVergangen) {
              // Override aktiv: gedimmte, aber klickbare Vergangenheits-Tage
              cls += 'text-gray-400 italic hover:bg-fuchsia-50 cursor-pointer';
            } else {
              cls += 'text-gray-700 hover:bg-fuchsia-50 cursor-pointer';
            }
            const istKlickbar = !istVergangenGesperrt && !istAusgebucht;
            return (
              <button
                key={i}
                type="button"
                disabled={!istKlickbar}
                onClick={() => klickTag(tag)}
                className={cls}
                style={istAusgewaehlt
                  ? { backgroundColor: CI.brombeer }
                  : istHeute
                    ? { border: `1.5px solid ${CI.brombeer}` }
                    : {}}
                title={istAusgebucht ? 'Nicht verfügbar (Abwesenheit / Wochenende)' : (istVergangen ? 'Vergangenheit' : '')}
              >
                {tag}
              </button>
            );
          })}
        </div>
        {loadingMonat && (
          <div className="text-[10px] text-gray-400 italic text-center mt-2">Lade Monat…</div>
        )}
      </div>

      {/* Rechte Seite: Slot-Auswahl + Tages-Termin-Liste */}
      <div className="border border-gray-200 rounded-lg p-3 bg-white">
        {!datum ? (
          <div className="text-sm text-gray-400 italic text-center py-8">
            Tag im Kalender wählen, um freie Uhrzeiten zu sehen.
          </div>
        ) : loadingTag ? (
          <div className="text-sm text-gray-400 italic text-center py-8">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Lade verfügbare Slots…
          </div>
        ) : (
          <>
            <div className="text-sm font-bold text-gray-800 mb-2">
              {new Date(datum + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
            {ueberregelung && (
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-orange-600 bg-orange-50 border border-orange-200 rounded px-2 py-1">
                Override aktiv — alle Zeiten 07:00–21:00 wählbar
              </div>
            )}
            {slotsAnzeigen.length === 0 ? (
              <div className="text-xs text-gray-500 italic bg-gray-50 border border-gray-200 rounded p-3 text-center">
                Keine verfügbaren Zeiten an diesem Tag.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {slotsAnzeigen.map(slot => {
                  const istBelegt = busy.includes(slot);
                  const istAusgewaehlt = slot === uhrzeit;
                  const termin = terminProUhrzeit[slot];
                  // Im Override-Modus auch belegte Slots klickbar (Doppel-Termin)
                  const belegtGesperrt = istBelegt && !ueberregelung;
                  let cls = 'py-1.5 text-xs font-bold rounded border transition-colors ';
                  if (istAusgewaehlt) {
                    cls += 'text-white shadow-sm';
                  } else if (belegtGesperrt) {
                    cls += 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through';
                  } else if (istBelegt) {
                    // Override aktiv: gedimmt, aber klickbar — Hendrik sieht "war belegt"
                    cls += 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 cursor-pointer';
                  } else {
                    cls += 'bg-white text-gray-700 border-gray-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 cursor-pointer';
                  }
                  return (
                    <button
                      key={slot}
                      type="button"
                      disabled={belegtGesperrt}
                      onClick={() => klickSlot(slot)}
                      className={cls}
                      style={istAusgewaehlt ? { backgroundColor: CI.brombeer, borderColor: CI.brombeer } : {}}
                      title={istBelegt && termin ? `Belegt: ${termin.lastName || ''}, ${termin.firstName || ''}` : ''}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Tages-Termin-Liste fuer Kontext */}
            {tagesTermine.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                  Termine an diesem Tag ({tagesTermine.length})
                </div>
                <ul className="space-y-1">
                  {tagesTermine.map(t => (
                    <li key={t.id} className="text-xs flex items-center gap-2 text-gray-600">
                      <span className="font-mono font-bold text-gray-700 w-12">{t.time || '—'}</span>
                      <span
                        className="text-[9px] font-bold px-1 py-0.5 rounded text-white"
                        style={{ backgroundColor: t.type === 'video' ? CI.blue : CI.green }}
                      >
                        {t.type === 'video' ? 'Video' : 'vor Ort'}
                      </span>
                      <span className="truncate flex-1">{t.lastName}, {t.firstName}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ==========================================================================
// Stammdaten-Aufklapper — read-only Anzeige aller Kunden-Stammdaten +
// "Bearbeiten"-Button, der das bestehende Edit-Modal oeffnet.
// ==========================================================================
// Wandelt den Kunden-Dict in ein flaches, editierbares Draft-Objekt.
function _kundeZuDraft(kunde) {
  const r = kunde?.relative || {};
  return {
    firstName: kunde?.firstName || '', lastName: kunde?.lastName || '',
    salutation: kunde?.salutation || '', dob: kunde?.dob || '',
    phone: kunde?.phone || '', email: kunde?.email || '',
    street: kunde?.street || '', zip: kunde?.zip || '', city: kunde?.city || '',
    state: kunde?.state || '',
    insuranceType: kunde?.insuranceType || '', insuranceId: kunde?.insuranceId || '',
    insuranceNumber: kunde?.insuranceNumber || '',
    careLevel: (kunde?.careLevel != null ? String(kunde.careLevel) : ''),
    tour: kunde?.tour || '', partnerName: kunde?.partnerName || '',
    dauerhaftVideo: !!kunde?.dauerhaftVideo,
    rel: {
      salutation: r.salutation || '', firstName: r.firstName || '', lastName: r.lastName || '',
      relationship: r.relationship || '', phone: r.phone || '', email: r.email || '',
    },
  };
}
function _relHatDaten(rel) {
  return !!(rel && (rel.firstName || rel.lastName || rel.phone || rel.email));
}

function StammdatenBlock({ kunde, onEdit, pflegekassen, kundeId, onSaved }) {
  const [offen, setOffen] = useState(false);
  const baseline = useMemo(() => _kundeZuDraft(kunde), [kunde]);
  const [draft, setDraft] = useState(baseline);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState(null);

  // Bei Kundenwechsel / nach dem Speichern (kunde-Prop aendert sich) reset.
  useEffect(() => { setDraft(_kundeZuDraft(kunde)); setFehler(null); }, [kunde]);

  if (!kunde) return null;

  const set = (feld, wert) => setDraft(d => ({ ...d, [feld]: wert }));
  const setRel = (feld, wert) => setDraft(d => ({ ...d, rel: { ...d.rel, [feld]: wert } }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);

  const speichern = async () => {
    if (!kundeId) { setFehler('Kunden-ID nicht auflösbar.'); return; }
    setSaving(true); setFehler(null);
    try {
      const body = {
        vorname: draft.firstName, nachname: draft.lastName, anrede: draft.salutation || null,
        geburtsdatum: draft.dob || null, telefon: draft.phone, email: draft.email,
        strasse: draft.street, plz: draft.zip, ort: draft.city, bundesland: draft.state,
        versicherungsart: draft.insuranceType || null,
        versichertennummer: draft.insuranceNumber,
        pflegekasse_id: draft.insuranceId || null,
        pflegegrad: draft.careLevel === '' ? null : Number(draft.careLevel),
        tour: draft.tour, partner_name: draft.partnerName, dauerhaft_video: !!draft.dauerhaftVideo,
      };
      if (_relHatDaten(draft.rel)) {
        body.angehoeriger = {
          vorname: draft.rel.firstName, nachname: draft.rel.lastName,
          anrede: draft.rel.salutation || null, verwandtschaft: draft.rel.relationship || null,
          telefon: draft.rel.phone, email: draft.rel.email,
        };
      } else if (kunde.relative) {
        body.angehoeriger = {};   // Angehoerigen entfernt -> Backend loescht primaeren
      }
      const neu = await api.put(`/api/kunden/${kundeId}`, body);
      if (onSaved) onSaved(neu);
    } catch (e) {
      console.warn('Stammdaten inline speichern fehlgeschlagen', e);
      setFehler(e?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const verwerfen = () => { setDraft(baseline); setFehler(null); };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOffen(o => !o)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
          <ClipboardList className="w-4 h-4" style={{ color: CI.brombeer }} />
          Alle Stammdaten
          <span className="text-xs font-normal text-gray-400">— direkt bearbeitbar</span>
          {dirty && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">geändert</span>}
        </div>
        {offen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {offen && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100 space-y-5">
          <Sektion icon={User} titel="Person">
            <ESelect label="Anrede" value={draft.salutation} onChange={v => set('salutation', v)}
              optionen={[['', '—'], ['Frau', 'Frau'], ['Herr', 'Herr'], ['Divers', 'Divers']]} />
            <EFeld label="Vorname" value={draft.firstName} onChange={v => set('firstName', v)} />
            <EFeld label="Nachname" value={draft.lastName} onChange={v => set('lastName', v)} />
            <EFeld label="Geburtsdatum" type="date" value={draft.dob} onChange={v => set('dob', v)} />
          </Sektion>
          <Sektion icon={Phone} titel="Kontakt">
            <EFeld label="Telefon" value={draft.phone} onChange={v => set('phone', v)} />
            <EFeld label="E-Mail" type="email" value={draft.email} onChange={v => set('email', v)} />
          </Sektion>
          <Sektion icon={MapPin} titel="Adresse">
            <EFeld label="Straße & Hausnummer" value={draft.street} onChange={v => set('street', v)} />
            <EFeld label="PLZ" value={draft.zip} onChange={v => set('zip', v)} />
            <EFeld label="Ort" value={draft.city} onChange={v => set('city', v)} />
            <EFeld label="Bundesland" value={draft.state} onChange={v => set('state', v)} />
          </Sektion>
          <Sektion icon={Shield} titel="Versicherung & Pflege">
            <ESelect label="Versicherungsart" value={draft.insuranceType} onChange={v => set('insuranceType', v)}
              optionen={[['', '—'], ['GKV', 'Gesetzlich (GKV)'], ['PKV', 'Privat (PKV)']]} />
            <ESelect label="Pflegekasse" value={draft.insuranceId} onChange={v => set('insuranceId', v)}
              optionen={[['', '— bitte wählen —'], ...(pflegekassen || []).map(pk => [pk.id, pk.name])]} />
            <EFeld label="Versichertennummer" value={draft.insuranceNumber} onChange={v => set('insuranceNumber', v)} />
            <ESelect label="Pflegegrad" value={draft.careLevel} onChange={v => set('careLevel', v)}
              optionen={[['', '—'], ['0', 'kein'], ['1', '1'], ['2', '2'], ['3', '3'], ['4', '4'], ['5', '5']]} />
          </Sektion>
          <Sektion icon={Activity} titel="Organisation">
            <EFeld label="Tour / Route" value={draft.tour} onChange={v => set('tour', v)} />
            <EFeld label="Partner / Berater-Zuordnung" value={draft.partnerName} onChange={v => set('partnerName', v)} />
            <div className="text-sm">
              <label className="text-gray-500 block text-xs mb-0.5">Dauerhaft per Video</label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.dauerhaftVideo} onChange={e => set('dauerhaftVideo', e.target.checked)} />
                <span className="text-gray-800 font-medium">{draft.dauerhaftVideo ? 'Ja' : 'Nein'}</span>
              </label>
            </div>
          </Sektion>
          <Sektion icon={Users} titel="Angehörige / pflegende Kontaktperson">
            <ESelect label="Verwandtschaft" value={draft.rel.relationship} onChange={v => setRel('relationship', v)}
              optionen={[['', '—'], ...VERWANDTSCHAFT_GRUPPEN.flatMap(g => g.optionen.map(o => [o, o]))]} />
            <ESelect label="Anrede" value={draft.rel.salutation} onChange={v => setRel('salutation', v)}
              optionen={[['', '—'], ['Frau', 'Frau'], ['Herr', 'Herr'], ['Divers', 'Divers']]} />
            <EFeld label="Vorname" value={draft.rel.firstName} onChange={v => setRel('firstName', v)} />
            <EFeld label="Nachname" value={draft.rel.lastName} onChange={v => setRel('lastName', v)} />
            <EFeld label="Telefon" value={draft.rel.phone} onChange={v => setRel('phone', v)} />
            <EFeld label="E-Mail" type="email" value={draft.rel.email} onChange={v => setRel('email', v)} />
          </Sektion>
          {!_relHatDaten(draft.rel) && (
            <div className="text-xs text-gray-400 italic -mt-2 pl-5">Keine Angehörige erfasst — Felder oben ausfüllen, um eine Kontaktperson anzulegen.</div>
          )}

          {fehler && <div className="text-sm text-red-600">{fehler}</div>}

          {/* Speichern/Verwerfen erscheint nur bei Aenderungen. Sticky, damit
              es beim langen Block immer sichtbar bleibt. */}
          {dirty ? (
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-gray-100 flex items-center justify-end gap-2">
              <span className="text-xs text-amber-700 mr-auto flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Ungespeicherte Änderungen</span>
              <button onClick={verwerfen} disabled={saving} className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg">Verwerfen</button>
              <button onClick={speichern} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-50" style={{ background: CI.brombeer }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Änderungen speichern
              </button>
            </div>
          ) : (
            <div className="flex justify-end pt-1">
              <button onClick={onEdit} className="text-xs text-gray-400 hover:text-brombeer hover:underline">
                Erweitertes Bearbeiten (Modal)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Inline-Textfeld im Stammdaten-Block (kompakt, mit dezenter Umrandung).
function EFeld({ label, value, onChange, type = 'text' }) {
  return (
    <div className="text-sm">
      <label className="text-gray-500 block text-xs mb-0.5">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-md px-2 py-1 text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-brombeer focus:border-transparent"
      />
    </div>
  );
}

// Inline-Auswahl im Stammdaten-Block. optionen = [[value, label], ...].
function ESelect({ label, value, onChange, optionen }) {
  return (
    <div className="text-sm">
      <label className="text-gray-500 block text-xs mb-0.5">{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-md px-2 py-1 text-gray-800 font-medium bg-white focus:outline-none focus:ring-2 focus:ring-brombeer focus:border-transparent"
      >
        {optionen.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Sektion({ icon: Icon, titel, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: CI.brombeer }} />
        {titel}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 pl-5">
        {children}
      </div>
    </div>
  );
}

function Feld({ label, wert, href }) {
  return (
    <div className="text-sm">
      <span className="text-gray-500">{label}: </span>
      {href ? (
        <a
          href={href}
          className="text-brombeer font-medium hover:underline"
          style={{ color: CI.brombeer }}
        >
          {wert}
        </a>
      ) : (
        <span className="text-gray-800 font-medium">{wert}</span>
      )}
    </div>
  );
}


// ==========================================================================
// Notizen-Aufklapper — termin-unabhaengige Notizen zum Kunden, lesen
// und direkt editieren ohne Umweg ueber das Stammdaten-Modal.
// ==========================================================================
function HistorieBlock({ kundeId }) {
  const [offen, setOffen] = useState(false);
  const [eintraege, setEintraege] = useState(null);   // null = noch nicht geladen
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);

  // Bei Kundenwechsel Zustand zuruecksetzen (lazy: erst beim Aufklappen laden).
  useEffect(() => { setOffen(false); setEintraege(null); setFehler(null); }, [kundeId]);

  const laden = async () => {
    if (!kundeId) return;
    setLaedt(true); setFehler(null);
    try {
      const daten = await api.get(`/api/kunden/${kundeId}/historie`);
      setEintraege(Array.isArray(daten) ? daten : []);
    } catch (e) {
      console.warn('Stammdatenhistorie laden fehlgeschlagen', e);
      setFehler('Historie konnte nicht geladen werden.');
      setEintraege([]);
    } finally { setLaedt(false); }
  };

  const toggle = () => {
    const neu = !offen;
    setOffen(neu);
    if (neu && eintraege === null) laden();
  };

  const fmtDatum = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr';
    } catch { return iso; }
  };
  const artLabel = (a) => a === 'zusammenfuehrung' ? 'Zusammenführung'
    : a === 'system' ? 'automatisch' : 'manuelle Bearbeitung';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={toggle}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
          <History className="w-4 h-4" style={{ color: CI.brombeer }} />
          Stammdatenhistorie
          <span className="text-xs font-normal text-gray-400">— wer hat wann was geändert</span>
        </div>
        {offen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {offen && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          {laedt && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
            </div>
          )}
          {fehler && <div className="text-sm text-red-600 py-2">{fehler}</div>}
          {!laedt && !fehler && eintraege && eintraege.length === 0 && (
            <div className="text-sm text-gray-400 italic py-2">
              Noch keine Änderungen aufgezeichnet. Ab jetzt wird jede Bearbeitung der Stammdaten hier festgehalten.
            </div>
          )}
          {!laedt && eintraege && eintraege.length > 0 && (
            <div className="divide-y divide-gray-100">
              {eintraege.map((e) => (
                <div key={e.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-800">{e.feld}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDatum(e.zeitpunkt)}</span>
                  </div>
                  <div className="mt-1 text-gray-700 break-words">
                    <span className="line-through text-gray-400">{e.altWert || '—'}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-medium">{e.neuWert || '—'}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {e.benutzerName ? `von ${e.benutzerName}` : 'System'} · {artLabel(e.art)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function _fmtDatumKurz(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' });
  } catch { return iso; }
}

// Gespraechshistorie (Vermerke) in der Akte — lazy geladen, mit Hinzufuegen/Loeschen.
function GespraechsHistorieBlock({ kundeId }) {
  const [offen, setOffen] = useState(false);
  const [eintraege, setEintraege] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [neuText, setNeuText] = useState('');
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => { setOffen(false); setEintraege(null); setFehler(null); setNeuText(''); }, [kundeId]);

  const laden = async () => {
    if (!kundeId) return;
    setLaedt(true); setFehler(null);
    try {
      const daten = await api.get(`/api/kunden/${kundeId}/vermerke`);
      setEintraege(Array.isArray(daten) ? daten : []);
    } catch (e) {
      console.warn('Gespraechshistorie laden fehlgeschlagen', e);
      setFehler('Gesprächshistorie konnte nicht geladen werden.');
      setEintraege([]);
    } finally { setLaedt(false); }
  };
  const toggle = () => { const n = !offen; setOffen(n); if (n && eintraege === null) laden(); };

  const hinzufuegen = async () => {
    const txt = neuText.trim();
    if (!txt || !kundeId) return;
    setSpeichert(true);
    try {
      await api.post(`/api/kunden/${kundeId}/vermerke`, { text: txt, quelle: 'manuell' });
      setNeuText('');
      await laden();
    } catch (e) {
      console.warn('Vermerk speichern fehlgeschlagen', e);
      alert(e?.detail || 'Vermerk konnte nicht gespeichert werden.');
    } finally { setSpeichert(false); }
  };

  const loeschen = async (id) => {
    if (!window.confirm('Diesen Gesprächsvermerk löschen?')) return;
    try {
      await api.del(`/api/kunden/${kundeId}/vermerke/${id}`);
      await laden();
    } catch (e) {
      console.warn('Vermerk loeschen fehlgeschlagen', e);
      alert('Löschen fehlgeschlagen.');
    }
  };

  const fmt = (iso) => { if (!iso) return ''; try { const d = new Date(iso); return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr'; } catch { return iso; } };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button onClick={toggle} className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
          <MessageSquarePlus className="w-4 h-4" style={{ color: CI.brombeer }} />
          Gesprächshistorie
          {eintraege && eintraege.length > 0 && <span className="text-xs font-normal text-gray-500">— {eintraege.length} Vermerk{eintraege.length === 1 ? '' : 'e'}</span>}
        </div>
        {offen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {offen && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          {laedt && <div className="flex items-center gap-2 text-sm text-gray-500 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>}
          {fehler && <div className="text-sm text-red-600 py-2">{fehler}</div>}
          {!laedt && eintraege && eintraege.length === 0 && (
            <div className="text-sm text-gray-400 italic py-2">Noch keine Gesprächsvermerke. Entstehen bei der Termin-Nachbereitung oder direkt hier.</div>
          )}
          {!laedt && eintraege && eintraege.length > 0 && (
            <div className="space-y-3 mb-4">
              {eintraege.map(e => (
                <div key={e.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs text-gray-500">
                      {fmt(e.zeitpunkt)}{e.erstelltVon ? ` · ${e.erstelltVon}` : ''}
                      {e.quelle === 'sprache' && <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold">Sprache</span>}
                    </span>
                    <button onClick={() => loeschen(e.id)} className="text-gray-300 hover:text-red-600" title="Vermerk löschen"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{e.text}</div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-gray-100 pt-3">
            <textarea
              value={neuText}
              onChange={(e) => setNeuText(e.target.value)}
              placeholder="Neuen Gesprächsvermerk schreiben…"
              rows={2}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brombeer"
            />
            <div className="flex justify-end mt-2">
              <button onClick={hinzufuegen} disabled={speichert || !neuText.trim()} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40" style={{ background: CI.brombeer }}>
                {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Vermerk speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Startseiten-Assistent: vergangene Termine, die noch auf Nachbereitung warten.
function OffeneNachbereitung({ refreshKey, onNachbereiten }) {
  const [termine, setTermine] = useState([]);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      setLaedt(true);
      try {
        const daten = await api.get('/api/termine/nachbereitung/offen?limit=50');
        if (aktiv) setTermine(Array.isArray(daten) ? daten : []);
      } catch (e) {
        console.warn('Offene Nachbereitung laden fehlgeschlagen', e);
        if (aktiv) setTermine([]);
      } finally {
        if (aktiv) setLaedt(false);
      }
    })();
    return () => { aktiv = false; };
  }, [refreshKey]);

  if (laedt || termine.length === 0) return null;   // still bleiben, wenn nichts offen ist

  return (
    <div className="mb-6 bg-white rounded-xl shadow-sm border-2 overflow-hidden" style={{ borderColor: CI.brombeer }}>
      <div className="px-5 py-3 flex items-center gap-2" style={{ background: '#fdf4fb' }}>
        <ClipboardList className="w-5 h-5" style={{ color: CI.brombeer }} />
        <span className="font-bold text-gray-800">Nachbereitung offen</span>
        <span className="text-sm text-gray-500">— {termine.length} Termin{termine.length === 1 ? '' : 'e'} warten auf deine Rückmeldung</span>
      </div>
      <ul className="divide-y divide-gray-100 max-h-72 overflow-auto">
        {termine.map(t => (
          <li key={t.id} className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm hover:bg-gray-50">
            <div className="min-w-0">
              <span className="font-semibold text-gray-800">{t.name}</span>
              <span className="text-gray-500 ml-2">{_fmtDatumKurz(t.date)}{t.time ? `, ${t.time}` : ''}</span>
              <span className="ml-2 text-xs px-2 py-0.5 rounded text-white" style={{ backgroundColor: t.type === 'video' ? CI.blue : CI.green }}>{t.type === 'video' ? 'Video' : 'Hausbesuch'}</span>
            </div>
            <button onClick={() => onNachbereiten({ id: t.id, name: t.name, date: t.date, time: t.time, type: t.type })} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md text-white shrink-0" style={{ background: CI.brombeer }}>
              Nachbereiten
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Modal fuer die Termin-Nachbereitung (Status + Vermerk + Aufgaben + Folgetermin).
function NachbereitungModal({ termin, onClose, onSaved }) {
  const [status, setStatus] = useState(null);
  const [vermerk, setVermerk] = useState('');
  const [aufgabenText, setAufgabenText] = useState('');
  const [folgetermin, setFolgetermin] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  // KI-Assistent (Sprachnotiz + Ausformulieren)
  const [aufnahme, setAufnahme] = useState(false);      // laeuft die Aufnahme?
  const [kiLaeuft, setKiLaeuft] = useState(false);      // Transkription/Formulierung laeuft?
  const [kiStatus, setKiStatus] = useState('');         // Statuszeile fuer den Nutzer
  const [kiFehler, setKiFehler] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const STATUS_OPTIONEN = [
    { key: 'durchgefuehrt', label: 'Durchgeführt', farbe: CI.green },
    { key: 'no_show', label: 'No-Show', farbe: CI.red },
    { key: 'verschoben', label: 'Verschoben', farbe: '#D97706' },
    { key: 'abgesagt', label: 'Abgesagt', farbe: '#6B7280' },
  ];

  // Entwurf aus dem Assistenten in die Felder uebernehmen (immer nur Entwurf!).
  const entwurfUebernehmen = (d) => {
    if (d.vermerk) setVermerk(prev => prev.trim() ? (prev.trim() + '\n\n' + d.vermerk) : d.vermerk);
    const zeilen = [...(d.aufgaben || []), ...((d.fristen || []).map(f => `Frist: ${f}`))];
    if (zeilen.length) setAufgabenText(prev => (prev.trim() ? prev.trim() + '\n' : '') + zeilen.join('\n'));
    if (d.folgetermin) setFolgetermin(true);
  };

  const stopStream = () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch { /* egal */ }
    streamRef.current = null;
  };

  const aufnahmeStarten = async () => {
    setKiFehler(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setKiFehler('Dieser Browser unterstützt keine Aufnahme. Tipp: Text unten eintippen und „Ausformulieren" nutzen.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (!blob.size) { setKiFehler('Keine Aufnahme erkannt.'); return; }
        setKiLaeuft(true); setKiStatus('Sprache wird umgewandelt…');
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
          fd.append('audio', blob, `aufnahme.${ext}`);
          fd.append('kontext', `Beratungstermin am ${termin.date || ''}`);
          const d = await api.postForm('/api/assistent/sprachnotiz', fd);
          entwurfUebernehmen(d);
          setKiStatus('Entwurf erstellt — bitte prüfen und ggf. korrigieren.');
        } catch (e) {
          console.warn('Sprachnotiz fehlgeschlagen', e);
          setKiFehler(e?.detail || 'Sprachnotiz fehlgeschlagen.');
          setKiStatus('');
        } finally { setKiLaeuft(false); }
      };
      rec.start();
      recorderRef.current = rec;
      setAufnahme(true);
      setKiStatus('Aufnahme läuft… sprich frei, dann auf „Stopp".');
    } catch (e) {
      console.warn('Mikrofon-Zugriff fehlgeschlagen', e);
      setKiFehler('Kein Mikrofon-Zugriff. Bitte im Browser die Erlaubnis erteilen.');
    }
  };

  const aufnahmeStoppen = () => {
    setAufnahme(false);
    try { recorderRef.current?.stop(); } catch { /* egal */ }
  };

  const ausformulieren = async () => {
    if (!vermerk.trim()) { setKiFehler('Bitte erst ein paar Stichworte eintippen.'); return; }
    setKiFehler(null); setKiLaeuft(true); setKiStatus('Wird ausformuliert…');
    try {
      const d = await api.post('/api/assistent/ausformulieren', {
        text: vermerk, kontext: `Beratungstermin am ${termin.date || ''}`,
      });
      // Beim Ausformulieren ersetzen wir den Rohtext durch die saubere Fassung.
      setVermerk(d.vermerk || vermerk);
      const zeilen = [...(d.aufgaben || []), ...((d.fristen || []).map(f => `Frist: ${f}`))];
      if (zeilen.length) setAufgabenText(prev => (prev.trim() ? prev.trim() + '\n' : '') + zeilen.join('\n'));
      if (d.folgetermin) setFolgetermin(true);
      setKiStatus('Ausformuliert — bitte prüfen.');
    } catch (e) {
      console.warn('Ausformulieren fehlgeschlagen', e);
      setKiFehler(e?.detail || 'Ausformulieren fehlgeschlagen.');
      setKiStatus('');
    } finally { setKiLaeuft(false); }
  };

  useEffect(() => () => { stopStream(); }, []);   // beim Schliessen Mikrofon freigeben

  const speichern = async () => {
    if (!status) return;
    setSpeichert(true);
    try {
      const aufgaben = aufgabenText.split('\n').map(z => z.trim()).filter(Boolean);
      await api.post(`/api/termine/${termin.id}/nachbereitung`, {
        status,
        vermerk_text: vermerk.trim() || null,
        aufgaben,
        folgetermin_noetig: folgetermin,
      });
      if (onSaved) onSaved();
    } catch (e) {
      console.warn('Nachbereitung speichern fehlgeschlagen', e);
      alert(e?.detail || 'Nachbereitung konnte nicht gespeichert werden.');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Termin nachbereiten</h3>
            <p className="text-sm text-gray-500">{termin.name} · {_fmtDatumKurz(termin.date)}{termin.time ? `, ${termin.time}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <div className="text-sm font-bold text-gray-700 mb-2">Was ist mit dem Termin passiert?</div>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONEN.map(o => (
                <button key={o.key} onClick={() => setStatus(o.key)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-bold border-2 transition-all ${status === o.key ? 'text-white' : 'bg-white'}`}
                  style={status === o.key ? { background: o.farbe, borderColor: o.farbe } : { borderColor: o.farbe, color: o.farbe }}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {status === 'no_show' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Für No-Shows kannst du im Mail-Verlauf einen freundlichen „Melden Sie sich gern"-Entwurf erstellen (kommt als eigener Schritt). Es wird nichts automatisch versendet.</span>
            </div>
          )}

          <div>
            <label className="text-sm font-bold text-gray-700">Gesprächsvermerk <span className="font-normal text-gray-400">(optional)</span></label>

            {/* KI-Assistent: Sprachnotiz + Ausformulieren */}
            <div className="mt-1 mb-2 p-2 rounded-lg border border-fuchsia-100 bg-fuchsia-50/50">
              <div className="flex flex-wrap items-center gap-2">
                {!aufnahme ? (
                  <button type="button" onClick={aufnahmeStarten} disabled={kiLaeuft}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md text-white disabled:opacity-40" style={{ background: CI.brombeer }}>
                    <Mic className="w-3.5 h-3.5" /> Sprachnotiz aufnehmen
                  </button>
                ) : (
                  <button type="button" onClick={aufnahmeStoppen}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md text-white animate-pulse" style={{ background: CI.red }}>
                    <Square className="w-3.5 h-3.5" /> Aufnahme stoppen
                  </button>
                )}
                <button type="button" onClick={ausformulieren} disabled={kiLaeuft || aufnahme || !vermerk.trim()}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md border border-fuchsia-200 disabled:opacity-40" style={{ color: CI.brombeer }}>
                  {kiLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />} Aus Stichworten ausformulieren
                </button>
                {kiStatus && <span className="text-xs text-gray-500">{kiStatus}</span>}
              </div>
              {kiFehler && <div className="mt-1 text-xs text-red-600">{kiFehler}</div>}
              <p className="mt-1 text-[11px] text-gray-400">Der Assistent erstellt nur einen Entwurf aus deinen Angaben — du prüfst und korrigierst, bevor gespeichert wird. Die Aufnahme wird nach der Umwandlung nicht gespeichert.</p>
            </div>

            <textarea value={vermerk} onChange={(e) => setVermerk(e.target.value)} rows={5}
              placeholder="Was wurde besprochen? Gab es Besonderheiten? … (oder oben aufnehmen/ausformulieren lassen)"
              className="mt-1 w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brombeer" />
            <p className="text-xs text-gray-400 mt-1">Wird als datierter Vermerk in der Gesprächshistorie gespeichert.</p>
          </div>

          <div>
            <label className="text-sm font-bold text-gray-700">Nächste Schritte / Aufgaben <span className="font-normal text-gray-400">(eine pro Zeile, optional)</span></label>
            <textarea value={aufgabenText} onChange={(e) => setAufgabenText(e.target.value)} rows={2}
              placeholder="z.B. Pflegehilfsmittel-Antrag zuschicken"
              className="mt-1 w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brombeer" />
            <p className="text-xs text-gray-400 mt-1">Jede Zeile wird als To-do zu diesem Kunden angelegt.</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={folgetermin} onChange={(e) => setFolgetermin(e.target.checked)} />
            Folgetermin nötig — legt ein To-do „Folgetermin vereinbaren" an
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg">Später</button>
          <button onClick={speichern} disabled={!status || speichert}
            className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40" style={{ background: CI.brombeer }}>
            {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Speichern
          </button>
        </div>
      </div>
    </div>
  );
}


function NotizenBlock({ kundeId, initialNotizen, onSaved }) {
  const [offen, setOffen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [text, setText] = useState(initialNotizen || '');
  const [isSaving, setIsSaving] = useState(false);
  const [fehler, setFehler] = useState(null);

  // Wenn der Kunde wechselt, lokalen Text zuruecksetzen
  useEffect(() => {
    setText(initialNotizen || '');
    setEditMode(false);
    setFehler(null);
  }, [kundeId, initialNotizen]);

  const speichern = async () => {
    if (!kundeId) return;
    setIsSaving(true);
    setFehler(null);
    try {
      await api.put(`/api/kunden/${kundeId}`, { notizen: text });
      setEditMode(false);
      if (onSaved) onSaved(text);
    } catch (e) {
      setFehler(e?.detail || 'Notiz konnte nicht gespeichert werden.');
    } finally {
      setIsSaving(false);
    }
  };

  const abbrechen = () => {
    setText(initialNotizen || '');
    setEditMode(false);
    setFehler(null);
  };

  const hatNotiz = (initialNotizen || '').trim().length > 0;
  const vorschau = (initialNotizen || '').trim().slice(0, 80);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOffen(o => !o)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
          <StickyNote className="w-4 h-4" style={{ color: CI.brombeer }} />
          Notizen zum Kunden
          {hatNotiz ? (
            <span className="text-xs font-normal text-gray-500">— {(initialNotizen || '').length} Zeichen{vorschau ? `: „${vorschau}${(initialNotizen || '').length > 80 ? '…' : ''}"` : ''}</span>
          ) : (
            <span className="text-xs font-normal text-gray-400 italic">— noch keine Eintragung</span>
          )}
        </div>
        {offen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {offen && (
        <div className="px-6 pb-6 pt-2 border-t border-gray-100">
          {!editMode ? (
            <div>
              {hatNotiz ? (
                <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-sm text-gray-800 whitespace-pre-wrap">
                  {initialNotizen}
                </div>
              ) : (
                <div className="text-sm text-gray-400 italic">Keine Notizen vorhanden.</div>
              )}
              <div className="flex justify-end mt-3">
                <button
                  onClick={() => setEditMode(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-bold border border-fuchsia-200 rounded-lg hover:bg-fuchsia-50"
                  style={{ color: CI.brombeer }}
                >
                  <Edit3 className="w-4 h-4" /> {hatNotiz ? 'Bearbeiten' : 'Notiz hinzufügen'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                placeholder="Freier Notiz-Text zum Kunden — unabhängig von einem Termin."
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-200 focus:outline-none"
              />
              {fehler && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-800">{fehler}</div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={abbrechen}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-bold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={speichern}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white rounded-lg shadow-sm disabled:opacity-50"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Speichern
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ==========================================================================
// ProtokollScansBlock — Upload + Liste der eingescannten / historischen
// Beratungsprotokolle, die vor Ort handschriftlich ausgefuellt wurden.
// Eintraege werden als KundeDokument mit kategorie='protokoll_scan'
// gespeichert; jedes hat optional ein Termindatum vom Papier zum Sortieren.
// ==========================================================================
function ProtokollScansBlock({ kundeId, onVersenden, refreshKey = 0 }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hochladen, setHochladen] = useState(false);
  const [scanDatum, setScanDatum] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [fehler, setFehler] = useState(null);
  const inputRef = useRef(null);

  const reload = async () => {
    if (!kundeId) { setScans([]); return; }
    setLoading(true);
    try {
      const liste = await api.get(`/api/kunden/${encodeURIComponent(kundeId)}/dokumente?kategorie=protokoll_scan`);
      const sortiert = (liste || []).slice().sort((a, b) => {
        const dA = a.dokument_datum || '';
        const dB = b.dokument_datum || '';
        if (dA && dB) return dB.localeCompare(dA);
        if (dA) return -1;
        if (dB) return 1;
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
      setScans(sortiert);
    } catch (_) {
      setScans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [kundeId, refreshKey]);

  const upload = async (datei) => {
    if (!datei || !kundeId) return;
    const erlaubteTypen = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!erlaubteTypen.includes((datei.type || '').toLowerCase())) {
      setFehler(`Dateityp nicht erlaubt: ${datei.type || 'unbekannt'}. Erlaubt: PDF, JPG, PNG, HEIC, DOCX.`);
      return;
    }
    if (datei.size > 20 * 1024 * 1024) {
      setFehler(`Datei zu groß (${(datei.size / 1024 / 1024).toFixed(1)} MB). Maximal 20 MB.`);
      return;
    }
    setFehler(null);
    setHochladen(true);
    const fd = new FormData();
    fd.append('kategorie', 'protokoll_scan');
    fd.append('beschreibung', '');
    if (scanDatum) fd.append('dokument_datum', scanDatum);
    fd.append('datei', datei);
    try {
      const res = await fetch(`/api/kunden/${encodeURIComponent(kundeId)}/dokumente`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setScanDatum('');
      await reload();
    } catch (err) {
      setFehler('Upload fehlgeschlagen: ' + (err.message || 'unbekannt'));
    } finally {
      setHochladen(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eingescanntes Protokoll wirklich löschen?')) return;
    try {
      await api.del(`/api/dokumente/${id}`);
      await reload();
    } catch (e) {
      setFehler('Löschen fehlgeschlagen: ' + (e?.detail || e?.message || 'unbekannt'));
    }
  };

  const handleZurAbrechnung = async (id) => {
    try {
      await api.post(`/api/dokumente/${id}/zur-abrechnung`, {});
      await reload();
    } catch (e) {
      setFehler('Markieren fehlgeschlagen: ' + (e?.detail || e?.message || 'unbekannt'));
    }
  };

  const handleZurueckAusAbrechnung = async (id) => {
    if (!window.confirm('Scan aus der AS-Bremen-Warteschlange zurueckziehen?')) return;
    try {
      await api.del(`/api/dokumente/${id}/zur-abrechnung`);
      await reload();
    } catch (e) {
      setFehler('Zurueckziehen fehlgeschlagen: ' + (e?.detail || e?.message || 'unbekannt'));
    }
  };

  return (
    <div className="mb-6 p-4 bg-amber-50/40 border border-amber-100 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <UploadCloud className="w-4 h-4" style={{ color: CI.brombeer }} />
        <label className="text-sm font-bold text-gray-800">
          Eingescannte / historische Protokolle
        </label>
        <span className="text-xs font-normal text-gray-500">
          — vor Ort unterschrieben oder aus früheren Beratungen
        </span>
      </div>

      {kundeId ? (
        <>
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end mb-3">
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">Termindatum (vom Papier)</label>
              <input
                type="date"
                value={scanDatum}
                onChange={(e) => setScanDatum(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              />
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                const datei = e.dataTransfer.files?.[0];
                if (datei) upload(datei);
              }}
              className={`flex-1 border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-fuchsia-400 bg-fuchsia-50' : 'border-gray-300 bg-white hover:bg-gray-50'
              }`}
              onClick={() => inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,application/pdf,image/jpeg,image/png,image/heic,image/heif,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => upload(e.target.files?.[0])}
                className="hidden"
              />
              {hochladen ? (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" /> Lade hoch…
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  <UploadCloud className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                  Datei hierher ziehen oder <span className="font-bold text-fuchsia-700">klicken</span>
                  <div className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG, HEIC, DOCX — max. 20 MB</div>
                </div>
              )}
            </div>
          </div>

          {fehler && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-800">{fehler}</div>
          )}

          {loading && scans.length === 0 ? (
            <div className="text-xs text-gray-400 italic px-2 py-2"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> lade…</div>
          ) : scans.length === 0 ? (
            <div className="text-xs text-gray-500 italic px-2 py-2">Noch keine eingescannten Protokolle für diesen Kunden.</div>
          ) : (
            <ul className="space-y-1.5">
              {scans.map(d => (
                <li key={d.id} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-gray-300">
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-xs font-bold text-gray-700 flex-shrink-0 w-24">
                    {d.dokument_datum
                      ? new Date(d.dokument_datum).toLocaleDateString('de-DE')
                      : <span className="text-gray-300 italic">ohne Datum</span>}
                  </span>
                  <a
                    href={`/api/dokumente/${d.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 text-sm font-medium text-gray-800 hover:text-fuchsia-700 truncate"
                    title={d.dateiname_anzeige}
                  >
                    {d.dateiname_anzeige}
                  </a>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">
                    {d.groesse_bytes ? `${(d.groesse_bytes / 1024).toFixed(0)} KB` : ''}
                  </span>
                  {d.abrechnung_status === 'offen' && (
                    <button
                      onClick={() => handleZurAbrechnung(d.id)}
                      className="flex items-center gap-1 text-[11px] font-bold px-2 py-1 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded-md border border-emerald-200"
                      title="Scan zur AS-Bremen-Sammelmail markieren"
                    >
                      <Wallet className="w-3 h-3" /> Abrechnen
                    </button>
                  )}
                  {d.abrechnung_status === 'wartend' && (
                    <button
                      onClick={() => handleZurueckAusAbrechnung(d.id)}
                      className="flex items-center gap-1 text-[11px] font-bold px-2 py-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-200 rounded-md border border-emerald-300"
                      title="Markiert — klicken um zurueckzuziehen"
                    >
                      <Wallet className="w-3 h-3" /> in Warteschlange
                    </button>
                  )}
                  {d.abrechnung_status === 'versendet' && (
                    <span
                      className="flex items-center gap-1 text-[11px] font-bold px-2 py-1 bg-slate-100 text-slate-700 rounded-md border border-slate-300"
                      title={`Versendet am ${d.abrechnung_versendet_am ? new Date(d.abrechnung_versendet_am).toLocaleString('de-DE') : ''}`}
                    >
                      <CheckCircle className="w-3 h-3" /> AS Bremen
                    </span>
                  )}
                  {(d.mime_type || '').toLowerCase() === 'application/pdf' && onVersenden && (
                    d.mail_versendet_am ? (
                      <button
                        onClick={() => onVersenden(d.id)}
                        className="flex items-center gap-1 text-[11px] font-bold px-2 py-1 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-md border border-blue-200"
                        title={`Bereits an Kunde gesendet am ${new Date(d.mail_versendet_am).toLocaleString('de-DE')} — erneut senden`}
                      >
                        <Mail className="w-3 h-3" /> erneut senden
                      </button>
                    ) : (
                      <button
                        onClick={() => onVersenden(d.id)}
                        className="flex items-center gap-1 text-[11px] font-bold px-2 py-1 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100 rounded-md border border-fuchsia-200"
                        title="Verschluesselt an Patient / Angehoerigen senden"
                      >
                        <Mail className="w-3 h-3" /> Versenden
                      </button>
                    )
                  )}
                  {d.abrechnung_status !== 'versendet' && (
                    <button
                      onClick={() => handleDelete(d.id)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Löschen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="text-xs text-gray-500 italic">Bitte erst einen Kunden auswählen.</div>
      )}
    </div>
  );
}


// ==========================================================================
// Tour-Zuweisungs-Block — zeigt aktive Tour-Zuordnung des Kunden + Historie,
// erlaubt Anlegen / Aendern / Loeschen direkt aus der Akte heraus.
// ==========================================================================
function TourZuweisungBlock({ kundeId, clickupTourText, street, zip, city, lat, lng }) {
  const [zuweisungen, setZuweisungen] = useState([]);
  const [aktiveTouren, setAktiveTouren] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Google-Maps-Navigationslink zu dieser Kundenadresse (Koordinaten bevorzugt,
  // sonst die Anschrift). Fuer die Tour-Planung direkt aus der Akte heraus.
  const mapsZiel = (typeof lat === 'number' && typeof lng === 'number')
    ? `${lat},${lng}`
    : [street, zip, city].filter(Boolean).join(', ');
  const mapsUrl = mapsZiel
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsZiel)}`
    : null;

  const reload = async () => {
    if (!kundeId) { setIsLoading(false); return; }
    setIsLoading(true);
    setFehler(null);
    try {
      const [zw, tou] = await Promise.all([
        api.get(`/api/kunden/${kundeId}/touren`),
        api.get('/api/touren?modus=aktiv'),
      ]);
      setZuweisungen(zw || []);
      setAktiveTouren(tou || []);
    } catch (e) {
      setFehler(e?.detail || 'Tour-Daten konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [kundeId]);

  const aktiv = zuweisungen.find(z => z.aktiv);
  const historie = zuweisungen.filter(z => !z.aktiv);

  const handleAdd = async (tourId, status, uhrzeit) => {
    try {
      await api.post(`/api/touren/${tourId}/kunden`, {
        kunde_id: kundeId,
        status,
        uhrzeit: uhrzeit || null,
      });
      setShowModal(false);
      reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        alert(e.detail);
      } else {
        alert(e?.detail || 'Zuweisung fehlgeschlagen.');
      }
    }
  };

  const handleStatusToggle = async () => {
    if (!aktiv) return;
    const neuerStatus = aktiv.status === 'bestaetigt' ? 'planung' : 'bestaetigt';
    try {
      await api.put(`/api/tour-kunden/${aktiv.id}`, { status: neuerStatus });
      reload();
    } catch (e) {
      alert(e?.detail || 'Aenderung fehlgeschlagen.');
    }
  };

  const handleUhrzeitChange = async (uhrzeit) => {
    if (!aktiv) return;
    try {
      await api.put(`/api/tour-kunden/${aktiv.id}`, { uhrzeit: uhrzeit || null });
      reload();
    } catch (e) {
      alert(e?.detail || 'Uhrzeit konnte nicht gespeichert werden.');
    }
  };

  const handleEntfernen = async () => {
    if (!aktiv) return;
    if (!window.confirm(`Kunde wirklich von der Tour „${aktiv.tour_name}" entfernen?`)) return;
    try {
      await api.del(`/api/tour-kunden/${aktiv.id}`);
      reload();
    } catch (e) {
      alert(e?.detail || 'Entfernen fehlgeschlagen.');
    }
  };

  const formatTourDatum = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
          <MapPin className="w-4 h-4" style={{ color: CI.brombeer }} />
          Tour-Zuweisung
          {aktiv && (
            <span className="ml-2 text-xs font-normal text-gray-500">
              {aktiv.tour_name} &middot; {formatTourDatum(aktiv.tour_datum)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mapsUrl && (
            <a
              href={mapsUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
              title="Navigation zu dieser Adresse in Google Maps"
            >
              <MapPin className="w-3.5 h-3.5" /> Google Maps
            </a>
          )}
          {!aktiv && !isLoading && aktiveTouren.length > 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
              title="Mit genauer Uhrzeit zuweisen"
            >
              <Plus className="w-3.5 h-3.5" /> mit Uhrzeit…
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-4 space-y-3">
        {clickupTourText && (
          <div className="text-xs text-gray-500 italic flex items-center gap-1.5">
            <History className="w-3 h-3" />
            Aus früherem Import: <span className="font-medium text-gray-700">{clickupTourText}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
          </div>
        ) : fehler ? (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {fehler}
          </div>
        ) : aktiv ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                <CalendarIcon className="w-3 h-3" /> {formatTourDatum(aktiv.tour_datum)}
              </div>
              <div className="text-base font-bold text-gray-800">{aktiv.tour_name}</div>
              <div className="flex items-center gap-2 mt-1.5">
                {aktiv.status === 'bestaetigt' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">
                    <CheckCircle2 className="w-3 h-3" /> bestätigt
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold">
                    <Clock className="w-3 h-3" /> in Planung
                  </span>
                )}
                {aktiv.status === 'bestaetigt' && (
                  <input
                    type="time"
                    defaultValue={aktiv.uhrzeit || ''}
                    onBlur={(e) => {
                      if ((e.target.value || '') !== (aktiv.uhrzeit || '')) {
                        handleUhrzeitChange(e.target.value);
                      }
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm w-24 focus:ring-2 focus:ring-emerald-300"
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('tourplan:open', { detail: { tourId: aktiv.tour_id } }))}
                className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md text-white hover:opacity-90"
                style={{ backgroundColor: CI.brombeer }}
                title="In den Tourplan springen — Uhrzeiten und Reihenfolge bearbeiten"
              >
                <MapPin className="w-3.5 h-3.5" /> Tour öffnen
              </button>
              <button
                onClick={handleStatusToggle}
                className={`text-xs font-bold px-3 py-1.5 rounded-md border ${aktiv.status === 'bestaetigt'
                  ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}
              >
                {aktiv.status === 'bestaetigt' ? 'auf Planung zurück' : 'bestätigen'}
              </button>
              <button
                onClick={handleEntfernen}
                className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md"
                title="Von dieser Tour entfernen"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-gray-500 italic">Noch keiner aktiven Tour zugewiesen.</div>
            {aktiveTouren.length === 0 ? (
              <div className="text-xs text-gray-400">
                Lege erst eine Tour über Einstellungen → Tourplan an.
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-400">
                  Aktuelle Touren — direkt zuweisen
                </div>
                {aktiveTouren.map(t => (
                  <div key={t.id} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gray-800 truncate">{t.name}</div>
                      <div className="text-xs text-gray-500">{formatTourDatum(t.datum)}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleAdd(t.id, 'planung')}
                        className="text-xs font-bold px-2.5 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                      >+ Planung</button>
                      <button
                        onClick={() => handleAdd(t.id, 'bestaetigt')}
                        className="text-xs font-bold px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >+ Bestätigt</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {historie.length > 0 && (
          <div className="pt-2 mt-2 border-t border-gray-100">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5">
              <History className="w-3 h-3" /> Historie ({historie.length})
            </div>
            <ul className="space-y-1">
              {historie.slice(0, 5).map(h => (
                <li key={h.id} className="text-xs text-gray-600 flex items-center gap-2">
                  <span className="text-gray-400">{formatTourDatum(h.tour_datum)}</span>
                  <span className="font-medium">{h.tour_name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${h.status === 'bestaetigt' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {h.status === 'bestaetigt' ? 'bestätigt' : 'Planung'}
                  </span>
                  {h.uhrzeit && <span className="text-gray-400">{h.uhrzeit}</span>}
                </li>
              ))}
              {historie.length > 5 && (
                <li className="text-xs text-gray-400 italic">… und {historie.length - 5} weitere</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {showModal && (
        <TourAuswahlModal
          aktiveTouren={aktiveTouren}
          onClose={() => setShowModal(false)}
          onSelect={handleAdd}
        />
      )}
    </div>
  );
}


// --------------------------------------------------------------------------
// Modal: aktive Tour fuer einen Kunden auswaehlen + Status + Uhrzeit
// --------------------------------------------------------------------------
function TourAuswahlModal({ aktiveTouren, onClose, onSelect }) {
  const [tourId, setTourId] = useState('');
  const [status, setStatus] = useState('planung');
  const [uhrzeit, setUhrzeit] = useState('');
  const [busy, setBusy] = useState(false);

  const formatTourDatum = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!tourId) return;
    setBusy(true);
    await onSelect(tourId, status, status === 'bestaetigt' ? (uhrzeit || null) : null);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-slate-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="w-5 h-5" style={{ color: CI.brombeer }} /> Tour zuweisen
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tour</label>
            {aktiveTouren.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                Keine aktiven Touren vorhanden. Lege erst eine Tour über
                Einstellungen → Tourplan an.
              </div>
            ) : (
              <select
                value={tourId}
                onChange={(e) => setTourId(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300 bg-white"
              >
                <option value="">— bitte wählen —</option>
                {aktiveTouren.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {formatTourDatum(t.datum)}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <div className="flex gap-2">
              <label className={`flex-1 cursor-pointer px-3 py-2 rounded-lg border text-sm font-bold text-center ${status === 'planung' ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-gray-500 border-gray-300'}`}>
                <input type="radio" name="status" value="planung" checked={status === 'planung'} onChange={() => setStatus('planung')} className="sr-only" />
                Planung
              </label>
              <label className={`flex-1 cursor-pointer px-3 py-2 rounded-lg border text-sm font-bold text-center ${status === 'bestaetigt' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-white text-gray-500 border-gray-300'}`}>
                <input type="radio" name="status" value="bestaetigt" checked={status === 'bestaetigt'} onChange={() => setStatus('bestaetigt')} className="sr-only" />
                Bestätigt
              </label>
            </div>
          </div>
          {status === 'bestaetigt' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Uhrzeit (optional)</label>
              <input
                type="time"
                value={uhrzeit}
                onChange={(e) => setUhrzeit(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300"
              />
              <p className="text-xs text-gray-500 mt-1">Kannst du auch später in der Akte oder im Tourplan eintragen.</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-600 hover:text-gray-900">Abbrechen</button>
            <button
              type="submit"
              disabled={!tourId || busy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-bold disabled:opacity-50"
              style={{ backgroundColor: CI.brombeer }}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Zuweisen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================================================
// SichtBausteinReihen — zwei Reihen mit je 5 Stufen-Buttons + Toggle
// "Patient kann sich selbst aeussern".
//
// Kernverhalten:
//  - Klick fuegt einen personalisierten Text in die "Sicht der Beteiligten"-
//    Textarea ein. Patient-Sicht steht IMMER oben, Pflegeperson-Sicht unten.
//  - Wenn die Reihe schon einen Block beigesteuert hat (gemerkt in
//    letzterPatientBlock / letzterPflegepersonBlock), wird ein erneuter
//    Klick auf einen Button derselben Reihe den vorherigen Block ERSETZEN
//    statt anzuhaengen — der Berater muss also den falschen Klick nicht
//    mehr manuell aus dem Text loeschen.
//  - Toggle "Patient kann sich selbst aeussern" (Default: ja). Bei "nein"
//    wechselt die Patient-Reihe auf STUFEN_PATIENT_BEOBACHTET, in dem der
//    Angehoerige im Namen / aus seiner Wahrnehmung ueber den Patienten
//    spricht (wichtig bei Demenz, Schlaganfall, Kleinkindern, ...).
//  - Wenn keine Angehoerigen-Daten vorhanden sind, kommt beim ersten Klick
//    ein einmaliger Hinweis-Toast.
// ==========================================================================
function SichtBausteinReihen({
  kunde,
  currentText,
  letzterPatientBlock,
  letzterPflegepersonBlock,
  onSetzeText,
}) {
  const [patientKannAeussern, setPatientKannAeussern] = useState(true);
  const [hinweisGezeigt, setHinweisGezeigt] = useState(false);
  const [hinweisOffen, setHinweisOffen] = useState(false);
  const hatAngehoerigen = !!(kunde && kunde.relative && (kunde.relative.firstName || kunde.relative.relationship));

  // Kind-Modus: pflegebeduerftige Person unter 12 -> kindgerechte Texte
  // ("Zuhause ist okay, Mama kuemmert sich gut um mich"). Funktioniert
  // nur, wenn das Kind sich auch selbst aeussern kann; bei Saeuglingen +
  // schwerer Sprachstoerung greift weiter die Beobachtungs-Variante.
  const kindModus = istKind(kunde);
  const personalisierungsModus = kindModus && patientKannAeussern ? 'kind' : 'erwachsen';

  // Welche Patient-Templates werden gerade verwendet:
  //   - kind + kann aeussern  -> STUFEN_PATIENT_KIND
  //   - kann nicht aeussern   -> STUFEN_PATIENT_BEOBACHTET (auch bei Kindern)
  //   - sonst                  -> STUFEN_PATIENT (Standard, Erwachsene)
  let stufenPatient;
  if (!patientKannAeussern) stufenPatient = STUFEN_PATIENT_BEOBACHTET;
  else if (kindModus) stufenPatient = STUFEN_PATIENT_KIND;
  else stufenPatient = STUFEN_PATIENT;

  const handleKlick = (template, perspektive) => {
    if (!hatAngehoerigen && !hinweisGezeigt) {
      setHinweisOffen(true);
      setHinweisGezeigt(true);
      setTimeout(() => setHinweisOffen(false), 4000);
    }
    const neuerText = fuelleTemplate(template, kunde, personalisierungsModus);
    const text = currentText || '';
    const anderer = perspektive === 'patient' ? letzterPflegepersonBlock : letzterPatientBlock;
    const eigener = perspektive === 'patient' ? letzterPatientBlock : letzterPflegepersonBlock;

    let neuerVollText;
    // 1. Replace-Pfad: eigener letzter Block ist noch unveraendert im Text
    if (eigener && text.includes(eigener)) {
      neuerVollText = text.replace(eigener, neuerText);
    }
    // 2. Insert-Pfad mit Reihenfolge-Garantie
    else if (perspektive === 'patient') {
      // Patient kommt OBEN
      if (anderer && text.includes(anderer)) {
        neuerVollText = text.replace(anderer, neuerText + '\n\n' + anderer);
      } else if (text.trim() === '') {
        neuerVollText = neuerText;
      } else {
        neuerVollText = neuerText + '\n\n' + text;
      }
    } else {
      // Pflegeperson kommt UNTEN
      if (anderer && text.includes(anderer)) {
        neuerVollText = text.replace(anderer, anderer + '\n\n' + neuerText);
      } else if (text.trim() === '') {
        neuerVollText = neuerText;
      } else {
        neuerVollText = text + '\n\n' + neuerText;
      }
    }

    if (perspektive === 'patient') {
      onSetzeText(neuerVollText, neuerText, undefined);
    } else {
      onSetzeText(neuerVollText, undefined, neuerText);
    }
  };

  const Reihe = ({ titel, stufen, perspektive }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider w-32 flex-shrink-0">{titel}</span>
      {stufen.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => handleKlick(s.text, perspektive)}
          className="text-xs font-bold px-2.5 py-1 rounded-md text-white hover:opacity-90 transition-opacity"
          style={{ backgroundColor: s.farbe }}
          title={s.text.replace(/\{[a-z_]+\}/g, '…').slice(0, 200) + (s.text.length > 200 ? '…' : '')}
        >
          {i + 1}. {s.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="mb-3 space-y-2 bg-gradient-to-r from-fuchsia-50 to-pink-50 border border-fuchsia-100 rounded-lg p-3">
      {kindModus && patientKannAeussern && (
        <div className="ml-32 -mt-1 mb-1 text-[11px] text-blue-700 italic">
          Kindgerechte Sprache aktiv (Patient/in unter 12 Jahre alt) — z.B. „Mama" / „Papa" statt „meine Mutter".
        </div>
      )}
      <Reihe titel="Sicht Patient/in"   stufen={stufenPatient}      perspektive="patient" />
      {/* Toggle: Patient kann sich selbst aeussern */}
      <label className="flex items-center gap-2 ml-32 -mt-1 mb-1 text-xs text-gray-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!patientKannAeussern}
          onChange={(e) => setPatientKannAeussern(!e.target.checked)}
          className="rounded"
        />
        <span>
          Patient kann sich nicht selbst äußern (z.B. Demenz, Schlaganfall, Kleinkind) —
          <span className="italic"> Texte aus Sicht des Angehörigen formulieren</span>
        </span>
      </label>
      <Reihe titel="Sicht Angehörige/r" stufen={STUFEN_PFLEGEPERSON} perspektive="pflegeperson" />
      {hinweisOffen && (
        <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            <b>Tipp:</b> Trage in den Stammdaten den Angehörigen mit
            Verwandtschaftsverhältnis ein — dann werden die Texte automatisch
            mit Namen personalisiert (z.B. „meine Tochter Maria").
          </span>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// KundeMailTab — Mail-Verlauf eines Kunden + Sende-Form.
// Etappe 2 der Kommunikationszentrale: lesen + senden ohne Vorlagen.
// Vorlagen-Sidebar kommt in Etappe 5 (rechte Spalte mit Klick-zum-Einfuegen).
// ==========================================================================
function KundeMailTab({ kundeId, kunde }) {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState(null);

  const [empfaengerKunde, setEmpfaengerKunde] = useState(true);
  const [empfaengerAngehoeriger, setEmpfaengerAngehoeriger] = useState(false);
  const [betreff, setBetreff] = useState('');
  const [body, setBody] = useState('');
  const [inReplyTo, setInReplyTo] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendeFehler, setSendeFehler] = useState(null);
  const [anhaenge, setAnhaenge] = useState([]);   // ausgewaehlte File-Objekte
  const bodyRef = useRef(null);
  const anhangInputRef = useRef(null);

  // KI-E-Mail-Entwurf
  const [kiLaeuft, setKiLaeuft] = useState(false);
  const [kiFehler, setKiFehler] = useState(null);
  const [kiStatus, setKiStatus] = useState('');

  const handleAnhaengeWaehlen = (e) => {
    const neu = Array.from(e.target.files || []);
    if (neu.length) setAnhaenge(prev => [...prev, ...neu]);
    if (anhangInputRef.current) anhangInputRef.current.value = '';  // gleiche Datei erneut waehlbar
  };
  const handleAnhangEntfernen = (idx) => setAnhaenge(prev => prev.filter((_, i) => i !== idx));

  // Vorlagen — werden einmal beim Mount geladen und in der rechten
  // Sidebar als anklickbare Bausteine angeboten.
  const [vorlagen, setVorlagen] = useState([]);

  // Einzelfall-Video-Karte: drei medizinische Themen-Checkboxen, deren
  // Auswahl in einen modular zusammengesetzten Text muendet.
  const [einzelfallAuswahl, setEinzelfallAuswahl] = useState(
    () => Object.fromEntries(EINZELFALL_VIDEO_THEMEN.map(t => [t.key, false]))
  );

  const kundeEmailRaw = kunde?.email || '';
  const angEmailRaw = kunde?.relative?.email || '';
  // Platzhalter-Adressen (z.B. 'not_needed@example.com' aus dem Booking-Widget
  // bei Hausbesuch-Kunden ohne echte Mail) ignorieren — sonst wuerde der
  // Mailserver sie mit '5.7.1 no public address' ablehnen.
  const kundeEmail = istPlatzhalterEmail(kundeEmailRaw) ? '' : kundeEmailRaw;
  const angEmail = istPlatzhalterEmail(angEmailRaw) ? '' : angEmailRaw;
  const kundeName = `${kunde?.firstName || ''} ${kunde?.lastName || ''}`.trim();
  const angName = `${kunde?.relative?.firstName || ''} ${kunde?.relative?.lastName || ''}`.trim();

  const laden = async () => {
    if (!kundeId) return;
    setLoading(true); setFehler(null);
    try {
      const liste = await api.get(`/api/kunden/${encodeURIComponent(kundeId)}/mails`);
      setMails(liste || []);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    laden();
    if (kundeEmail) {
      setEmpfaengerKunde(true);
      setEmpfaengerAngehoeriger(false);
    } else if (angEmail) {
      setEmpfaengerKunde(false);
      setEmpfaengerAngehoeriger(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeId]);

  // Vorlagen einmalig laden — sie aendern sich selten (nur ueber Super-
  // Admin-UI), daher kein Polling.
  useEffect(() => {
    api.get('/api/mail-vorlagen')
      .then(liste => setVorlagen(liste || []))
      .catch(e => console.warn('Mail-Vorlagen konnten nicht geladen werden:', e));
  }, []);

  // Text in den Body an Cursor-Position einsetzen — zentral, damit
  // sowohl Vorlagen-Karten als auch die Einzelfall-Video-Karte denselben
  // Insert-Mechanismus benutzen.
  const fuegeTextEin = (text) => {
    if (!text) return;
    const ta = bodyRef.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const vorher = body.slice(0, start);
      const nachher = body.slice(end);
      const trenner = vorher && !vorher.endsWith('\n') ? '\n\n' : '';
      const trennerEnde = nachher && !nachher.startsWith('\n') ? '\n\n' : '';
      const neu = vorher + trenner + text + trennerEnde + nachher;
      setBody(neu);
      const neuePos = (vorher + trenner + text).length;
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(neuePos, neuePos);
      }, 0);
    } else {
      setBody(prev => (prev ? prev.replace(/\n*$/, '\n\n') : '') + text);
    }
  };

  // Eine Vorlage in den aktuellen Mail-Body einfuegen — an der Cursor-
  // Position, mit Platzhalter-Ersetzung aus dem Kunden-Kontext.
  const fuegeVorlageEin = (vorlage) => {
    if (!vorlage?.body_template) return;
    const kontext = baueVorlagenKontext(kunde);
    fuegeTextEin(platzhalterErsetzen(vorlage.body_template, kontext));
  };

  // Einzelfall-Video-Text aus den drei Themen-Checkboxen zusammensetzen
  // und in den Body einfuegen. Nach dem Einfuegen werden die Checkboxen
  // zurueckgesetzt — sonst wuerde der Berater beim naechsten Klick auf
  // einen anderen Kunden den alten Stand sehen.
  const fuegeEinzelfallEin = () => {
    const text = baueEinzelfallVideoText(einzelfallAuswahl);
    if (!text) return;
    fuegeTextEin(text);
    setEinzelfallAuswahl(Object.fromEntries(EINZELFALL_VIDEO_THEMEN.map(t => [t.key, false])));
  };

  // No-Show-Rueckmeldung: empathischer Muster-Text (fest, kein KI) — setzt
  // Betreff + Body. Nur ein Entwurf; gesendet wird erst nach Freigabe.
  const fuegeNoShowEin = () => {
    const { betreff: b, body: t } = baueNoShowText(kunde);
    setBetreff(b);
    if (body.trim()) fuegeTextEin(t); else setBody(t);
    setKiStatus(''); setKiFehler(null);
  };

  // KI-E-Mail-Entwurf aus Gespraechsinhalt. Quelle: aktueller Body (falls
  // schon Stichworte drin), sonst der neueste Gespraechsvermerk des Kunden.
  // Ergebnis fuellt Betreff + Body — der Berater prueft und sendet selbst.
  const emailEntwurfErzeugen = async () => {
    setKiFehler(null); setKiStatus('');
    let quelle = body.trim();
    try {
      if (!quelle && kundeId) {
        const vermerke = await api.get(`/api/kunden/${kundeId}/vermerke`);
        if (Array.isArray(vermerke) && vermerke.length) quelle = (vermerke[0].text || '').trim();
      }
    } catch (e) { console.warn('Vermerke fuer E-Mail-Entwurf laden fehlgeschlagen', e); }
    if (!quelle) {
      setKiFehler('Kein Gesprächsinhalt gefunden. Bitte ein paar Stichworte ins Textfeld tippen oder erst einen Gesprächsvermerk erfassen.');
      return;
    }
    setKiLaeuft(true); setKiStatus('Entwurf wird erstellt…');
    try {
      const d = await api.post('/api/assistent/email-entwurf', { text: quelle, kunde_id: kundeId });
      if (d.betreff) setBetreff(d.betreff);
      if (d.body) setBody(d.body);
      setKiStatus('Entwurf erstellt — bitte prüfen, anpassen und dann senden.');
    } catch (e) {
      console.warn('E-Mail-Entwurf fehlgeschlagen', e);
      setKiFehler(e?.detail || 'E-Mail-Entwurf fehlgeschlagen.');
      setKiStatus('');
    } finally { setKiLaeuft(false); }
  };

  const handleAntworten = (mail) => {
    if (mail.absender_email === kundeEmail) {
      setEmpfaengerKunde(true);
      setEmpfaengerAngehoeriger(false);
    } else if (mail.absender_email === angEmail) {
      setEmpfaengerKunde(false);
      setEmpfaengerAngehoeriger(true);
    }
    const orig = mail.betreff || '';
    setBetreff(orig.toLowerCase().startsWith('re:') ? orig : `Re: ${orig}`);
    setInReplyTo(mail.message_id || null);
    setBody('');
    setSendeFehler(null);
    // Scroll zur Sende-Form
    setTimeout(() => {
      document.getElementById('mail-sendeform')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleSenden = async () => {
    setSendeFehler(null);
    const empfaenger = [];
    if (empfaengerKunde && kundeEmail) empfaenger.push(kundeEmail);
    if (empfaengerAngehoeriger && angEmail) empfaenger.push(angEmail);
    if (empfaenger.length === 0) {
      setSendeFehler('Bitte mindestens einen Empfaenger auswaehlen.');
      return;
    }
    if (!betreff.trim()) {
      setSendeFehler('Betreff darf nicht leer sein.');
      return;
    }
    if (!body.trim()) {
      setSendeFehler('Mail-Text darf nicht leer sein.');
      return;
    }
    setIsSending(true);
    try {
      const fd = new FormData();
      empfaenger.forEach(e => fd.append('empfaenger_emails', e));
      fd.append('betreff', betreff.trim());
      fd.append('body_text', body.trim());
      if (inReplyTo) fd.append('in_reply_to', inReplyTo);
      anhaenge.forEach(f => fd.append('anhaenge', f, f.name));
      await api.postForm(`/api/kunden/${encodeURIComponent(kundeId)}/mails`, fd);
      setBetreff('');
      setBody('');
      setInReplyTo(null);
      setAnhaenge([]);
      await laden();
    } catch (err) {
      setSendeFehler(err instanceof ApiError ? (err.detail || `Fehler ${err.status}`) : 'Verbindung fehlgeschlagen.');
    } finally {
      setIsSending(false);
    }
  };

  const formatDatum = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const keineEmailHinterlegt = !kundeEmail && !angEmail;

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-fade-in space-y-6">
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2" style={{ color: CI.brombeer }}>
          <Mail className="w-4 h-4" /> Mail-Verlauf
          {mails.length > 0 && <span className="text-xs font-normal text-gray-500">({mails.length})</span>}
        </h3>
        {loading && (
          <div className="text-xs text-gray-400 italic flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade Mails...
          </div>
        )}
        {fehler && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
            {fehler}
          </div>
        )}
        {!loading && !fehler && mails.length === 0 && (
          <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            Noch keine Mails zu diesem Kunden. Eingehende Mails ueber das Postfach hd@spa-pflegeberatung.de erscheinen hier automatisch (Match auf E-Mail-Adresse). Oder schreib unten direkt eine neue.
          </div>
        )}
        <div className="space-y-3">
          {mails.map(mail => (
            <MailCard
              key={mail.id}
              mail={mail}
              onAntworten={handleAntworten}
              formatDatum={formatDatum}
            />
          ))}
        </div>
      </div>

      <div id="mail-sendeform" className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: CI.brombeer }}>
          <Plus className="w-4 h-4" /> Neue Mail schreiben
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4">
          <div>

        {inReplyTo && (
          <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-center gap-2">
            <span className="font-bold flex-shrink-0">Antwort auf:</span>
            <span className="truncate">{betreff.replace(/^Re:\s*/i, '')}</span>
            <button
              onClick={() => { setInReplyTo(null); setBetreff(betreff.replace(/^Re:\s*/i, '')); }}
              className="ml-auto text-blue-600 hover:underline flex-shrink-0"
              title="Bezug entfernen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Empfaenger</label>
          <div className="flex flex-col gap-2">
            {kundeEmail && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={empfaengerKunde}
                  onChange={e => setEmpfaengerKunde(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>
                  <span className="font-medium">{kundeName || 'Patient'}</span>
                  <span className="text-gray-500"> &lt;{kundeEmail}&gt;</span>
                </span>
              </label>
            )}
            {angEmail && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={empfaengerAngehoeriger}
                  onChange={e => setEmpfaengerAngehoeriger(e.target.checked)}
                  className="w-4 h-4"
                />
                <span>
                  <span className="font-medium">{angName || 'Angehoeriger'}</span>
                  <span className="text-gray-500"> &lt;{angEmail}&gt;</span>
                </span>
              </label>
            )}
            {keineEmailHinterlegt && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Weder Patient noch Angehoeriger hat eine echte E-Mail-Adresse hinterlegt — bitte in den Stammdaten ergaenzen, sonst kann hier keine Mail verschickt werden.
                {(kundeEmailRaw && istPlatzhalterEmail(kundeEmailRaw)) || (angEmailRaw && istPlatzhalterEmail(angEmailRaw)) ? (
                  <div className="mt-1 italic">
                    Hinweis: Platzhalter-Adressen aus dem Booking-Widget
                    (z.B. <span className="font-mono">not_needed@example.com</span>) werden hier
                    bewusst ignoriert.
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Betreff</label>
          <input
            type="text"
            value={betreff}
            onChange={e => setBetreff(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="z.B. Ergaenzung zu unserem Beratungsgespraech"
            disabled={keineEmailHinterlegt}
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Mail-Text</label>
          <textarea
            ref={bodyRef}
            value={body}
            onChange={e => setBody(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg text-sm font-sans focus:ring-2 focus:ring-blue-500"
            rows={14}
            placeholder="Liebe Frau ...,&#10;&#10;ergaenzend zu unserem Beratungsgespraech am ... moechte ich Ihnen folgende Informationen zukommen lassen:&#10;&#10;..."
            disabled={keineEmailHinterlegt}
          />
        </div>

        {(() => {
          // Alle URLs im Body finden, damit Hendrik sie zum Testen anklicken
          // kann (Textareas selbst koennen keine klickbaren Links enthalten).
          // Trailing-Satzzeichen werden abgeschnitten, damit ',' / '.' / ')'
          // am Satzende nicht in der URL landen.
          const matches = (body || '').match(/https?:\/\/[^\s<>"'\]]+/g) || [];
          const links = Array.from(new Set(
            matches.map(u => u.replace(/[.,;:!?)\]}>'"]+$/, ''))
          ));
          if (links.length === 0) return null;
          return (
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="text-xs font-medium text-gray-700 mb-1">🔗 Links im Text (zum Testen anklicken)</div>
              <ul className="space-y-1">
                {links.map((url, idx) => (
                  <li key={idx} className="text-xs">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:underline break-all"
                      title={url}
                    >
                      {url.length > 90 ? url.slice(0, 87) + '…' : url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {sendeFehler && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
            {sendeFehler}
          </div>
        )}

        {/* Anhaenge */}
        <div className="mb-3">
          <input ref={anhangInputRef} type="file" multiple onChange={handleAnhaengeWaehlen} className="hidden" />
          <button
            type="button"
            onClick={() => anhangInputRef.current?.click()}
            disabled={isSending}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Paperclip className="w-3.5 h-3.5" /> Anhang hinzufügen
          </button>
          {anhaenge.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {anhaenge.map((f, idx) => (
                <li key={idx} className="inline-flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-md pl-2 pr-1 py-1 text-xs text-gray-700">
                  <Paperclip className="w-3 h-3 text-gray-400" />
                  <span className="max-w-[180px] truncate" title={f.name}>{f.name}</span>
                  <span className="text-gray-400">({(f.size / 1024).toFixed(0)} KB)</span>
                  <button type="button" onClick={() => handleAnhangEntfernen(idx)} className="text-gray-400 hover:text-red-600 p-0.5" title="Entfernen">
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end items-center gap-2">
          <button
            onClick={() => { setBetreff(''); setBody(''); setInReplyTo(null); setSendeFehler(null); setAnhaenge([]); }}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            disabled={isSending}
          >
            Verwerfen
          </button>
          <button
            onClick={handleSenden}
            disabled={isSending || keineEmailHinterlegt}
            className="px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0 flex items-center gap-2"
            style={{ backgroundColor: CI.brombeer }}
          >
            {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {isSending ? 'Sende...' : 'Mail senden'}
          </button>
        </div>
          </div>

          <aside className="space-y-4 self-start">
            {/* KI-E-Mail-Entwurf: aus dem Gespraechsinhalt einen Entwurf
                (Betreff + Text) erzeugen. Nur Entwurf — Versand nach Freigabe. */}
            <div className="rounded-lg p-3 border" style={{ borderColor: CI.brombeer, background: '#fdf4fb' }}>
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: CI.brombeer }}>
                <MessageSquarePlus className="w-3.5 h-3.5" /> E-Mail-Entwurf aus Gespräch
              </h4>
              <p className="text-[11px] text-gray-600 leading-snug mb-2">
                Erstellt aus dem letzten Gesprächsvermerk (oder deinen Stichworten im Textfeld) einen freundlichen E-Mail-Entwurf mit Betreff, Text und passenden Links. Du prüfst und sendest selbst.
              </p>
              <button
                onClick={emailEntwurfErzeugen}
                disabled={kiLaeuft}
                className="w-full px-3 py-2 text-sm font-bold text-white rounded-md disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: CI.brombeer }}
              >
                {kiLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4" />}
                Entwurf erstellen
              </button>
              {kiStatus && <p className="text-[11px] text-gray-500 mt-1.5">{kiStatus}</p>}
              {kiFehler && <p className="text-[11px] text-red-600 mt-1.5">{kiFehler}</p>}
            </div>

            {/* No-Show-Rueckmeldung: fester empathischer Muster-Text. */}
            <div className="rounded-lg p-3 border border-amber-200 bg-amber-50/60">
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5 text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5" /> No-Show-Rückmeldung
              </h4>
              <p className="text-[11px] text-gray-600 leading-snug mb-2">
                Freundliche Nachricht, wenn der Kunde nicht zum Termin erschienen ist — mit Bitte um Rückmeldung und Datenschutz-Hinweis (Löschung, falls kein Bedarf mehr).
              </p>
              <button
                onClick={fuegeNoShowEin}
                className="w-full px-3 py-2 text-sm font-bold text-white rounded-md"
                style={{ backgroundColor: '#B45309' }}
              >
                Text einfügen
              </button>
            </div>

            {/* Einzelfall-Video-Karte: drei medizinische Themen-Bausteine,
                die zu einem informellen Erklaer-Text fuer den Kunden
                zusammengesetzt werden (Antrag auf dauerhafte Video-
                Beratung als Einzelfallentscheidung der Pflegekasse). */}
            <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3">
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: CI.brombeer }}>
                <FileText className="w-3.5 h-3.5" /> Einzelfallentscheidung Video
              </h4>
              <p className="text-[11px] text-gray-600 leading-snug mb-2">
                Hinweis-Text fuer Kunden, die einen Antrag auf dauerhafte Video-Beratung bei ihrer Pflegekasse stellen wollen. Bitte mindestens ein medizinisches Thema auswaehlen — daraus wird der passende Text mit Einleitung, Begruendung und Muster-Antragsschreiben zusammengebaut.
              </p>
              <div className="space-y-1.5 mb-2">
                {EINZELFALL_VIDEO_THEMEN.map(t => (
                  <label key={t.key} className="flex items-start gap-2 cursor-pointer text-sm py-1 px-2 rounded hover:bg-blue-100/60">
                    <input
                      type="checkbox"
                      checked={!!einzelfallAuswahl[t.key]}
                      onChange={(e) => setEinzelfallAuswahl(a => ({ ...a, [t.key]: e.target.checked }))}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span className="text-gray-800 leading-snug">{t.label}</span>
                  </label>
                ))}
              </div>
              <button
                onClick={fuegeEinzelfallEin}
                disabled={keineEmailHinterlegt || !Object.values(einzelfallAuswahl).some(Boolean)}
                className="w-full px-3 py-2 text-sm font-bold text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: CI.brombeer }}
              >
                Text einfuegen
              </button>
            </div>

          <div className="bg-fuchsia-50/40 border border-fuchsia-200 rounded-lg p-3">
            <h4 className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: CI.brombeer }}>
              <FileText className="w-3.5 h-3.5" /> Vorlagen einfuegen
            </h4>
            {vorlagen.length === 0 ? (
              <div className="text-xs text-gray-400 italic bg-gray-50 border border-gray-200 rounded px-3 py-3">
                Noch keine Vorlagen angelegt. Werden vom Admin im Super-Admin verwaltet
                (oder einmalig per <span className="font-mono">seed_mailvorlagen.py</span>
                eingespielt).
              </div>
            ) : (
              <ul className="space-y-1.5">
                {vorlagen.map(v => (
                  <li key={v.id}>
                    <button
                      onClick={() => fuegeVorlageEin(v)}
                      disabled={keineEmailHinterlegt}
                      className="w-full text-left px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-fuchsia-400 hover:bg-fuchsia-50 disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white transition-colors"
                      title={`Klick fuegt diese Vorlage in den Mail-Text ein.\n\n${v.body_template?.slice(0, 200) || ''}...`}
                    >
                      <div className="font-medium text-gray-800">{v.titel}</div>
                      {v.kategorie && (
                        <div className="text-[11px] text-gray-500 mt-0.5">{v.kategorie}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-gray-400 italic mt-3 leading-snug">
              Platzhalter wie <span className="font-mono">{'{{plz}}'}</span>,
              <span className="font-mono"> {'{{vorname}}'}</span> oder
              <span className="font-mono"> {'{{bkk_pflegefinder_link}}'}</span> werden
              beim Einfuegen automatisch aus dem Kunden-Datensatz gefuellt.
            </p>
          </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function MailCard({ mail, onAntworten, formatDatum }) {
  const eingehend = mail.richtung === 'eingehend';
  return (
    <div className={`rounded-lg border p-4 ${eingehend ? 'bg-gray-50 border-gray-200' : 'bg-emerald-50 border-emerald-200'}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="text-xs text-gray-600 min-w-0">
          {eingehend ? (
            <><span className="font-bold">Von:</span> <span className="break-all">{mail.absender_email}</span></>
          ) : (
            <><span className="font-bold">An:</span> <span className="break-all">{(mail.empfaenger_emails || []).join(', ')}</span></>
          )}
        </div>
        <div className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">{formatDatum(mail.created_at)}</div>
      </div>
      {mail.betreff && (
        <div className="text-sm font-bold text-gray-900 mb-2">{mail.betreff}</div>
      )}
      <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">{mail.body_text}</div>
      {eingehend && (
        <div className="flex justify-end mt-3">
          <button
            onClick={() => onAntworten(mail)}
            className="text-xs font-medium hover:underline"
            style={{ color: CI.brombeer }}
          >
            Antworten →
          </button>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// GeoKoordinatenControl — in der Akte neben der Adresse. Oeffnet die Karte
// (wenn geokodiert), setzt Koordinaten von Hand (fuer Adressen, die
// OpenStreetMap nicht findet, deren Adresse aber korrekt bleiben muss),
// oder erzwingt eine automatische Neu-Suche.
// ==========================================================================
function GeoKoordinatenControl({ kundeId, lat, lng, geoQuelle, onKarte, onUpdate }) {
  const [eingabeOffen, setEingabeOffen] = useState(false);
  const [wert, setWert] = useState('');
  const [busy, setBusy] = useState(false);
  const hatKoordinaten = typeof lat === 'number' && typeof lng === 'number';
  const istManuell = geoQuelle === 'manuell';

  const speichern = async () => {
    // Akzeptiert "52.81, 9.96" / "52.81 9.96" (Google-Maps-Format).
    const m = wert.trim().match(/(-?\d+[.,]?\d*)\s*[,; ]\s*(-?\d+[.,]?\d*)/);
    if (!m) { alert('Bitte im Format "52.81, 9.96" eingeben (aus Google Maps).'); return; }
    const la = parseFloat(m[1].replace(',', '.'));
    const ln = parseFloat(m[2].replace(',', '.'));
    if (!isFinite(la) || !isFinite(ln) || la < -90 || la > 90 || ln < -180 || ln > 180) {
      alert('Die Koordinaten sehen nicht plausibel aus. Erwartet: Breite, Länge (z.B. 52.81, 9.96).');
      return;
    }
    setBusy(true);
    try {
      const d = await api.put(`/api/kunden/${kundeId}/koordinaten`, { lat: la, lng: ln });
      onUpdate({ lat: d.lat, lng: d.lng, geoQuelle: d.geoQuelle });
      setEingabeOffen(false);
      setWert('');
    } catch (e) {
      alert('Konnte Koordinaten nicht speichern: ' + (e?.detail || e?.message || 'unbekannt'));
    } finally { setBusy(false); }
  };

  const autoSuchen = async () => {
    setBusy(true);
    try {
      const d = await api.post(`/api/kunden/${kundeId}/koordinaten/auto`, {});
      onUpdate({ lat: d.lat, lng: d.lng, geoQuelle: d.geoQuelle });
      if (typeof d.lat !== 'number') {
        alert('OpenStreetMap hat die Adresse weiterhin nicht gefunden. Bitte Koordinaten von Hand setzen.');
      }
    } catch (e) {
      alert('Neu-Suche fehlgeschlagen: ' + (e?.detail || e?.message || 'unbekannt'));
    } finally { setBusy(false); }
  };

  if (eingabeOffen) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          autoFocus value={wert}
          onChange={e => setWert(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') speichern(); if (e.key === 'Escape') { setEingabeOffen(false); setWert(''); } }}
          placeholder="52.81, 9.96"
          className="text-xs border border-gray-300 rounded px-2 py-0.5 w-28 focus:outline-none focus:ring-2 focus:ring-fuchsia-200"
        />
        <button onClick={speichern} disabled={busy}
          className="text-xs font-bold px-2 py-0.5 rounded bg-fuchsia-700 text-white hover:bg-fuchsia-800 disabled:opacity-50">
          Speichern
        </button>
        <button onClick={() => { setEingabeOffen(false); setWert(''); }}
          className="text-xs text-gray-400 hover:text-gray-700">Abbr.</button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {hatKoordinaten ? (
        <>
          <button onClick={onKarte}
            className="text-xs font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
            title="Karte mit allen Kunden im Umkreis öffnen">
            Karte & Umkreis
          </button>
          {istManuell && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" title="Koordinaten manuell gesetzt">
              Pin manuell
            </span>
          )}
          <button onClick={() => { setWert(`${lat}, ${lng}`); setEingabeOffen(true); }}
            className="text-[11px] text-gray-400 hover:text-fuchsia-700 underline" title="Koordinaten von Hand ändern">
            ändern
          </button>
        </>
      ) : (
        <>
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Adresse konnte nicht auf der Karte verortet werden">
            ⚠ nicht auf der Karte
          </span>
          <button onClick={() => setEingabeOffen(true)} disabled={busy}
            className="text-xs font-bold px-2 py-0.5 rounded-md bg-fuchsia-700 text-white hover:bg-fuchsia-800 disabled:opacity-50">
            Koordinaten setzen
          </button>
          <button onClick={autoSuchen} disabled={busy}
            className="text-[11px] text-gray-400 hover:text-gray-700 underline">
            {busy ? '…' : 'automatisch neu suchen'}
          </button>
        </>
      )}
    </span>
  );
}

// ==========================================================================
// UmkreisKartenModal — Vollbild-Overlay mit Karte rund um den aktuellen
// Kunden. Wiederverwendet die bestehende KundenKarte-Komponente (Home-
// Startseite, Tourplan), inkl. Tour-Zuweisungs-Popup.
//
// KEIN fester Radius-Filter mehr: die Karte startet eng auf dem Zielkunden
// zentriert (rosa Pin + Namensschild), alle uebrigen Kunden werden mit
// durchgereicht. Je weiter man rauszoomt / je groesser das Fenster, desto
// mehr Pins erscheinen automatisch — Leaflet clustert dichte Bereiche zu
// Zahlen zusammen, beim Reinzoomen tauchen die Nachnamen wieder auf.
// ==========================================================================
function UmkreisKartenModal({ zielKunde, alleKunden, onClose }) {
  const [aktiveTouren, setAktiveTouren] = useState([]);
  const ladeTouren = () => {
    api.get('/api/touren?modus=aktiv')
      .then(r => setAktiveTouren(r || []))
      .catch((e) => { console.warn('UmkreisKartenModal: Tour-Liste fehlgeschlagen', e); });
  };
  useEffect(() => { ladeTouren(); }, []);

  const zielHatKoords =
    typeof zielKunde?.lat === 'number' && typeof zielKunde?.lng === 'number';

  // Alle Kunden mit Koordinaten (inkl. Zielkunde selbst) — ohne Radius-Grenze.
  const kartenKunden = useMemo(() => {
    if (!zielHatKoords) return [];
    return (alleKunden || [])
      .filter(k => typeof k.lat === 'number' && typeof k.lng === 'number');
  }, [zielHatKoords, alleKunden]);

  const handleAddZuTour = async (kundeId, tourId) => {
    try {
      await api.post(`/api/touren/${tourId}/kunden`, { kunde_id: kundeId, status: 'planung' });
      const tour = aktiveTouren.find(t => t.id === tourId);
      const tourLabel = tour ? `${tour.name} (${tour.datum})` : 'der Tour';
      alert(`Hinzugefuegt zu Tour „${tourLabel}".`);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert('Konnte nicht hinzugefuegt werden: ' + msg);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-gray-900/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: CI.brombeer }}>
              Kunden rund um {zielKunde.firstName} {zielKunde.lastName}
            </h2>
            <div className="text-xs text-gray-500 mt-0.5">
              {zielKunde.firstName} {zielKunde.lastName} ist rosa markiert · Rauszoomen zeigt mehr Kunden · Pin-Klick: Akte oeffnen oder zu einer aktiven Tour hinzufuegen
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 p-1.5 rounded-full hover:bg-gray-100"
            title="Schliessen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          {!zielHatKoords ? (
            <div className="p-8 text-sm text-gray-500 italic text-center">
              Für diesen Kunden ist keine Geokodierung vorhanden — bitte Stammdaten (PLZ/Ort) prüfen.
            </div>
          ) : (
            <KundenKarte
              kunden={kartenKunden}
              touren={aktiveTouren}
              onAddZuTour={handleAddZuTour}
              onTourenReload={ladeTouren}
              highlightKundeId={zielKunde.id}
              zentrumKunde={zielKunde}
              labelsAnzeigen={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}
